import { ParticipantSelections } from './ParticipantSelections';
import type { ParticipantSelection } from '../../collaboration/selection';
/**
 * The imperative three.js viewer.
 *
 * Deliberately not React: the editor mutates hundreds of objects per frame and the
 * reconciler is the wrong tool for that. React owns the surrounding UI only.
 */
import * as THREE from 'three';
import { builtinTexturesVersion, subscribeBuiltinTextures } from '../../state/builtinTextures';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { ContactShadows } from './ContactShadows';
import { StillRefiner } from './StillRefiner';
import { FrameScheduler } from './FrameScheduler';
import { SceneIndex } from './SceneIndex';
import { InstancedCohorts } from './InstancedCohorts';
import { PlanTopView } from './PlanTopView';
import { subscribeAssets } from './assetEvents';
import { makeInteriorEnvironment } from './nativeEnvironment';
import { OutdoorView, type OutdoorStatus } from './outdoorView';
import { NO_WINDOW_VIEW, viewSunDirection, windowViewById, type WindowView } from '../../state/windowViews';
import type { Item, Level, Room, Vec2, Wall, Scene as HPScene } from '../../core/hp/types';
import { moveWalker, WALK_BODY_RADIUS, WALK_MAX_STEP } from '../../core/geometry/walkCollision';
import { depthInWell, flightEnds, leadsUp, stairLanding, stairwaysFrom, stepOutOf, wellAt, wellRoom, wellsDown, wellsKey, wellsUp, type Stairway } from '../../core/geometry/stairwells';
import { subscribeModelBounds } from '../../core/geometry/modelBounds';
import { levelAltitude } from '../../state/floors';
import { itemFootprint, type Footprints, type LocalFootprint } from '../../core/geometry/footprint';
import { smartSnapItem, type SnapCapture, type SnapGuide } from '../../core/geometry/snapping';
import { constrainMove, moveItemGroup, previewItemMove, type ItemMoveGesture } from '../../state/planGesture';
import { pointedShelfPosition, type SurfaceProbe, type LandingPointer } from '../../core/geometry/landing';
import { meshSurfaceProbe } from './surfaceProbe';
import { treadsOf, type Treads } from './treadIndex';
import { interiorPoint } from '../../state/placement';
import { coastDecay, coastVelocity, DRAG_FRICTION, easeInOutQuint, PAN_MAX_PITCH, panStep, pinchStep, stickPace, twoFingerMode,
  passableDoorways, throughDoor, WALK_FLIGHT_MS, walkTarget, WHEEL_FRICTION, type PanSample, type TwoFingerEvidence, type TwoFingerMode, type WalkPose, type WalkTapped } from '../../state/walkNavigation';
import { areaCentre, withinArea, REVEAL_INSET, type ScreenPoint, type ScreenRect } from '../../state/itemReveal';
import { wallsOfRoom } from '../../state/minimap';
import { cursorStyle, rotateGlyphAngle, sceneCursor } from './cursors';
import { ScenePointerIntent, containsPoint, constrainItemToRoom, previewSceneItemTransform, rayPlanePoint, sameYaw, snapSceneRotation,
  type SceneGestureKind, type SceneItemTransform } from '../../state/sceneGesture';
import { wallCopies } from '../../state/contextCommands';
import { applyWells, buildScene, isRegion, paintPlaceholder, placeholderFor, type BuildResult, type PlaceholderState, type Wells } from './buildScene';
import { subscribeWebStates, webStateOf, type WebState } from '../../state/catalogApi';
export { isRegion } from './buildScene';
import { buildOpeningModel, ModelLibrary } from './ModelLibrary';
import { atticNode, atticShape, tiltIntoSlope } from './attic';
import { atticFrame, isAttic, roofWindows } from '../../state/attic';
import { ceilingHeightAt, lowestCeilingOver, slopedCeilings, type RoomCeiling } from '../../state/roomCeiling';
import { ROOF_WINDOW_LINING, slopeNode, slopeSignature } from './slope';
import { LowerFloors, type LowerFloorState } from './LowerFloors';
import { compileInBackground } from './backgroundCompile';
import { FloorAbove } from './FloorAbove';
import { buildWallSolids } from '../../core/geometry/walls';
import { openingRayEntry } from '../../core/geometry/openingRay';
import { HydrationQueue, type HydrationProgress, type HydrationTask } from './HydrationQueue';
import { applyModeVisibility, restoreModeGeometry, cloneFullModel, attachedWallId, cutawayWalls, LOW_WALL_HEIGHT,
  type CutawayCamera, type SceneVisibilityMode, type VisibilityScope } from './modeVisibility';
import type { WallMode } from '../../state/wallMode';
import { acrossWall, alongWall, clampWallPan, faceNormal, itemFacesWall, panWallPlanBy, pxPerMetre, wallDirectionSign, wallPlanCameraDistance,
  wallPlanCentreX, wallPlanHeight, wallPlanInset, wallPlanVertical, zoomWallPlanAt, type WallPlanFrame, type WallPlanInsets, type WallViewTarget } from '../../state/wallPlan';
import { onSpace, spaceHeld } from '../../state/spacePan';
import { editProperties } from '../../state/propertyEdit';
import { openingKind } from '../../state/openingProperties';
import { edgePoint, edgeSides, openingBox, openingEdges, previewOpeningEdge, type OpeningEdge } from '../../state/openingResize';
import { knownModelTags } from '../../state/modelTags';
import { openingCopies } from '../../core/geometry/openingTwins';
import { itemWallPlanBox, snapWallPlanBox, wallPlanBounds, wallPlanBoxes, type WallPlanAxis, type WallPlanCapture, type WallPlanGuide,
  type WallPlanMarks } from '../../state/wallPlanSnap';
import { stackedItems } from '../../core/geometry/stacking';
import type { Opening } from '../../core/hp/types';
import type { OpeningProperties } from '../../core/geometry/edit';
import { outlineMaterial, SelectionPresentation } from './SelectionPresentation';
import { roomEdgeInsets, SurfaceSelection, type SurfaceSelectionTarget } from './SurfaceSelection';
import { TrimSelection } from './TrimSelection';
import { obstructions } from '../../core/geometry/overlap';
import { ItemAppearance } from './ItemAppearance';
import { axisRayParameter, fallbackResizeBounds, previewItemResize, resizeAxisVector, separateHandles, type ResizeBounds, type ResizeFace } from '../../state/itemResize';
import { MaterialTransformPreview, type MaterialSlotInfo } from './MaterialTransformPreview';
import type { EditorState } from '../../state/store';
import type { SavedCameras, SavedCameraView } from '../../state/projectView';
import type { ItemIdentity, PresenceViewport } from '../../collaboration/protocol';
import { t as tr } from '../../i18n/I18n';
import { movableItems } from '../../state/selectionTap';

export type CameraMode = 'orbit' | 'walk' | 'top';
export type ViewerGestureKind = SceneGestureKind | null;
/** A doorway's enter button on screen in the demo walkthrough, and the room the camera would go into. */
export interface DoorMarker { id: string; x: number; y: number; roomIndex: number }

export interface ViewerOverlay {
  center: { x: number; y: number };
  rotate: { x: number; y: number } | null;
  /** The height drag button of a piece on the floor or under the ceiling (HeightFromFloorWithDragButton), beside the
   * rotate handle; none while the camera looks steeply down, as the app hides it. */
  height?: { x: number; y: number } | null;
  frame: { x: number; y: number; width: number; height: number };
  /** Each face handle the camera sees: `dir` is the face's outward normal on screen (a unit vector),
   * `outline` the face's four corners on screen, which light up while its handle is hot; `anchor` the
   * face's centre when the handle had to move out along its axis to clear the others. */
  resize?: Array<ResizeFace & { x: number; y: number; angle: number; active: boolean; dir: { x: number; y: number }; outline: Array<{ x: number; y: number }>; anchor?: { x: number; y: number } }>;
  /** The item's oriented resize box, as screen-space edges, while resize mode is on; `hidden` when
   * neither face along the edge looks at the camera. */
  box?: Array<{ x1: number; y1: number; x2: number; y2: number; hidden?: boolean }>;
  /** The size of each axis whose handle shows, beside that handle: `x`, `y` is where the readout's
   * near edge sits, `dir` the side it stands out to. */
  labels?: Array<{ x: number; y: number; dir: { x: number; y: number }; text: string; axis: 'w' | 'h' | 'd' }>;
  /** Each axis's size as a drawing's dimension: the box edges along that axis on the item's outline
   * (a face in view meeting one turned away), best first, `n` the way out from the box on screen.
   * `delta` is how far the dragged size has come, in centimetres. */
  dims?: Array<{ axis: 'w' | 'h' | 'd'; text: string; delta?: number; candidates: Array<{ x1: number; y1: number; x2: number; y2: number; n: { x: number; y: number } }> }>;
  /** The box as it was when the dragged face was taken, as screen-space edges. */
  ghost?: Array<{ x1: number; y1: number; x2: number; y2: number }>;
  /** Screen areas the context bar must leave free: control touch targets, or the selected wall-plan drawing. */
  keepOut?: Array<{ x: number; y: number; width: number; height: number }>;
  /**
   * A door's or window's resize mode (the app's opening resize markers), on the face of its wall in view: a handle per
   * edge, `dir` its outward way on screen; the opening's outline, and as it was when the dragged edge was taken; its
   * width, height and, for a window, sill as dimensions, `delta` how far a dragged one has come in centimetres.
   */
  opening?: {
    handles: Array<{ edge: OpeningEdge; x: number; y: number; angle: number; active: boolean; dir: { x: number; y: number }; anchor?: { x: number; y: number } }>;
    outline: Array<{ x: number; y: number }>;
    ghost?: Array<{ x: number; y: number }>;
    dims: Array<{ kind: OpeningDimension; text: string; delta?: number; line: { x1: number; y1: number; x2: number; y2: number; n: { x: number; y: number } } }>;
  };
}
/** The sizes an opening's resize mode shows: its width, its height, a window's sill. */
export type OpeningDimension = 'width' | 'height' | 'sill';
export interface ViewerInteractionHandlers {
  onNavigationIntent?(): void;
  /** `modifiers.shiftKey`: the tap gathers items into the selection instead of replacing it. */
  onPick?(hit: THREE.Object3D | null, anchor: { x: number; y: number }, material?: {slot:string|null;wallSide?:'front'|'back'}, modifiers?: { shiftKey: boolean }): void;
  /** The demo walkthrough's door buttons (EnterRoomDoorMarkers): each doorway in view, where it is on screen, the room beyond it. */
  onDoorMarkers?(markers: DoorMarker[]): void;
  /** The parts of the selected item's or opening's model (`id` is the item's or the opening's). */
  onMaterialSlots?(id:string,slots:MaterialSlotInfo[]):void;
  onGesture?(kind: ViewerGestureKind | null): void;
  /** Release form presentation before capturing an immutable gesture baseline. */
  onEditPrepare?(): void;
  /** False: the plan changed since the press, and the edit ends at once, cancelled, before any preview. */
  onEditBegin?(): boolean | void;
  onEditPreview?(level: Level): void;
  onEditEnd?(cancelled: boolean): void;
  onOverlay?(overlay: ViewerOverlay | null): void;
  onResizeModeEnd?(): void;
  /** An Alt-drag dropped a copy: the caller decides what to select. */
  onCopyPlaced?(id: string): void;
  /** A group move ended leaving `count` locked members where they were. */
  onLockedStayed?(count: number): void;
  /** The identity of an Alt-drag's copy of `sourceId` (`store.newItemIdentity`); without it the copy gets a web id. */
  copyIdentity?(sourceId: string): ItemIdentity;
  /**
   * The walk has climbed or gone down a flight past the floor above or its own (`climbFloors`): the caller brings
   * `index` into view, and the walk carries on on it from the same spot.
   */
  onWalkFloor?(index: number): void;
}
interface PointerSession {
  intent: ScenePointerIntent;
  /** The walk's look with the right button: released in place it opens the context menu, never a tap's pick. */
  secondary?: boolean;
  previous: { x: number; y: number };
  base: Level | null;
  item: Item | null;
  planeY: number;
  grab: Vec2;
  /** Actual mesh grab; floor projection can stretch it beyond a small item's footprint. */
  shelfGrab?: Vec2;
  /** A mesh above the eye starts on a vertical depth plane when its bottom cannot be hit. */
  meshSeed?: { point: Vec2; normal: Vec2 };
  reach: number;
  roomIndex?: number;
  rotationOffset: number;
  wallGrab?: { along: number; y: number };
  /** The wall plan dragging a door or a window: which one, and where on it the finger took hold. */
  opening?: { id: string; grab: { along: number; y: number } };
  footprints?: Footprints;
  resize?: { face: ResizeFace; bounds: ResizeBounds; origin: THREE.Vector3; axis: THREE.Vector3; start: number };
  /** The height drag button: a vertical plane through the piece facing the camera, where the press met it, the piece's height then and its highest. */
  height?: { plane: THREE.Plane; y0: number; start: number; max: number };
  /** An opening's edge dragged in its resize mode: the face's plane the pointer runs on, how far from the edge it took hold (along the wall, up), the handle's angle for the cursor. */
  openingResize?: { id: string; edge: OpeningEdge; wall: Wall; plane: THREE.Plane; grab: { t: number; h: number }; angle: number };
  /** Floor/ceiling moves: the copy-or-move gesture, where it started, and a Shift-locked axis. */
  gesture?: ItemMoveGesture;
  /** A Shift-gathered group pulled by `item`, one of its members: the ids that travel together, and how many locked ones stay. */
  group?: string[];
  held?: number;
  origin?: Vec2;
  axis?: 'x' | 'z';
  copy?: boolean;
  /** What the magnets caught last frame, so a held alignment does not flicker to a rival. */
  captures?: SnapCapture[];
  /** Where those magnets are, in plan space, for the on-screen guides. */
  guides?: SnapGuide[];
  /** The wall plan's magnets: what held the dragged thing last frame, and the guides the overlay draws. */
  wallCaptures?: WallPlanCapture[];
  wallGuides?: WallPlanGuide[];
}

export interface LoadOptions {
  /** Local projects can have no embedded source id; use their editor identity. */
  projectId?: string;
  levelIndex?: number;
  showCeiling?: boolean;
  /** Whole walls, the orbit cutaway, or every wall cut to waist height. */
  wallMode?: WallMode;
  /** Keep the current camera instead of re-framing — used when only materials changed. */
  keepCamera?: boolean;
  /** "Hide other rooms": only this room's floor, walls and items are built. */
  isolatedRoom?: number | null;
}

/** The camera on the plan, for the minimap: where it stands, where it looks, what it orbits. */
export interface CameraPose {
  mode: CameraMode;
  position: Vec2;
  height: number;
  yaw: number;
  forward: Vec2;
  target: Vec2;
}

export type ViewerStats = BuildResult['stats'] & {
  fps: number;
  drawCalls: number;
  programs: number;
  /** Items showing real catalog geometry rather than a placeholder box. */
  realModels: number;
  renderFrames: number;
  planRenderFrames: number;
  aoFrames: number;
  shadowFrames: number;
  cpuSubmitMs: number;
  frameIntervalMs: number;
  submittedTriangles: number;
  colorCalls: number;
  shadowCalls: number;
  aoCalls: number;
  shadowTriangles: number;
  colorTriangles: number;
  aoTriangles: number;
  compositeCalls: number;
  compositeTriangles: number;
  meshes: number;
  materials: number;
};

export class Viewer {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly controls: OrbitControls;

  private contactShadows: ContactShadows | null = null;
  /** Averages jittered re-renders of a still view: edges without a staircase, AO without grain. */
  private still: StillRefiner | null = null;
  private ambientOcclusion = true;
  private aoIntensity = .6;
  setAmbientOcclusion(enabled: boolean, intensity = .6): void {
    this.ambientOcclusion = enabled;
    this.aoIntensity = intensity;
    this.contactShadows?.setIntensity(intensity);
    this.requestFrame();
  }

  private topLayer: PlanTopView | null = null;
  private unsubscribeAssets = subscribeAssets(() => { this.topLayer?.invalidate(); this.requestFrame(true); });
  planLayer(): PlanTopView {
    this.settleAppearances();
    return this.topLayer ??= new PlanTopView(this.renderer, () => this.contents, this.scene.environment, {
      node: name => this.nodeFor(name), localBounds: name => this.index?.localBounds(name),
      retainResources: root => this.retainResources(root),
      // A loaded model's own entry; before it, or when its conversion failed, its catalog document.
      modelTags: id => this.models?.entry?.(id)?.tags ?? knownModelTags(id),
    });
  }

  private built: BuildResult | null = null;
  /** Where a piece's loaded surface is under plan points, for landing what is dragged across it. */
  readonly surfaceProbe: SurfaceProbe = meshSurfaceProbe(name => this.nodeFor(name));
  private hp: HPScene | null = null;
  private scheduler?: FrameScheduler;
  private index = new SceneIndex();
  private cohorts?: InstancedCohorts;
  private levelIndex = 0;
  private currentScope = '';
  private itemInputs = new Map<string, Item>();
  private itemsById = new Map<string, Item>();
  private itemTasks = new Map<string, HydrationTask<THREE.Object3D>>();
  private openingTasks: HydrationTask<THREE.Object3D>[] = [];
  private shellInputs?: { walls: Level['walls']; openings: Level['openings']; rooms: Level['rooms']; ceiling: boolean; scope: string; isolated: number | null; textures: number; slopes: string };
  /** What a rebuild replays; never the wall mode, which `setWallMode` may have changed since. */
  private lastLoad?: Omit<LoadOptions, 'wallMode'>;
  /** Textures the catalog answered after the shell was built: build it again with them, camera in place. */
  private unsubscribeBuiltins = subscribeBuiltinTextures(() => {
    if (!this.disposed && this.hp && this.lastLoad) this.load(this.hp, { ...this.lastLoad, keepCamera: true });
  });
  /**
   * A flight's model measured: its stairwell follows the model as drawn (`flightsUp`), so the floors and ceilings it cuts,
   * the floors below and the floor above are cut again, once for every model that arrived in a frame.
   */
  private wellsRefresh: number | null = null;
  private unsubscribeBounds = subscribeModelBounds(id => {
    if (this.disposed || this.wellsRefresh !== null || !this.hp?.levels.some(level => level.items.some(item => item.modelRef.id === id && leadsUp(item, level)))) return;
    this.wellsRefresh = requestAnimationFrame(() => {
      this.wellsRefresh = null;
      if (!this.disposed && this.hp && this.lastLoad) this.load(this.hp, { ...this.lastLoad, keepCamera: true });
    });
  });
  private isolatedRoom: number | null = null;
  private isolatedPolygon: Vec2[] | null = null;
  private frameListeners = new Set<() => void>();
  private previousItems?: readonly Item[];
  private previousWalls?: readonly Wall[];
  private previousOpenings?: Level['openings'];
  private previousRoofKey?: string;
  private releaseShell?: () => void;
  private shadowFrames = 0;
  private inventoryCache?: { key: string; meshes: number; triangles: number; textures: number; materials: number };
  private frameMetrics = { cpuSubmitMs: 0, frameIntervalMs: 0, drawCalls: 0, submittedTriangles: 0,
    shadowCalls: 0, shadowTriangles: 0, colorCalls: 0, aoCalls: 0, colorTriangles: 0, aoTriangles: 0, compositeCalls: 0, compositeTriangles: 0 };
  private cameraViews = new Map<string, { position: THREE.Vector3; quaternion: THREE.Quaternion; target: THREE.Vector3;
    fov: number; walkFov: number | null; yaw: number; pitch: number }>();
  private lastFrame = 0;
  private fps = 0;
  private mode: CameraMode = 'orbit';
  private disposed = false;
  private active = true;
  private participantView = false;
  private renderFrames = 0;
  private sun: THREE.DirectionalLight;
  private hemi: THREE.HemisphereLight;
  private ambient: THREE.AmbientLight;
  private outdoor: OutdoorView;
  /** The view out of the windows and how far it is turned (`setWindowView`). */
  private windowView: { view: WindowView | null; rotation: number } = { view: null, rotation: 0 };
  private windowViewListeners = new Set<(status: OutdoorStatus) => void>();
  private envTarget: THREE.WebGLRenderTarget | null = null;
  private walk = { yaw: 0, pitch: 0, pos: new THREE.Vector3(), keys: new Set<string>(), dragging: false };
  /** The on-screen stick's push (`setWalkStick`), each way −1…1; the keys and the stick add up. */
  private walkStick = { fwd: 0, strafe: 0 };
  /** How long the stick has walked without letting go: its pace ramps from 1 to 2 m/s, as the app's (`stickPace`). */
  private walkStickTime = 0;
  /** Where two fingers and the wheel carried the walker since the last frame, plan metres; the frame applies it with the walls. */
  private walkPending: { x: number; z: number } | null = null;
  /** The slide a drag or the wheel leaves behind, m/s, and how much of it survives a 60 Hz frame. */
  private walkCoast: { x: number; z: number; friction: number } | null = null;
  /** Two fingers on the walk picture: where they were, what they are read as, and how far a drag has carried the walker. */
  private walkFingers: { origin: { centre: { x: number; y: number }; span: number; angle: number }; centre: { x: number; y: number }; span: number;
    angle: number; mode: TwoFingerMode | null; evidence: TwoFingerEvidence; carried: { x: number; z: number }; samples: PanSample[] } | null = null;
  /** The flight to a double-tapped place (`walkTarget`). */
  private walkFlight: { from: WalkPose; to: WalkPose; started: number } | null = null;
  /** What the last double tap framed and where the walker stood before it: a second double tap on it goes back. */
  private walkApproach: { key: string; from: WalkPose; at: WalkPose } | null = null;
  /** The walk's previous tap, to tell a double tap. */
  private walkTap: { at: number; x: number; y: number } | null = null;
  private walkSupportY = 0;
  /** The stairwells cut into the shell of the floor in view, as `wellsKey` writes them. */
  private wellsSignature = '';
  /** The floor the walk asked to come into view (`climbFloors`), and how far the walker's heights move with it. */
  private walkShift: { to: number; shift: number } | null = null;
  /**
   * The flights the walker is on, by item id, taken along from the floor it left and moved to the heights of the floor it
   * came to: that floor builds them again, as its own or as a floor below, and the walk stands on these until it has.
   */
  private carried: Map<string, { node: THREE.Object3D; item: Item }> | null = null;
  /** Whether the previous frame moved the orbit camera from the keyboard. */
  private orbitKeyed = false;
  private roomNavigationFrame = 0;
  private fullPixelRatio = 1;
  private interacting = false;
  private pointer: PointerSession | null = null;
  private touches = new Map<number, {x:number;y:number}>();
  private touchNavigation = false;
  private forwardingTouch = false;
  private interactionHandlers: ViewerInteractionHandlers = {};
  private editingEnabled = true;
  private resizeMode = false;
  private resizeBoundsCache = new WeakMap<THREE.Object3D,{key:string;bounds:ResizeBounds}>();
  private materialPreview: MaterialTransformPreview | null = null;
  private materialPreviewState: EditorState['materialTransformPreview'] = null;
  private materialPreviewKey = '';
  private materialSlotsSignature = '';
  private pickedMaterial: {slot:string|null;wallSide?:'front'|'back'} = {slot:null};
  private overlaySignature = '';
  private appliedCursor = '';
  private lowWalls = false;
  /** The orbit cutaway is on; `cutWalls` is what it lowers for the camera right now. */
  private cutaway = false;
  private cutWalls = new Set<string>();
  private cutScratch = new Set<string>();
  private cutDelta = new Set<string>();
  private cutawayVersion = 0;
  /** Each wall's settled height, and the walls still sliding towards theirs. */
  private wallTargets = new Map<string, number>();
  private wallSlides = new Map<string, WallSlide>();
  private eyeForward = new THREE.Vector3();
  /** The project's horizontal field of view; the vertical one is derived per aspect. */
  private walkFovH: number | null = null;
  private highlighted: string | null = null;
  /** The Shift-gathered items, each in its own outline; empty unless two or more are selected. */
  private gathered: string[] = [];
  private gatheredPresentations = new Map<string, SelectionPresentation>();
  private hoverKind: 'none' | 'item' = 'none';
  private hoverSelected = false;
  /** The hovered thing is a door or a window of the wall plan: it drags, but an Alt copy is not offered. */
  private hoverOpening = false;
  private altHeld = false;
  private hoverAt = 0;
  private participantSelections?: ParticipantSelections;
  private participantItemIds = new Set<string>();
  private selectionPresentation: SelectionPresentation | null = null;
  private conflictPresentations = new Map<string, SelectionPresentation>();
  /** How far each ceiling piece over a slope hangs under its elevation, to the slope. */
  private itemDrops?: Map<string, number>;
  private conflictFootprints: { items: Level['items']; resources: number; value: Footprints } | null = null;
  private appearance: ItemAppearance | null = null;
  private appearanceRoot: THREE.Group | null = null;
  private pendingAppearances = new Map<string, Set<string>>();
  private appearanceSeen = new Set<string>();
  private appearingIds = new Set<string>();
  private reconciling = false;
  private models = new ModelLibrary();
  // A model added from the catalog may still be converting on the server: two minutes, not thirty seconds.
  private hydration = new HydrationQueue<THREE.Object3D>((node) => this.releaseHydratedNode(node), 4, 120_000);
  /**
   * The floors under the one in view, as the app draws them in 3D (`LowerFloors`): made when a floor with floors
   * below it first comes into view. While 3D is hidden they wait (`lowerFloorsStale`) for it to show again.
   */
  private lowerFloors?: LowerFloors;
  private lowerFloorsStale = false;
  /** The floor over the one in view while the walk is under a stairwell into it (`FloorAbove`), made the first time it is. */
  private floorAbove?: FloorAbove;
  private contents = new THREE.Group();
  private shellSignature = '';
  private currentProject = '';
  private hasShownGeometry = false;
  private shellPickable: THREE.Object3D[] = [];
  private placeholderSignatures = new Map<string, string>();
  /**
   * The wall plan (the app's WallSide): one wall face-on through `wallCamera`, an orthographic
   * camera standing in the room in front of the chosen face; null in the 3D views. Zoom and
   * pan belong to the frame and reset when the wall changes, as Lite's do.
   */
  private wallPlan: { target: WallViewTarget; zoom: number; pan: { x: number; y: number }; distance: number } | null = null;
  private orbitFit?: { box: number[]; aspect: number; fov: number; distance: number };
  private wallCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, .05, 60);
  /** The metre grid behind the wall, wall-plan only. */
  private wallGrid: THREE.Mesh | null = null;
  /** Whether floors, ceilings and the outdoors are hidden for the wall plan right now. */
  private wallPlanMasked = false;
  private wallPinch: { distance: number; mid: { x: number; y: number } } | null = null;
  /** The selected door or window, which the wall plan can drag. */
  private selectedOpening: string | null = null;
  /** The selected wall or floor, and the outlines the 3D view draws for it and for a selected door or window. */
  private selectedSurface: { kind: 'wall'; id: string } | { kind: 'room'; index: number } | null = null;
  private surfaceSelection: SurfaceSelection | null = null;
  private openingPresentation: SelectionPresentation | null = null;
  /** The selected plinth or ceiling molding (`plinth:<wall>`, `molding:<wall>`), outlined as an opening is. */
  private selectedTrim: string | null = null;
  private trimPresentation: TrimSelection | null = null;
  /** The demo walkthrough: its door buttons are followed, a dozen times a second at most. */
  private walkthroughDoors = false;
  private doorMarkersKey = '';
  private doorMarkersAt = 0;
  /** The outline materials compiled ahead of the first selection. */
  private outlineWarmup: THREE.Material[] | null = null;
  /** What the chrome takes at either side of the canvas while the wall plan is open; null means the rails' default. */
  private wallInsets: WallPlanInsets | null = null;
  private unsubscribeSpace: (() => void) | null = null;

  /** Stops listening to the catalog's queue; set in the constructor. */
  private unsubscribeWebStates: () => void = () => {};

  /** How a placeholder for this item should look, from what the queue last said. */
  private placeholderStateFor(item: Item): PlaceholderState {
    const status = webStateOf(item.modelRef.id)?.status;
    return status === 'error' || status === 'missing' || status === 'unavailable' ? 'failed' : 'pending';
  }

  /**
   * A model the queue has settled: items standing as placeholders for it are rebuilt when
   * the GLB is there, or repainted to say it is not coming.
   */
  private onWebState(modelId: string, state: WebState): void {
    if (this.disposed) return;
    if (state.status === 'done') {
      this.hydration.requeue((task, recordState) => task.kind === 'item' && recordState === 'missing' && (task.revision as Item | undefined)?.modelRef.id === modelId);
      this.lowerFloors?.requeue(modelId);
      return;
    }
    const look: PlaceholderState = state.status === 'queued' || state.status === 'running' ? 'pending' : 'failed';
    for (const [id, item] of this.itemInputs) {
      if (item.modelRef.id !== modelId) continue;
      const node = this.nodeFor(`item:${id}`);
      if (node && (node as THREE.Mesh).isMesh && !node.userData.real && !node.userData.generated) paintPlaceholder(node as THREE.Mesh, look);
    }
    this.requestFrame(true);
  }

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.unsubscribeWebStates = subscribeWebStates((modelId, state) => this.onWebState(modelId, state));
    this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: 'high-performance' });
    this.fullPixelRatio = Math.min(devicePixelRatio, 1.5);
    this.renderer.setPixelRatio(this.fullPixelRatio);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.autoUpdate = false;
    this.renderer.shadowMap.needsUpdate = true;
    this.renderer.info.autoReset = false;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;   // PCFSoft is deprecated as of r185
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.models.configureRenderer(this.renderer);

    this.scene.background = new THREE.Color(SCENE_BACKGROUND);

    this.camera = new THREE.PerspectiveCamera(55, 1, 0.05, 400);
    this.camera.position.set(6, 6, 6);
    this.still = new StillRefiner({ renderer: this.renderer, camera: this.camera, drawFrame: () => this.drawFrame() });

    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.mouseButtons.RIGHT = THREE.MOUSE.ROTATE;
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.maxPolarAngle = Math.PI * 0.495;
    this.onInteractStart = this.onInteractStart.bind(this);
    this.onInteractEnd = this.onInteractEnd.bind(this);
    this.onContextMenu = this.onContextMenu.bind(this);

    // A neutral indoor IBL so PBR materials are lit before any project lights load.
    this.envTarget = makeInteriorEnvironment(this.renderer);
    this.scene.environment = this.envTarget.texture;
    this.scene.environmentIntensity = .85;
    this.outdoor = new OutdoorView(this.models.textureImages.resources, { anisotropy: this.renderer.capabilities.getMaxAnisotropy(),
      onChange: () => this.requestFrame(), onStatus: status => { for (const listener of this.windowViewListeners) listener(status); } });
    this.scene.add(this.outdoor.root);

    this.ambient = new THREE.AmbientLight(0xffffff, 0);
    this.hemi = new THREE.HemisphereLight(0xe4edff, 0xb1a190, .16);
    this.sun = new THREE.DirectionalLight(0xfff7eb, SUN_INTENSITY);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.bias = -0.0007;
    this.sun.shadow.normalBias = 0.02
    this.sun.shadow.radius = 3;
    this.scene.add(this.ambient, this.hemi, this.sun, this.sun.target);

    this.canvas.addEventListener('contextmenu', this.onContextMenu);
    this.controls.addEventListener('start', this.onInteractStart);
    this.controls.addEventListener('end', this.onInteractEnd);
    this.controls.addEventListener('change', this.onControlsChange);
    // Figma's Space: the left button pans the orbit while the key is held, and the cursor says so.
    this.unsubscribeSpace = onSpace(held => {
      this.controls.mouseButtons.LEFT = held ? THREE.MOUSE.PAN : THREE.MOUSE.ROTATE;
      this.applyCursor(this.pointer?.intent.active ? this.pointer.intent.kind : null, this.altHeld);
    });

    this.onResize();
    window.addEventListener('resize', this.onResize);
    canvas.addEventListener('pointerdown', this.onPointerDown, true);
    canvas.addEventListener('wheel', this.onWheel, { passive: false });
    window.addEventListener('pointerup', this.onPointerUp, true);
    window.addEventListener('pointermove', this.onPointerMove, true);
    window.addEventListener('pointercancel', this.onPointerCancel);
    window.addEventListener('blur', this.onBlur);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('online', this.onOnline);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    const shadowRender = this.renderer.shadowMap.render.bind(this.renderer.shadowMap);
    this.renderer.shadowMap.render = (...args) => {
      const before = this.renderer.info.render.calls, triangles = this.renderer.info.render.triangles;
      const updates = this.renderer.shadowMap.enabled && (this.renderer.shadowMap.autoUpdate || this.renderer.shadowMap.needsUpdate);
      shadowRender(...args);
      if (updates) this.shadowFrames++;
      this.frameMetrics.shadowCalls += this.renderer.info.render.calls - before;
      this.frameMetrics.shadowTriangles += this.renderer.info.render.triangles - triangles;
    };
    this.requestFrame();
  }

  private get level(): Level | undefined { return this.hp?.levels[this.levelIndex ?? 0]; }
  private nodeFor(name: string): THREE.Object3D | undefined {
    return this.index ? this.index.get(name) : this.contents?.getObjectByName(name) ?? this.built?.root.getObjectByName(name);
  }
  matchesScope(projectId: string, levelIndex: number): boolean { return this.currentProject === projectId && (this.levelIndex ?? 0) === levelIndex; }
  private retainResources(root: THREE.Object3D): () => void {
    return (this.models as ModelLibrary & { retainResources?(root: THREE.Object3D): () => void })?.retainResources?.(root) ?? (() => {});
  }
  private requestFrame(shadows = false): void {
    this.still?.reset();
    if (shadows && this.renderer?.shadowMap) this.renderer.shadowMap.needsUpdate = true;
    if (this.disposed || !this.active || typeof requestAnimationFrame !== 'function' || (typeof document !== 'undefined' && document.hidden)) return;
    this.scheduler ??= new FrameScheduler(this.loop);
    this.scheduler.request();
  }
  private onControlsChange = (): void => { this.requestFrame(); };

  load(hp: HPScene, opts: LoadOptions = {}): ViewerStats {
    if (this.disposed) return this.stats();
    const { wallMode, ...replay } = opts;
    this.lastLoad = replay;
    this.materialPreview?.end();this.materialPreviewKey='';
    this.appearance?.restore();
    const hadEmptyGeometry = this.built ? this.sceneBounds(false).isEmpty() : false;
    const project = opts.projectId ?? hp.projectId ?? '';
    const levelIndex = Math.max(0, Math.min(opts.levelIndex ?? 0, Math.max(hp.levels.length - 1, 0)));
    const scope = JSON.stringify([project, levelIndex]);
    const scopeChanged = scope !== this.currentScope;
    const projectChanged = !this.hp || project !== this.currentProject;
    if (scopeChanged) {
      if (projectChanged) { this.cameraViews?.clear(); this.hasShownGeometry = false; }
      this.cancelInteraction(); this.cancelTouchNavigation();
      this.settleAppearances();
      this.appearanceSeen?.clear();
      for (const key of this.pendingAppearances?.keys() ?? []) if (key !== scope) this.pendingAppearances.delete(key);
      this.selectionPresentation?.clear(); this.clearConflicts(); this.highlighted = null; this.overlaySignature = '';
      this.clearGathered(); this.setParticipantSelections([]);
      this.surfaceSelection?.clear(); this.openingPresentation?.clear();
      this.materialPreviewState = null;
      this.topLayer?.clear(); this.cohorts?.clear();
      this.hydration.reconcile([]);
      for (const child of [...this.contents.children]) { child.removeFromParent(); disposeTree(child); }
      this.placeholderSignatures.clear();
      this.currentProject = project; this.currentScope = scope; this.levelIndex = levelIndex;
      this.index ??= new SceneIndex(); this.index.clear();
      this.itemInputs = new Map(); this.itemsById = new Map(); this.itemTasks = new Map(); this.openingTasks = [];
      this.previousItems = undefined; this.previousWalls = undefined; this.previousOpenings = undefined;
      this.wallTargets?.clear(); this.wallSlides?.clear(); this.cutWalls?.clear();
    }
    this.reconciling = true;
    try {
    this.hp = hp;
    if (wallMode !== undefined) { this.lowWalls = wallMode === 'low'; this.cutaway = wallMode === 'cutaway'; }
    const level = this.level;
    const isolated = opts.isolatedRoom ?? null;
    const isolationChanged = isolated !== this.isolatedRoom;
    this.isolatedRoom = isolated;
    this.isolatedPolygon = level?.rooms.find((room) => room.index === isolated)?.polygon ?? null;
    const shell = level ? isolateShell({ ...level, items: [] }, isolated) : undefined;
    const wells = this.shellWells(hp, !!opts.showCeiling), wellsSignature = `${wellsKey(wells.floor)}/${wellsKey(wells.ceiling)}`;
    const previous = this.shellInputs;
    const textures = builtinTexturesVersion();
    // The mansards that slope their rooms' ceilings bring the walls down and leave the flat ceilings their flat part. While a
    // piece is dragged the walls keep the slopes they were built for — no shell is rebuilt on a move — and catch up on release.
    const editing = !!this.pointer?.intent.active && this.pointer.intent.kind !== 'camera';
    const ceilings = level ? slopedCeilings(level) : [], slopes = editing && previous ? previous.slopes : ceilingsKey(ceilings);
    const sameShell = previous?.scope === scope && previous.walls === level?.walls && previous.openings === level?.openings
      && previous.rooms === level?.rooms && previous.ceiling === !!opts.showCeiling && previous.isolated === isolated && previous.textures === textures
      && previous.slopes === slopes;
    const signature = sameShell ? this.shellSignature : JSON.stringify([scope, shell, !!opts.showCeiling, textures, slopes], withoutUniqueIds);
    if (level) this.shellInputs = { scope, walls: level.walls, openings: level.openings, rooms: level.rooms, ceiling: !!opts.showCeiling, isolated, textures, slopes };
    const rebuild = signature !== this.shellSignature || !this.built;
    if (rebuild) {
      this.contents.removeFromParent();
      this.releaseShell?.(); this.releaseShell = undefined;
      if (this.built) { this.scene.remove(this.built.root); disposeTree(this.built.root); }
      this.built = buildScene({ ...hp, levels: shell ? [shell] : [] }, {
        maxAnisotropy: this.renderer.capabilities.getMaxAnisotropy(), showCeiling: !!opts.showCeiling,
        textureImages: this.models.textureImages, wells, ceilings,
      });
      this.wellsSignature = wellsSignature;
      this.built.root.getObjectByName('items')?.removeFromParent();
      this.releaseShell = this.retainResources(this.built.root);
      for (const entry of this.index?.values() ?? []) if (entry.node.parent !== this.contents) this.index.remove(entry.node.name);
      for (const node of this.built.root.children) this.index?.upsert(node);
      this.contents.name = 'items';
      this.built.root.add(this.contents);
      this.shellPickable = [...this.built.pickable];
      this.scene.add(this.built.root);
      this.shellSignature = signature;
      // New wall nodes stand at full height: forget what the old ones had settled on.
      this.wallTargets?.clear(); this.wallSlides?.clear();
      this.requestFrame(true);
      this.applyLowWalls();
      this.setStreetViews();
    } else if (shell && wellsSignature !== this.wellsSignature) this.recutWells(shell, wells, wellsSignature);
    this.upgradeItems();
    if (rebuild) this.applyLowWalls();
    const built = this.built!;
    built.stats.items = level?.items.length ?? 0;
    built.stats.lights = level?.items.filter((item) => item.light).length ?? 0;
    const bounds = new THREE.Box3().makeEmpty();
    if (this.index) { for (const entry of this.index.values()) if (entry.node.userData.kind !== 'view') bounds.union(entry.bounds); }
    else for (const child of built.root.children) if (child.userData.kind !== 'view') bounds.expandByObject(child);
    built.bounds = bounds;
    // The floors below follow the document as soon as 3D is on screen; a hidden 3D view builds them when it shows.
    if (this.active) this.syncLowerFloors(); else this.lowerFloorsStale = true;
    this.applyLowerFloorsVisibility();
    if (rebuild) {
      this.aimSun();
      this.fitOutdoors();
    }
    const shown = this.sceneBounds(this.active);
    // The walk climbed to this floor (`climbFloors`): the walker carries on from where it is. Another floor chosen puts it
    // down at the same spot, out of any stairwell.
    const climbed = scopeChanged && !projectChanged && this.walkShift?.to === this.levelIndex ? this.walkShift : null;
    if (scopeChanged && !climbed) this.carried = null;
    if (scopeChanged && this.mode === 'walk') {
      if (projectChanged) this.walkView(); else if (climbed) this.shiftWalk(climbed.shift); else { this.leaveWell(); this.resetWalkSupport(); }
    } else if ((!opts.keepCamera && (!scopeChanged || projectChanged)) || (!scopeChanged && hadEmptyGeometry && !this.hasShownGeometry && !shown.isEmpty() && this.active)) this.frameAll();
    if (this.active && !shown.isEmpty()) this.hasShownGeometry = true;
    if (isolationChanged) {
      for (const node of [...this.contents.children]) if (node.userData?.kind === 'item') this.applyNodeVisibility(node, this.itemsById.get(node.userData.itemId));
      this.requestFrame(true);
    }
    if (this.wallPlan) this.applyWallPlanCamera();
    return this.stats();
    } finally {
      this.reconciling = false;
      this.syncItemAppearances(performance.now(), true);
      this.followHighlight(); this.followSurfaces();
      if (!this.outlineWarmup && this.built && !this.built.bounds.isEmpty()) setTimeout(() => this.warmOutlineShaders(), 0);
      this.updateMaterialSlots();
      this.requestFrame();
    }
  }

  /**
   * After a drag the walls catch up with the slopes it changed: during it the shell kept the slopes it was built for, and
   * the scene the drag ends on is the one its last move showed, which the 3D view does not hand over again.
   */
  private settleSlopes(): void {
    const level = this.level;
    if (!this.hp || !level || !this.shellInputs || this.disposed) return;
    if (ceilingsKey(slopedCeilings(level)) !== this.shellInputs.slopes) this.load(this.hp, { ...this.lastLoad, keepCamera: true });
  }

  hydrationProgress(): HydrationProgress { return this.hydration.progress(); }
  /** «Повторить загрузку моделей»: the models that failed, on every floor drawn, and the pictures that failed without waiting out their pause. */
  retryModels(): void { this.hydration.retry(); this.lowerFloors?.retry(); this.models.textureImages.retryFailed(); this.outdoor?.retry(); }

  /**
   * What the camera frames and the sun lights: the floor in view, or, while it has nothing on it, the floors under it,
   * as the app's 3D shows an empty floor over the ones below. `sync` brings floors below that waited for 3D to show.
   */
  private sceneBounds(sync = true): THREE.Box3 {
    const own = this.built?.bounds ?? new THREE.Box3();
    if (!own.isEmpty()) return own;
    if (sync && this.lowerFloorsStale) this.syncLowerFloors();
    const below = this.lowerFloors?.bounds;
    return below && !below.isEmpty() ? below : own;
  }

  /**
   * The stairwells of the floor in view (`stairwells.ts`): holes in its floor over the flights of the floor below that lead
   * up to it, and in its ceilings, when they are drawn, over its own flights that lead up.
   */
  private shellWells(hp: HPScene, ceiling: boolean): Wells {
    const index = this.levelIndex ?? 0;
    return { floor: wellsDown(hp.levels, index), ceiling: ceiling ? wellsUp(hp.levels, index) : [] };
  }

  /** The flights moved and their stairwells with them: the floors and ceilings are cut again in place, the shell stays. */
  private recutWells(shell: Level, wells: Wells, signature: string): void {
    const root = this.built?.root;
    if (!root) return;
    applyWells(root, shell, wells);
    this.wellsSignature = signature;
    for (const room of shell.rooms) { this.index?.update(`floor:${room.index}`); this.index?.update(`ceiling:${room.index}`); }
    this.index?.remove('stairwells');
    const edges = root.getObjectByName('stairwells');
    if (edges) this.index?.upsert(edges);
    this.requestFrame(true);
  }

  /** Brings the floors below up to date with the document and the floor in view; the ground, the sun and a frame follow a change. */
  private syncLowerFloors(): void {
    this.lowerFloorsStale = false;
    if (!this.hp || this.disposed || (!this.lowerFloors && !this.levelIndex)) return;
    if (!this.lowerFloors) {
      const layer = new LowerFloors({ models: this.models, maxAnisotropy: () => this.renderer.capabilities.getMaxAnisotropy(),
        retain: root => this.retainResources(root), admit: () => this.hydration.idle, changed: () => this.lowerFloorsArrived(),
        settled: () => this.warmLowerFloors(), stairwellsInSight: () => this.mode === 'walk' });
      // Their furniture loads once the floor in view has loaded its own.
      this.hydration.onIdle = () => { layer.resume(); this.floorAbove?.resume(); };
      this.lowerFloors = layer; this.scene.add(layer.root);
    }
    if (!this.lowerFloors.update(this.hp, this.levelIndex ?? 0, builtinTexturesVersion())) return;
    this.applyLowerFloorsVisibility();
    this.fitOutdoors();
    if (this.built?.bounds.isEmpty()) this.aimSun();
    this.requestFrame(true);
  }

  /**
   * The furniture of the floors below comes in the background, hundreds of pieces after the floor in view has its own:
   * it is drawn in batches, a frame at most every LOWER_FLOORS_FRAME_MS, not a frame and a shadow pass per piece.
   */
  private lowerFloorsFrame: ReturnType<typeof setTimeout> | null = null;
  private lowerFloorsArrived(): void {
    if (this.lowerFloorsFrame) return;
    this.lowerFloorsFrame = setTimeout(() => { this.lowerFloorsFrame = null; if (!this.disposed) this.requestFrame(true); }, LOWER_FLOORS_FRAME_MS);
  }

  /**
   * Most furniture of the floors below arrives in rooms the camera cannot see into yet (`LowerFloors.look`). Once they
   * have loaded, its shaders are compiled in the background and its pictures uploaded while the page is idle, so the
   * first look into such a room does not stall a frame on them, and the warming does not hold frames up either.
   */
  private lowerFloorsWarm: ReturnType<typeof setTimeout> | null = null;
  private warmLowerFloors(): void {
    if (this.lowerFloorsWarm) clearTimeout(this.lowerFloorsWarm);
    this.lowerFloorsWarm = setTimeout(() => {
      this.lowerFloorsWarm = null;
      const layer = this.lowerFloors, renderer = this.renderer;
      if (!layer || this.disposed || typeof renderer?.compile !== 'function') return;
      const current = () => !this.disposed && this.lowerFloors === layer;
      compileInBackground(renderer, layer.root, this.camera, this.scene, current).then(() => {
        if (!current()) return;
        const textures = [...layer.textures()];
        const upload = (deadline?: IdleDeadline) => {
          if (this.disposed || this.lowerFloors !== layer) return;
          // One picture at least, more for a few milliseconds of an idle period: a large one takes tens to go up.
          const started = performance.now();
          do { const texture = textures.pop(); if (texture) renderer.initTexture(texture); }
          while (textures.length && (deadline?.timeRemaining() ?? 0) > 4 && performance.now() - started < 8);
          if (textures.length) whenIdle(upload);
        };
        whenIdle(upload);
      }).catch(() => {});
    }, LOWER_FLOORS_WARM_MS);
  }

  /**
   * The floor over the one in view, while the walk is under a stairwell into it and 3D is on screen (`FloorAbove`): the
   * walk looks up the stairwell into it and climbs into it rather than into the sky. The wall plan and an isolated room
   * leave it out, as they leave out the floors below.
   */
  private syncFloorAbove(): void {
    if (!this.hp || this.disposed || !this.renderer) return;
    const index = this.levelIndex ?? 0, shown = this.mode === 'walk' && this.active && !this.wallPlan && this.isolatedRoom == null;
    if (!this.floorAbove && !(shown && wellsUp(this.hp.levels, index).length)) return;
    if (!this.floorAbove) {
      const layer = new FloorAbove({ models: this.models, maxAnisotropy: () => this.renderer.capabilities.getMaxAnisotropy(),
        retain: root => this.retainResources(root), admit: () => this.hydration.idle, changed: () => this.lowerFloorsArrived() });
      this.hydration.onIdle = () => { this.lowerFloors?.resume(); layer.resume(); };
      this.floorAbove = layer; this.scene.add(layer.root);
    }
    if (this.floorAbove.update(this.hp, index, builtinTexturesVersion(), shown)) this.requestFrame(true);
  }

  /** The floor drawn over the one in view, for checks. */
  floorAboveState(): { level: string | null; offset: number; items: number; openings: number; visible: boolean } | null {
    return this.floorAbove ? { ...this.floorAbove.describe(), visible: this.floorAbove.root.visible } : null;
  }

  /** The wall plan draws one wall of the floor in view, and «Скрыть другие комнаты» one room: the floors below go. */
  private applyLowerFloorsVisibility(): void {
    if (this.lowerFloors) this.lowerFloors.root.visible = !this.wallPlan && this.isolatedRoom == null;
    this.syncFloorAbove();
  }

  /**
   * The view out of the windows centres on everything drawn, and its ground lies at the lowest floor: under the floor
   * in view by the floors' altitudes, whether or not the floors below are built yet.
   */
  private fitOutdoors(): void {
    const bounds = (this.built?.bounds ?? new THREE.Box3()).clone(), below = this.lowerFloors?.bounds;
    if (below && !below.isEmpty()) bounds.union(below);
    if (bounds.isEmpty() || !this.outdoor) return;
    const centre = bounds.getCenter(new THREE.Vector3());
    this.outdoor.fit({ x: centre.x, z: centre.z }, -levelAltitude(this.hp?.levels ?? [], this.levelIndex ?? 0) - .005);
    this.fitFootprint();
  }

  /** The house's footprint on that ground, which cuts it away under the house: the rooms and walls of the lowest floor. */
  private fitFootprint(): void {
    const levels = this.hp?.levels ?? [], ground = levels[0], index = this.levelIndex ?? 0;
    if (!ground || !this.outdoor) return;
    const key = this.footprintKey;
    if (key && key.rooms === ground.rooms && key.walls === ground.walls) return;
    this.footprintKey = { rooms: ground.rooms, walls: ground.walls };
    const shapes: Vec2[][] = ground.rooms.map(room => room.polygon);
    for (const wall of ground.walls) {
      if (wall.hidden) continue;
      const n = { x: -wall.dir.z * wall.thickness / 2, z: wall.dir.x * wall.thickness / 2 };
      shapes.push([{ x: wall.start.x + n.x, z: wall.start.z + n.z }, { x: wall.end.x + n.x, z: wall.end.z + n.z },
        { x: wall.end.x - n.x, z: wall.end.z - n.z }, { x: wall.start.x - n.x, z: wall.start.z - n.z }]);
    }
    this.outdoor.setFootprint(shapes);
  }
  private footprintKey: { rooms: readonly unknown[]; walls: readonly unknown[] } | null = null;

  /** Towards the view's own sun, turned with the view; null without a view or a sun, when the house keeps its usual one. */
  private sunDirection(): [number, number, number] | null {
    const chosen = this.windowView?.view;
    return chosen ? viewSunDirection(chosen, this.windowView.rotation) : null;
  }

  /**
   * The view out of the windows (`src/state/windowViews.ts`), turned by `rotation` degrees clockwise on the plan;
   * «none» draws the plain background. The sun follows the view's own sun, and its shadows with it.
   */
  setWindowView(id: string, rotation: number): void {
    const view = id === NO_WINDOW_VIEW ? null : windowViewById(id);
    const before = this.sunDirection()?.join(), turnOnly = view?.id === this.windowView?.view?.id;
    this.windowView = { view, rotation };
    this.outdoor?.setView(view, rotation);
    if (this.sunDirection()?.join() === before) return;
    // The sky turns at once; while the view is being turned the sun and its shadows follow when the turning pauses,
    // not on every step of a slider.
    clearTimeout(this.sunTimer);
    const aim = () => { if (this.disposed) return; this.aimSun(); this.fitFootprint(); this.requestFrame(true); };
    if (turnOnly) this.sunTimer = setTimeout(aim, 120); else aim();
  }
  private sunTimer: ReturnType<typeof setTimeout> | undefined;
  /** The view's pictures started coming, came in, or failed. */
  onWindowViewStatus(listener: (status: OutdoorStatus) => void): () => void {
    this.windowViewListeners.add(listener);
    return () => { this.windowViewListeners.delete(listener); };
  }
  windowViewState(): ReturnType<OutdoorView['state']> & { sun: number[] | null } {
    return { ...this.outdoor.state(), sun: this.sunDirection() };
  }
  /** Asks again for a view whose pictures failed. */
  retryWindowView(): void { this.outdoor?.retry(); }

  /** The floors drawn under the one in view, for checks: each with where it stands, and how their furniture is loading. */
  lowerFloorsState(): { visible: boolean; floors: LowerFloorState[]; progress: HydrationProgress | null; bounds: number[] | null;
    batches: { cohortMeshes: number; cohortInstances: number } | null } {
    const layer = this.lowerFloors;
    return { visible: !!layer?.root.visible, floors: layer?.describe() ?? [], progress: layer?.progress() ?? null,
      bounds: layer && !layer.bounds.isEmpty() ? [...layer.bounds.min.toArray(), ...layer.bounds.max.toArray()] : null,
      batches: layer?.batches() ?? null };
  }

  private releaseHydratedNode(node:THREE.Object3D):void {
    this.cohorts?.remove(node);
    const target=this.materialPreviewState?.target;
    if(target?.kind==='item'&&node.name===`item:${target.itemId}`&&this.nodeFor(node.name)===node){
      // The queue releases its previous value before attaching the replacement.
      // Restore borrowed material assignments first, so library ownership remains
      // canonical and the new target receives the current UV draft on attachment.
      this.materialPreview?.end();this.materialPreviewKey='';
    }
    if (this.index?.get(node.name) === node) this.index.remove(node.name);
    this.topLayer?.invalidate(node.name);
    if(node.userData.generated){node.removeFromParent();disposeTree(node);}
    else this.models.release(node);
  }

  private upgradeItems(): void {
    const level = this.level;
    if (!this.built || !level) { this.hydration.reconcile([]); return; }
    if (this.previousItems === level.items && this.previousWalls === level.walls && this.previousOpenings === level.openings) return;
    this.itemInputs ??= new Map(); this.itemTasks ??= new Map();
    this.itemsById = new Map(level.items.map(item => [item.id, item]));
    const aniso = this.renderer.capabilities.getMaxAnisotropy();
    const wanted = new Set(level.items.map(item => `item:${item.id}`));
    for (const opening of level.openings) wanted.add(`opening:${opening.id}`);
    for (const child of [...this.contents.children]) {
      if (wanted.has(child.name)) continue;
      this.cohorts?.remove(child);
      this.index?.remove(child.name); this.topLayer?.invalidate(child.name);
      child.removeFromParent();
      if (!child.userData.real && !child.userData.generated && child.userData.kind !== 'opening') disposeTree(child);
    }
    for (const id of this.itemInputs.keys()) if (!this.itemsById.has(id)) { this.itemInputs.delete(id); this.itemTasks.delete(id); }
    const tasks: HydrationTask<THREE.Object3D>[] = [];
    // A mansard that slopes its room's ceiling is drawn as that ceiling (src/render/three/slope.ts); one standing free, as its wedge.
    const slopes = new Map<string, { ceiling: RoomCeiling; index: number }>(), ceilings = slopedCeilings(level);
    for (const ceiling of ceilings) ceiling.slopes.forEach((slope, index) => slopes.set(slope.item.id, { ceiling, index }));
    // What hangs from the ceiling over a slope hangs from the slope: drawn as far down as the slope comes over it.
    const drops = new Map<string, number>();
    if (ceilings.length) for (const item of level.items) {
      if (item.placement !== 'ceiling' || isAttic(item)) continue;
      const drop = item.elevation + item.size.h - lowestCeilingOver(ceilings, itemFootprint(item));
      if (drop > 1e-3) drops.set(item.id, drop);
    }
    const previousDrops = this.itemDrops ?? new Map<string, number>();
    this.itemDrops = drops;
    for (const item of level.items) {
      // A mansard is shaped by the openings of its wall too (src/render/three/attic.ts): its signature carries its cuts.
      const attic = isAttic(item), sloped = attic ? slopes.get(item.id) : undefined, drop = drops.get(item.id) ?? 0;
      const signature = sloped ? slopeSignature(item, sloped.ceiling, sloped.index) : attic ? atticSignature(item, level) : itemSignature(item);
      if (this.itemInputs.get(item.id) === item && (previousDrops.get(item.id) ?? 0) === drop
        && (!attic || this.itemTasks.get(item.id)?.signature === this.currentScope + signature)) { tasks.push(this.itemTasks.get(item.id)!); continue; }
      const key = `item:${item.id}`;
      let node = this.nodeFor(key);
      if (node && !node.userData.real && !node.userData.generated && this.placeholderSignatures.get(key) !== signature) {
        this.index?.remove(key); node.removeFromParent(); disposeTree(node); node = undefined;
      }
      if (!node) {
        node = sloped ? slopeNode(item, sloped.ceiling, sloped.index, { maxAnisotropy: aniso, textureImages: this.models.textureImages })
          : attic ? atticNode(item, level, { maxAnisotropy: aniso, textureImages: this.models.textureImages })
          : placeholderFor(item, aniso, this.models.textureImages, this.placeholderStateFor(item));
        rememberItemFit(node, item);
        this.contents.add(node); this.placeholderSignatures.set(key, signature);
      }
      placeItem(node, item, drop); this.registerNode(node); this.applyNodeVisibility(node, item);
      this.topLayer?.invalidate(key); this.requestFrame(true);
      // Native daylight excludes room lamps entirely from the light uniform list.
      const procedural = isRegion(item) || attic;
      const task: HydrationTask<THREE.Object3D> = { key, signature: this.currentScope + signature,
        revision: item, kind: procedural ? 'procedural' : 'item', label: item.caption ?? item.modelRef.id,
        build: async () => {
          if (sloped) { const faces = slopeNode(item, sloped.ceiling, sloped.index, { maxAnisotropy: aniso, textureImages: this.models.textureImages }); rememberItemFit(faces, item); return faces; }
          if (attic) { const wedge = atticNode(item, level, { maxAnisotropy: aniso, textureImages: this.models.textureImages }); rememberItemFit(wedge, item); return wedge; }
          if (!procedural) { const model = await this.models.instance(item, aniso); if (model) rememberItemFit(model, item); return model; }
          const panel = placeholderFor(item, aniso, this.models.textureImages);
          panel.userData.generated = true; rememberItemFit(panel, item); return panel;
        },
        apply: real => { if (!this.reconciling) this.appearance?.restore(); placeItem(real, item, this.itemDrops?.get(item.id) ?? 0); this.attachModel(real); },
        unavailable: () => {
          const standing = this.nodeFor(key);
          if (standing?.parent !== this.contents) {
            const placeholder = placeholderFor(item, aniso, this.models.textureImages, this.placeholderStateFor(item));
            rememberItemFit(placeholder,item); this.contents.add(placeholder); this.registerNode(placeholder);
            this.applyNodeVisibility(placeholder,item); this.topLayer?.invalidate(key);
          } else if ((standing as THREE.Mesh).isMesh && !standing.userData.real) paintPlaceholder(standing as THREE.Mesh, this.placeholderStateFor(item));
          this.requestFrame(true);
        },
      };
      this.itemInputs.set(item.id, item); this.itemTasks.set(item.id, task); tasks.push(task);
    }
    // A window wholly over a mansard's knee lies in its slope (src/state/attic.ts): turned there as it is built.
    const roofs = roofWindows(level);
    // In a slope of its room's ceiling the window stands at the outer end of its lining, the sky beyond it.
    const lined = (itemId: string) => slopes.has(itemId) ? ROOF_WINDOW_LINING : 0;
    const roofKey = JSON.stringify([...roofs].map(([id, roof]) => [id, roof.frame.origin, roof.frame.inward, roof.frame.knee, roof.frame.height, roof.frame.depth, lined(roof.item.id)]));
    if (this.previousWalls !== level.walls || this.previousOpenings !== level.openings || this.previousRoofKey !== roofKey) {
      this.openingTasks = [];
      const openings = new Map(level.openings.map(opening => [opening.id, opening]));
      for (const solid of buildWallSolids(level)) for (const reveal of solid.reveals) {
        const opening = openings.get(reveal.openingId); if (!opening) continue;
        const roof = roofs.get(opening.id);
        this.openingTasks.push({ key: `opening:${opening.id}`,
          signature: this.currentScope + JSON.stringify([opening, reveal.frame, roof ? [roof.frame.origin, roof.frame.inward, roof.frame.knee, roof.frame.height, roof.frame.depth, lined(roof.item.id)] : null], withoutUniqueIds),
          revision: opening, kind: 'opening', label: opening.caption ?? opening.modelRef.id,
          build: async () => {
            const model = await buildOpeningModel(this.models, opening, reveal.frame, aniso);
            if (model && roof) tiltIntoSlope(model, opening, roof.wall, roof.frame, lined(roof.item.id));
            return model;
          }, apply: real => this.attachModel(real) });
      }
    }
    this.previousRoofKey = roofKey;
    tasks.push(...this.openingTasks);
    this.previousItems = level.items; this.previousWalls = level.walls; this.previousOpenings = level.openings;
    this.hydration.reconcile(tasks);
    this.refreshPickable();
  }

  private registerNode(node: THREE.Object3D): void {
    node.traverse(object => {
      if ((object as THREE.Mesh).isMesh && (node.userData.kind === 'item' || node.userData.kind === 'opening'))
        object.userData = { ...object.userData, ...node.userData };
    });
    this.index?.upsert(node);
  }

  private applyNodeVisibility(node: THREE.Object3D, item?: Item): void {
    const level = this.level; if (!level) return;
    const opening = item ? undefined : level.openings.find(value => `opening:${value.id}` === node.name);
    applyModeVisibility(node, { ...level, walls: [], items: item ? [item] : [], openings: opening ? [opening] : [] },
      this.visibilityMode(), name => name === node.name ? node : undefined);
    // "Hide other rooms": an item outside the isolated room goes with its room.
    if (this.isolatedPolygon && item && !containsPoint(this.isolatedPolygon, item.position)) node.visible = false;
    // The wall plan: only this wall's openings and what stands against its face.
    const wall = this.wallPlanWall();
    if (wall && this.wallPlan) {
      if (item) node.visible &&= onWallPlan(item, level, wall, this.wallPlan.target.side);
      else if (opening) node.visible = opening.wallId === wall.id;
    }
    this.index?.update(node.name);
    this.syncCohort(node, item);
  }

  private syncCohort(node: THREE.Object3D, item = this.itemsById?.get(node.userData.itemId)): void {
    if (!item || !node.userData.real) return;
    if (!this.cohorts) {
      this.cohorts = new InstancedCohorts(root => this.retainResources(root)); this.scene.add(this.cohorts.root);
    }
    const target = this.materialPreviewState?.target;
    this.cohorts.update(node, item, this.highlighted === item.id || !!this.participantItemIds?.has(item.id) || !!this.gathered?.includes(item.id) || this.pointer?.item?.id === item.id
      || !!this.appearingIds?.has(item.id) || (target?.kind === 'item' && target.itemId === item.id));
  }

  private attachModel(real: THREE.Object3D): void {
    const old = this.nodeFor(real.name);
    if (old && old !== real) {
      this.materialPreview?.end(); this.materialPreviewKey = '';
      this.cohorts?.remove(old);
      this.index?.remove(old.name); old.removeFromParent();
      if (!old.userData.real && !old.userData.generated && old.userData.kind !== 'opening') disposeTree(old);
    }
    if (real.parent !== this.contents) this.contents.add(real);
    this.registerNode(real);
    const item = real.userData.kind === 'item' ? this.itemsById?.get(real.userData.itemId) : undefined;
    this.applyNodeVisibility(real, item);
    this.topLayer?.invalidate(real.name);
    if (!this.reconciling) this.syncItemAppearances(performance.now());
    if (this.highlighted && real.name === `item:${this.highlighted}` || this.selectedOpening && real.name === `opening:${this.selectedOpening}`) this.updateMaterialSlots();
    if (this.materialPreviewState) this.setMaterialTransformPreview(this.materialPreviewState);
    this.requestFrame(true);
  }

  /** Explicit editor events only: loading documents and undo/redo never create these. */
  queueItemAppearances(projectId: string, ids: readonly string[], levelIndex = 0): void {
    if (this.disposed) return;
    const scope = JSON.stringify([projectId, levelIndex]);
    const pending = this.pendingAppearances.get(scope) ?? new Set<string>();
    for (const id of ids) {
      if (scope === (this.currentScope || JSON.stringify([this.currentProject, 0])) && this.appearanceSeen.has(id)) continue;
      if (this.active) pending.add(id);
      else if (scope === (this.currentScope || JSON.stringify([this.currentProject, 0]))) this.appearanceSeen.add(id);
    }
    if (pending.size) this.pendingAppearances.set(scope, pending);
  }

  private syncItemAppearances(now: number, consume = false): void {
    const level = this.level;
    if (!level || !this.contents) return;
    for (const id of this.appearingIds ?? []) {
      const node = this.nodeFor(`item:${id}`);
      if (!node || !(this.itemsById?.has(id) ?? level.items.some(item => item.id === id))) {
        this.appearance?.settle(id); this.appearingIds.delete(id);
      } else this.appearance?.replace(id, node, now, true);
    }
    if (consume) {
      for (const id of this.pendingAppearances?.get(this.currentScope || JSON.stringify([this.currentProject, 0])) ?? []) {
        const item = this.itemsById?.get(id) ?? level.items.find(item => item.id === id), node = this.nodeFor(`item:${id}`);
        if (item && node && this.active && !this.appearanceSeen.has(id)) {
          if (!this.appearance) {
            this.appearanceRoot = new THREE.Group(); this.scene.add(this.appearanceRoot);
            this.appearance = new ItemAppearance(this.appearanceRoot);
          }
          this.appearingIds.add(id); this.syncCohort(node, item); this.cohorts?.flush();
          this.appearance.begin(id, node, item, now);
        }
        this.appearanceSeen.add(id);
      }
      this.pendingAppearances?.delete(this.currentScope || JSON.stringify([this.currentProject, 0]));
      for (const item of level.items) this.appearanceSeen?.add(item.id);
    }
    this.appearance?.update(now);
    if (!this.appearance?.active) this.finishCohortAppearances();
  }

  private finishCohortAppearances(): void {
    const ids = [...(this.appearingIds ?? [])]; this.appearingIds?.clear();
    for (const id of ids) { const node = this.nodeFor(`item:${id}`); if (node) this.syncCohort(node); }
  }

  private settleAppearances(): void {
    const changed = !!this.appearance?.active;
    this.appearance?.settle();
    for (const id of this.appearingIds ?? []) this.index?.update(`item:${id}`);
    this.finishCohortAppearances();
    if (changed) this.requestFrame(true);
  }

  private refreshPickable(): void {
    if (!this.built) return;
    this.built.pickable = [...this.shellPickable];
    this.contents.traverse((object) => {
      if (!(object as THREE.Mesh).isMesh) return;
      let owner = object;
      while (owner.parent && owner.parent !== this.contents) owner = owner.parent;
      if (owner.userData.kind === 'item' || owner.userData.kind === 'opening') {
        object.userData = { ...object.userData, ...owner.userData };
        this.built!.pickable.push(object);
      }
    });
  }

  private saveCameraView(): void {
    if (!this.hp || !this.camera || !this.controls?.target) return;
    this.cameraViews ??= new Map();
    this.cameraViews.set(this.mode, { position: this.camera.position.clone(),
      quaternion: this.camera.quaternion.clone(), target: this.controls.target.clone(), fov: this.camera.fov,
      walkFov: this.walkFovH, yaw: this.walk.yaw, pitch: this.walk.pitch });
  }

  private restoreCameraView(): boolean {
    const view = this.cameraViews?.get(this.mode); if (!view) return false;
    this.camera.position.copy(view.position); this.camera.quaternion.copy(view.quaternion);
    this.controls.target.copy(view.target); this.camera.fov = view.fov; this.walkFovH = view.walkFov;
    if (this.mode === 'walk') {
      this.walk.pos.copy(view.position); this.walk.yaw = view.yaw; this.walk.pitch = view.pitch;
      this.resetWalkSupport(view.position.y - this.walkEyeHeight);
    }
    this.camera.updateProjectionMatrix(); this.camera.updateMatrixWorld(); this.setStreetViews();
    this.requestFrame(); return true;
  }

  /**
   * Both cameras as plain numbers, for storing where the user left the project. The one
   * in use is written down first, so the state matches what is on screen right now.
   */
  cameraState(): SavedCameras | null {
    if (!this.hp || this.mode === 'top') return null;
    this.saveCameraView();
    const flatten = (mode: CameraMode): SavedCameraView | null => {
      const view = this.cameraViews?.get(mode);
      if (!view) return null;
      // The camera mode's zoom is a framing aid the editor drops when its panel closes;
      // the view the project is left on is the one underneath it.
      const base = mode === this.mode ? this.zoomBase : null;
      return { position: view.position.toArray(), quaternion: view.quaternion.toArray() as [number, number, number, number],
        target: view.target.toArray(), fov: base?.fov ?? view.fov, walkFov: base ? base.walkFov : view.walkFov,
        yaw: view.yaw, pitch: view.pitch };
    };
    const orbit = flatten('orbit'), walk = flatten('walk');
    return orbit || walk ? { mode: this.mode === 'walk' ? 'walk' : 'orbit', orbit, walk } : null;
  }

  /** Transient view uses the actual lens on screen, including photo zoom. */
  participantViewport(): PresenceViewport | null {
    if (!this.hp || !this.active || !this.canvas.clientWidth || !this.canvas.clientHeight) return null;
    if (this.wallPlan) return { kind: 'wall', ...this.wallPlan.target, zoom: this.wallPlan.zoom, pan: { ...this.wallPlan.pan }, following: null };
    if (this.mode === 'top') return null;
    return { kind: this.mode, camera: { position: this.camera.position.toArray(),
      quaternion: this.camera.quaternion.toArray() as [number, number, number, number], target: this.controls.target.toArray(),
      fov: this.camera.fov, walkFov: this.walkFovH, yaw: this.walk.yaw, pitch: this.walk.pitch },
      wallMode: this.cutaway ? 'cutaway' : this.lowWalls ? 'low' : 'full', showCeiling: this.shellInputs?.ceiling ?? false,
      isolatedRoom: this.isolatedRoom, following: null };
  }

  /** Called only after context reconciliation; does not cancel or commit an edit. */
  applyParticipantViewport(view: PresenceViewport): boolean {
    if (!this.active || !this.hp || !this.canvas.clientWidth || !this.canvas.clientHeight) return false;
    if (view.kind === 'floor' || view.kind === 'ceiling') return false;
    if (view.kind === 'wall') {
      if (this.wallPlan?.target.wallId !== view.wallId || this.wallPlan.target.side !== view.side) return false;
      this.stopCameraMotion(); this.stopWallSlide();
      this.participantView = true;
      this.wallPlan.zoom = view.zoom; this.wallPlan.pan = { ...view.pan };
      this.applyWallPlanCamera(); this.requestFrame(true); return true;
    }
    if (this.wallPlan || this.mode !== view.kind || (this.cutaway ? 'cutaway' : this.lowWalls ? 'low' : 'full') !== view.wallMode
      || (this.shellInputs?.ceiling ?? false) !== view.showCeiling || this.isolatedRoom !== view.isolatedRoom) return false;
    this.stopCameraMotion();
    this.participantView = true;
    const camera = view.camera;
    this.zoomBase = null;
    this.camera.position.fromArray(camera.position); this.camera.quaternion.fromArray(camera.quaternion).normalize();
    this.controls.target.fromArray(camera.target); this.camera.fov = camera.fov; this.walkFovH = camera.walkFov;
    this.walk.pos.copy(this.camera.position); this.walk.yaw = camera.yaw; this.walk.pitch = camera.pitch;
    if (view.kind === 'walk') { this.walkSupportY = Math.max(0, camera.position[1] - this.walkEyeHeight); this.applyWalkFov(); }
    this.camera.updateProjectionMatrix(); this.camera.updateMatrixWorld();
    this.hasShownGeometry = true; this.saveCameraView(); this.requestFrame(true); return true;
  }

  private stopCameraMotion(): void {
    if (this.roomNavigationFrame) cancelAnimationFrame(this.roomNavigationFrame);
    this.roomNavigationFrame = 0; this.walk.keys.clear();
    // Flush OrbitControls' residual delta before applying the authoritative pose.
    const damping = this.controls.enableDamping;
    this.controls.enableDamping = false; this.controls.update(); this.controls.enableDamping = damping;
  }

  releaseParticipantViewport(): void { this.participantView = false; }

  /**
   * Re-enter a project on its stored cameras. `mode` is what the editor asks for and wins
   * over the stored one; the other camera is kept so switching modes lands where it was
   * left too. False means nothing usable was stored and the caller should frame as usual.
   */
  restoreCameraState(state: SavedCameras, mode: 'orbit' | 'walk'): boolean {
    if (!this.camera || !this.controls?.target) return false;
    this.cameraViews ??= new Map();
    for (const key of ['orbit', 'walk'] as const) {
      const view = state[key];
      if (view) this.cameraViews.set(key, { position: new THREE.Vector3().fromArray(view.position),
        quaternion: new THREE.Quaternion().fromArray(view.quaternion), target: new THREE.Vector3().fromArray(view.target),
        fov: view.fov, walkFov: view.walkFov, yaw: view.yaw, pitch: view.pitch });
    }
    // Leave the live mode alone when there is nothing to restore, so the caller's own
    // framing still runs.
    if (!state[mode]) return false;
    this.settleAppearances(); this.cancelInteraction();
    this.mode = mode;
    if (!this.restoreCameraView()) return false;
    // The window is rarely the size it was last time, and the walk view's vertical fov
    // is derived from the aspect.
    if (mode === 'walk') this.applyWalkFov();
    this.controls.enabled = this.controlsAllowed();
    // Framing "the first time geometry appears" would throw the restored camera away.
    if (this.built && !this.sceneBounds().isEmpty()) this.hasShownGeometry = true;
    this.requestFrame(true);
    return true;
  }

  /** Orbit the floor in view into view, filling the viewport; an empty floor, the floors under it. */
  frameAll(): void {
    // Empty Box3 corners are infinite; fitting them would permanently poison the camera.
    const box = this.sceneBounds();
    if (!this.built || box.isEmpty()) return;
    const centre = box.getCenter(new THREE.Vector3());
    this.mode = 'orbit';
    this.setStreetViews();
    this.walkFovH = null;
    this.controls.enabled = this.controlsAllowed();
    this.camera.fov = 50;
    this.camera.updateProjectionMatrix();
    this.camera.position.copy(centre).addScaledVector(FIT_VIEW, -(this.orbitFitDistance() ?? 0.5));
    this.controls.target.copy(centre);
    this.controls.update();
  }

  /** Straight-down view, framed like the 2D plan. */
  topView(): void {
    const box = this.sceneBounds();
    if (!this.built || box.isEmpty()) return;
    const centre = box.getCenter(new THREE.Vector3());
    this.mode = 'top';
    this.setStreetViews();
    this.walkFovH = null;
    this.controls.enabled = this.controlsAllowed();
    this.camera.fov = 50;
    this.camera.updateProjectionMatrix();
    // The tiny z offset resolves the degenerate straight-down roll. Its sign fixes the
    // plan orientation: +z on the far side gives +x right / +z down, the same handedness
    // the 2D plan uses. Flipping it mirrors nothing (a top projection cannot mirror) but
    // rotates the plan 180°, which reads as a bug when the two views sit side by side.
    this.controls.target.copy(centre);
    this.camera.position.set(
      centre.x,
      centre.y + this.fitDistance(box, new THREE.Vector3(0, -1, 0), 1.02),
      centre.z + 0.001,
    );
    this.controls.update();
  }

  /**
   * Distance at which the whole bounding box fits, viewed along `dir`.
   *
   * A bounding SPHERE is far too loose for a long thin apartment — the diagonal sets the
   * radius and the room ends up filling a third of the frame. This projects the eight real
   * corners instead.
   */
  private fitDistance(box: THREE.Box3, dir: THREE.Vector3, margin: number): number {
    const centre = box.getCenter(new THREE.Vector3());
    const forward = dir.clone().normalize();
    const up = Math.abs(forward.y) > 0.98 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
    const right = new THREE.Vector3().crossVectors(up, forward).normalize();
    const trueUp = new THREE.Vector3().crossVectors(forward, right).normalize();
    const tanV = Math.tan((this.camera.fov * Math.PI) / 360);
    const tanH = tanV * Math.max(this.camera.aspect, 0.2);
    let need = 0;
    const p = new THREE.Vector3();
    for (let i = 0; i < 8; i++) {
      p.set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z).sub(centre);
      const depth = p.dot(forward);
      need = Math.max(need, Math.abs(p.dot(right)) / tanH - depth, Math.abs(p.dot(trueUp)) / tanV - depth);
    }
    return Math.max(need * margin, 0.5);
  }

  /**
   * Stand where the project's saved camera stands. The app hands that camera to the view it was
   * saved in, so only a walk camera is stood in as saved: point, heading, gaze, lens and eye
   * height. Another view's camera (`PanoramaView` is the app's orbit) starts the walk the way
   * the app's Walk button leaves its orbit: on the floor under it at eye height, heading kept,
   * gaze level (Unity WalkView.EnterView → CameraPoseModel.InitCameraViewAngles).
   */
  walkView(): void {
    const saved = this.hp?.camera, walked = this.walkCamera;
    // The app's 2D plan saves no point, only the origin: its camera lends nothing.
    const cam = walked ?? (saved && Math.hypot(saved.position.x, saved.position.z) > 1e-6 ? saved : undefined);
    this.mode = 'walk';
    this.setStreetViews();
    this.controls.enabled = false;
    const rooms = this.level?.rooms ?? [];
    const savedInside = cam && (!rooms.length || rooms.some(room => containsPoint(room.polygon, cam.position)));
    const room = rooms.reduce<Level['rooms'][number] | undefined>((best, candidate) => !best || candidate.area > best.area ? candidate : best, undefined);
    const shown = this.built ? this.sceneBounds() : null;
    const center = shown && !shown.isEmpty() ? shown.getCenter(new THREE.Vector3()) : new THREE.Vector3();
    const position = savedInside ? cam.position : interiorFloorPoint(room ? [room] : []) ?? {x:center.x,z:center.z};
    this.walk.pos.set(position.x, this.walkEyeHeight, position.z);
    this.leaveWell(); this.resetWalkSupport();
    const xs = room?.polygon.map(p=>p.x) ?? [0], zs = room?.polygon.map(p=>p.z) ?? [0];
    this.walk.yaw = cam?.yaw ?? (Math.max(...xs)-Math.min(...xs) >= Math.max(...zs)-Math.min(...zs) ? Math.PI/2 : 0);
    this.walk.pitch = walked?.pitch ?? (cam ? 0 : -.05);
    this.walkFovH = walked?.fovHorizontal ?? null;
    if (!walked) { this.camera.fov = 90; this.camera.updateProjectionMatrix(); }
    this.applyWalkFov();
    this.applyWalk(); this.requestFrame(true);
  }

  setCameraMode(mode: CameraMode): void {
    this.settleAppearances();
    this.cancelInteraction();
    this.stopWalkMotion(); this.walkApproach = null;
    if (mode !== this.mode) {
      this.saveCameraView(); this.mode = mode;
      if (this.restoreCameraView()) { this.controls.enabled = this.controlsAllowed(); this.requestFrame(true); return; }
    }
    if (mode === 'orbit') this.frameAll();
    else if (mode === 'top') this.topView();
    else this.walkView();
  }

  get cameraMode(): CameraMode { return this.mode; }

  /** Runs after every drawn frame — a camera move always draws one. Returns the unsubscribe. */
  onFrame(listener: () => void): () => void {
    this.frameListeners.add(listener);
    return () => { this.frameListeners.delete(listener); };
  }

  cameraPose(): CameraPose {
    const wall = this.wallPlanWall();
    if (this.wallPlan && wall) {
      const n = faceNormal(wall, this.wallPlan.target.side);
      return { mode: this.mode, position: { x: this.wallCamera.position.x, z: this.wallCamera.position.z }, height: this.wallCamera.position.y,
        yaw: Math.atan2(-n.x, n.z), forward: { x: -n.x, z: -n.z }, target: { x: wall.center.x, z: wall.center.z } };
    }
    const direction = this.camera.getWorldDirection(new THREE.Vector3());
    let yaw: number;
    if (Math.hypot(direction.x, direction.z) > 1e-6) {
      yaw = Math.atan2(direction.x, -direction.z);
    } else {
      const right = new THREE.Vector3(1, 0, 0).applyQuaternion(this.camera.getWorldQuaternion(new THREE.Quaternion()));
      yaw = Math.atan2(right.z, right.x);
    }
    return { mode: this.mode, position: { x: this.camera.position.x, z: this.camera.position.z },
      height: this.camera.position.y, yaw, forward: { x: direction.x, z: direction.z }, target: { x: this.controls.target.x, z: this.controls.target.z } };
  }

  /**
   * Take the camera to a room, the minimap's tap: a walk teleports to the room's interior
   * point; an orbit pans there while keeping its height and angle.
   */
  navigateToRoom(room: Room): void {
    this.interactionHandlers?.onNavigationIntent?.();
    const centre = interiorPoint(room.polygon);
    this.settleAppearances(); this.cancelInteraction();
    if (this.mode === 'walk') {
      this.stopWalkMotion(); this.walkApproach = null;
      this.walk.pos.set(centre.x, this.walkEyeHeight, centre.z);
      this.leaveWell(); this.resetWalkSupport();
      this.applyWalk(); this.saveCameraView(); this.requestFrame(true);
      return;
    }
    this.panOrbit(centre.x - this.controls.target.x, centre.z - this.controls.target.z);
  }

  /**
   * «Войти в комнату» through a door (the app's EnterRoom.EnterThroughDoor): the walk flies through it over a second and
   * stands a metre into the room on its other side, looking on, as a second double tap on the door takes it; the orbit
   * pans to that room, as `navigateToRoom` does.
   */
  enterThroughDoor(openingId: string, room: Room): void {
    const level = this.level, opening = level?.openings.find(value => value.id === openingId), wall = opening && level?.walls.find(value => value.id === opening.wallId);
    if (this.mode !== 'walk' || !opening || !wall || this.participantView) { this.navigateToRoom(room); return; }
    this.interactionHandlers?.onNavigationIntent?.();
    this.settleAppearances(); this.cancelInteraction();
    this.stopWalkMotion(); this.walkApproach = null; this.walkCoast = null; this.walkPending = null;
    this.walkFlight = { from: { x: this.walk.pos.x, z: this.walk.pos.z, yaw: this.walk.yaw, pitch: this.walk.pitch },
      to: throughDoor(wall, opening, { x: this.walk.pos.x, z: this.walk.pos.z }), started: performance.now() };
    this.requestFrame(true);
  }

  /** Slide the orbit camera and its target across the floor, keeping height and angle. */
  private panOrbit(dx: number, dz: number): boolean {
    if (Math.hypot(dx, dz) < 1e-3) return false;
    if (this.roomNavigationFrame) cancelAnimationFrame(this.roomNavigationFrame);
    const startTarget = this.controls.target.clone(), startPosition = this.camera.position.clone(), started = performance.now();
    const step = () => {
      this.roomNavigationFrame = 0;
      if (this.disposed) return;
      const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
      const t = reduced ? 1 : Math.min(1, (performance.now() - started) / 180), eased = 1 - (1 - t) ** 3;
      this.controls.target.set(startTarget.x + dx * eased, startTarget.y, startTarget.z + dz * eased);
      this.camera.position.set(startPosition.x + dx * eased, startPosition.y, startPosition.z + dz * eased);
      this.controls.update(); this.requestFrame(true);
      if (t < 1) this.roomNavigationFrame = requestAnimationFrame(step); else this.saveCameraView();
    };
    step();
    return true;
  }

  /** Turn the walker on the spot, including height when an edit needs to be revealed. */
  private faceWalkTowards(yaw: number, pitch = this.walk.pitch): boolean {
    const start = this.walk.yaw, startPitch = this.walk.pitch;
    const delta = Math.atan2(Math.sin(yaw - start), Math.cos(yaw - start));
    const pitchDelta = clamp(pitch, -1.4, 1.4) - startPitch;
    if (Math.abs(delta) < 1e-3 && Math.abs(pitchDelta) < 1e-3) return false;
    if (this.roomNavigationFrame) cancelAnimationFrame(this.roomNavigationFrame);
    const started = performance.now();
    const step = () => {
      this.roomNavigationFrame = 0;
      if (this.disposed || this.mode !== 'walk') return;
      const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
      const t = reduced ? 1 : Math.min(1, (performance.now() - started) / 180), eased = 1 - (1 - t) ** 3;
      this.walk.yaw = start + delta * eased;
      this.walk.pitch = startPitch + pitchDelta * eased;
      this.applyWalk(); this.requestFrame(true);
      if (t < 1) this.roomNavigationFrame = requestAnimationFrame(step); else this.saveCameraView();
    };
    step();
    return true;
  }

  /** Where a point on the floor lands on screen, or null when it is behind the camera. */
  screenPointAt(point: Vec2, height = 0, bounds?: DOMRect): ScreenPoint | null {
    const rect = bounds ?? this.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const projected = new THREE.Vector3(point.x, height, point.z).project(this.viewCamera);
    if (!(projected.z > -1 && projected.z < 1)) return null;
    return { x: rect.left + (projected.x + 1) * rect.width / 2, y: rect.top + (1 - projected.y) * rect.height / 2 };
  }

  /**
   * Keep a point in sight after an edit the user did not aim with the camera: an orbit pans
   * until the point sits in the middle of the free area, a walk turns to face it — moving
   * the walker is a navigation decision, not a side effect of adding an item.
   */
  revealPoint(point: Vec2, area: ScreenRect, height = 0): boolean {
    if (this.disposed || !this.active) return false;
    if (withinArea(this.screenPointAt(point, height), area, REVEAL_INSET)) return false;
    if (this.wallPlan) {
      // The wall plan slides along the wall until the point sits in the free part of the stage — as far as a
      // panel's cover lets it; the scale never changes for a reveal.
      const at = this.screenPointAt(point, height), centre = areaCentre(area), frame = this.wallPlanFrame();
      if (!at || !frame) return false;
      const next = panWallPlanBy(frame, centre.x - at.x, 0);
      if (Math.abs(next.x - this.wallPlan.pan.x) < 1e-9 && Math.abs(next.y - this.wallPlan.pan.y) < 1e-9) return false;
      this.slideWallPlan(next);
      return true;
    }
    if (this.mode === 'walk') {
      const dx = point.x - this.walk.pos.x, dz = point.z - this.walk.pos.z;
      const distance = Math.hypot(dx, dz);
      return distance < 1e-3 ? false : this.faceWalkTowards(Math.atan2(dx, dz), Math.atan2(height - this.walk.pos.y, distance));
    }
    const centre = areaCentre(area), anchor = this.floorPointAt(centre.x, centre.y);
    if (!anchor) return false;
    return this.panOrbit(point.x - anchor.x, point.z - anchor.z);
  }

  /** Milliseconds to draw one frame, measured directly — independent of rAF throttling. */
  measureFrameMs(samples = 12): number {
    this.still?.reset();
    this.drawFrame();
    const t0 = performance.now();
    for (let i = 0; i < samples; i++) this.drawFrame();
    return (performance.now() - t0) / samples;
  }

  stats(): ViewerStats {
    const info = this.renderer.info;
    const key = `${this.index?.resourceVersion ?? -1}:${this.mode}:${this.lowWalls}:${this.cutaway}:${this.cutawayVersion ?? 0}`;
    let inventory = this.inventoryCache;
    if (!inventory || inventory.key !== key || !this.index) {
      const materials = new Set<THREE.Material>(), textures = new Set<THREE.Texture>();
      let meshes = 0, triangles = 0;
      this.built?.root.traverse(object => {
        const mesh = object as THREE.Mesh; if (!mesh.isMesh) return;
        meshes++; triangles += (mesh.geometry.index?.count ?? mesh.geometry.getAttribute('position')?.count ?? 0) / 3;
        for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
          materials.add(material);
          for (const value of Object.values(material)) if (value instanceof THREE.Texture) textures.add(value);
        }
      });
      inventory = this.inventoryCache = { key, meshes, triangles, textures: textures.size, materials: materials.size };
    }
    const resources = (this.models as ModelLibrary & { resourceStats?(): Record<string, unknown> })?.resourceStats?.() ?? {};
    const numericResources = Object.fromEntries(Object.entries(resources).filter((entry): entry is [string, number] => typeof entry[1] === 'number'));
    const frame = this.frameMetrics;
    return {
      ...(this.built?.stats ?? { walls: 0, panels: 0, openings: 0, items: 0, lights: 0, triangles: 0, textures: 0 }),
      ...numericResources, ...this.cohorts?.stats(), triangles:inventory.triangles, textures:inventory.textures, meshes:inventory.meshes, materials:inventory.materials,
      fps: Math.round(this.fps), drawCalls: frame?.drawCalls ?? info.render.calls, programs: info.programs?.length ?? 0,
      realModels: this.hydration.progress().itemsLoaded, renderFrames: this.renderFrames,
      planRenderFrames: this.topLayer?.renderFrames ?? 0, aoFrames: this.contactShadows?.frames ?? 0,
      shadowFrames: this.shadowFrames ?? 0, cpuSubmitMs: frame?.cpuSubmitMs ?? 0, frameIntervalMs: frame?.frameIntervalMs ?? 0,
      submittedTriangles: frame?.submittedTriangles ?? info.render.triangles ?? 0,
      colorCalls: frame?.colorCalls ?? 0, shadowCalls: frame?.shadowCalls ?? 0, aoCalls: frame?.aoCalls ?? 0,
      colorTriangles:frame?.colorTriangles??0,shadowTriangles:frame?.shadowTriangles??0,aoTriangles:frame?.aoTriangles??0,
      compositeCalls:frame?.compositeCalls??0,compositeTriangles:frame?.compositeTriangles??0,
    };
  }

  /** Put the camera in front of one object, for inspection. */
  focus(name: string): { found: boolean; size?: number[]; centre?: number[] } {
    const target = this.built?.root.getObjectByName(name);
    if (!target) return { found: false };
    const box = new THREE.Box3().setFromObject(target);
    const centre = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    this.mode = 'orbit';
    this.controls.enabled = true;
    const dir = new THREE.Vector3(0.5, 0.4, 0.75).normalize();
    this.camera.position.copy(centre).addScaledVector(dir, this.fitDistance(box, dir.clone().negate(), 1.3));
    this.controls.target.copy(centre);
    this.controls.update();
    return { found: true, size: size.toArray().map((v) => +v.toFixed(3)), centre: centre.toArray().map((v) => +v.toFixed(3)) };
  }

  /** Every built item, with its world bounds — for checking geometry against the plan. */
  inventory(): Array<{ name: string; caption?: string; real: boolean; size: number[]; parts?: number }> {
    const group = this.built?.root.getObjectByName('items');
    if (!group) return [];
    return group.children
      .filter((o) => o.userData?.kind === 'item')
      .map((o) => {
        const b = new THREE.Box3().setFromObject(o).getSize(new THREE.Vector3());
        return {
          name: o.name,
          caption: o.userData.caption as string | undefined,
          real: !!o.userData.real,
          size: b.toArray().map((v) => +v.toFixed(3)),
          parts: o.userData.parts as number | undefined,
        };
      });
  }

  /** Re-render at the current size — call after the canvas becomes visible again. */
  resize(): void { this.onResize(); }

  /** How far the still view has been refined: samples averaged so far, and the cap. */
  refinement(): { samples: number; max: number; enabled: boolean; supported: boolean } {
    return { samples: this.still?.samples ?? 0, max: this.still?.maxSamples ?? 0, enabled: this.still?.enabled ?? false, supported: this.still?.supported() ?? false };
  }
  setRefinement(enabled: boolean): void { this.still?.setEnabled(enabled); this.requestFrame(); }   // off: a plain frame replaces the average on screen

  setActive(active: boolean): void {
    if (!active) { this.cancelInteraction(); this.cancelTouchNavigation(); this.settleAppearances(); this.pendingAppearances?.clear(); }
    this.active = active;
    this.outdoor?.setVisible(this.outdoorsShown());
    if (active) this.requestFrame(); else { this.scheduler?.stop(); this.still?.reset(); }
    if (active && this.lowerFloorsStale) this.syncLowerFloors();
    this.syncFloorAbove();
    this.followHighlight();
    if (active && !this.hasShownGeometry && this.built && !this.sceneBounds().isEmpty()) {
      // Several walls can be drawn while 3D is hidden. Fit their current bounds only
      // when the project is first revealed; later reveals preserve the user's view.
      if (this.mode !== 'walk') this.frameAll();
      this.hasShownGeometry = true;
    }
    this.controls.enabled = this.controlsAllowed();
    if (active) return;
    this.walk.keys.clear();
    this.walk.dragging = false;
    this.lastFrame = 0;
  }

  setExposure(value: number): void {
    this.renderer.toneMappingExposure = value; this.requestFrame();
  }

  setWallMode(mode: WallMode): void {
    const lowWalls = mode === 'low', cutaway = mode === 'cutaway';
    if (lowWalls === this.lowWalls && cutaway === this.cutaway) return;
    this.settleAppearances();
    this.lowWalls = lowWalls; this.cutaway = cutaway;
    this.applyLowWalls(true); this.requestFrame(true);
  }

  private cutawayActive(): boolean { return !!this.cutaway && !this.lowWalls && this.mode === 'orbit'; }

  /** Whether this wall is meant to stand at waist height, whatever it is doing right now. */
  private wallCut(wallId: string): boolean {
    return this.mode !== 'walk' && (!!this.lowWalls || (this.cutawayActive() && !!this.cutWalls?.has(wallId)));
  }

  private visibilityMode(now = performance.now()): SceneVisibilityMode {
    return { walk: this.mode === 'walk', lowWalls: !!this.lowWalls, cutWalls: this.cutawayActive() ? this.cutWalls : undefined,
      cutHeight: (wallId, subject) => this.cutHeightAt(wallId, now, subject) };
  }

  /** A wall passes through every height of its slide; what hangs on it goes straight to the destination. */
  private cutHeightAt(wallId: string, now: number, subject: 'wall' | 'attachment' = 'wall'): number | null {
    const slide = subject === 'wall' ? this.wallSlides?.get(wallId) : undefined;
    if (slide) { const height = slideHeight(slide, now); return height >= slide.full - 1e-6 ? null : height; }
    return this.wallCut(wallId) ? LOW_WALL_HEIGHT : null;
  }

  /**
   * The camera as the cutaway sees it: where it stands on the plan, half a metre behind
   * itself along its view so a camera looking straight down still has a "front", and the
   * way it looks — for a camera pointed at the floor, the way the top of the screen faces.
   */
  private cutawayCamera(): CutawayCamera {
    const forward = this.camera.getWorldDirection(this.eyeForward ??= new THREE.Vector3());
    let fx = forward.x, fz = forward.z;
    if (Math.hypot(fx, fz) < .1) { const up = this.eyeForward.set(0, 1, 0).applyQuaternion(this.camera.quaternion); fx = up.x; fz = up.z; }
    const length = Math.hypot(fx, fz) || 1;
    fx /= length; fz /= length;
    return { x: this.camera.position.x - fx * .5, z: this.camera.position.z - fz * .5, forward: { x: fx, z: fz } };
  }

  /** Re-judge the cutaway for the camera as it stands; the walls that changed, or null. */
  private refreshCutaway(): ReadonlySet<string> | null {
    this.cutWalls ??= new Set(); this.cutScratch ??= new Set(); this.cutDelta ??= new Set();
    const level = this.level, next = this.cutScratch;
    next.clear();
    if (level && this.cutawayActive() && this.camera) cutawayWalls(level, this.cutawayCamera(), this.cutWalls, next);
    const delta = this.cutDelta;
    delta.clear();
    for (const id of next) if (!this.cutWalls.has(id)) delta.add(id);
    for (const id of this.cutWalls) if (!next.has(id)) delta.add(id);
    if (!delta.size) return null;
    this.cutScratch = this.cutWalls; this.cutWalls = next;
    return delta;
  }

  /**
   * Point every wall (or just `only`) at its height for the current mode and camera. A
   * change slides there over WALL_SLIDE_MS when `animate` allows it and the visitor has
   * not asked for reduced motion; otherwise it lands at once. Returns the walls whose
   * geometry has to be applied now.
   */
  private syncWallSlides(now: number, animate: boolean, only?: ReadonlySet<string> | null): Set<string> {
    this.wallTargets ??= new Map(); this.wallSlides ??= new Map();
    const changed = new Set<string>();
    const level = this.level;
    if (!level) return changed;
    const smooth = animate && !reducedMotion();
    for (const wall of level.walls) {
      if (only && !only.has(wall.id)) continue;
      const slide = this.wallSlides.get(wall.id), settled = this.wallTargets.get(wall.id);
      const target = this.wallCut(wall.id) ? LOW_WALL_HEIGHT : wall.height;
      if (settled === target && (smooth || !slide)) continue;
      const from = slide ? slideHeight(slide, now) : settled ?? wall.height;
      this.wallTargets.set(wall.id, target);
      if (smooth && Math.abs(from - target) > 1e-6) this.wallSlides.set(wall.id, { from, to: target, at: now, full: wall.height });
      else this.wallSlides.delete(wall.id);
      changed.add(wall.id);
    }
    return changed;
  }

  /**
   * Every frame: the camera may have moved the cutaway, and sliding walls take their next
   * step. Doors, windows and fixtures change only with a wall's destination, and the shadow
   * map is redrawn when a wall sets off and when it lands, not for every step between.
   */
  private updateWallCuts(now: number): boolean {
    if (!this.built || !this.level) return false;
    const delta = this.refreshCutaway();
    if (!delta && !this.wallSlides?.size) return false;
    const changed = delta ? this.syncWallSlides(now, true, delta) : new Set<string>();
    const walls = new Set(changed);
    let landed = false;
    for (const [id, slide] of this.wallSlides ?? []) {
      if (now - slide.at >= WALL_SLIDE_MS) { this.wallSlides.delete(id); landed = true; }
      walls.add(id);
    }
    if (walls.size) this.applyWallChanges({ walls, attachments: changed }, now, changed.size > 0 || landed);
    return (this.wallSlides?.size ?? 0) > 0;
  }

  /** Re-cut these walls, and the openings and fixtures of those whose destination changed. */
  private applyWallChanges(scope: VisibilityScope, now: number, shadows: boolean): void {
    const root = this.built?.root, level = this.level;
    if (!root || !level) return;
    applyModeVisibility(root, level, this.visibilityMode(now), name => this.nodeFor(name), scope);
    for (const id of scope.walls) { const node = this.nodeFor(`wall:${id}`); if (node) this.index?.update(node.name); }
    if (scope.attachments.size) {
      for (const opening of level.openings) {
        if (!scope.attachments.has(opening.wallId)) continue;
        const node = this.nodeFor(`opening:${opening.id}`);
        if (node) this.index?.update(node.name);
      }
      for (const item of level.items) {
        const wallId = attachedWallId(item, level);
        if (!wallId || !scope.attachments.has(wallId)) continue;
        const node = this.nodeFor(`item:${item.id}`);
        if (node) { this.index?.update(node.name); this.syncCohort(node, item); }
      }
    }
    this.applyWallPlanVisibility();
    this.cutawayVersion = (this.cutawayVersion ?? 0) + 1;
    if (shadows && this.renderer?.shadowMap) this.renderer.shadowMap.needsUpdate = true;
    this.still?.reset();
  }

  /**
   * The view out of the windows is drawn in the orbit and the walk, round the house and through its windows, and not
   * in the straight-down view or the wall plan; its pictures are asked for only once 3D is on screen. The walk lights
   * the rooms a little more, as it always has.
   */
  private setStreetViews(): void {
    this.outdoor?.setVisible(this.outdoorsShown());
    const walking = this.mode === 'walk';
    this.scene.environmentIntensity = walking ? 1.15 : .85;
    this.syncFloorAbove();
    const root = this.built?.root;
    if (!root) return;
    for (const child of root.children) if (child.userData?.kind === 'view') child.visible = walking;
    this.applyLowWalls(); this.requestFrame(true);
  }
  private outdoorsShown(): boolean { return this.active && this.mode !== 'top' && !this.wallPlan; }

  /**
   * Every wall, opening and fixture to the current mode; `animate` lets the walls slide
   * there. Only the nodes the pass actually changed — a different geometry or visibility —
   * re-enter the instanced cohorts and the spatial index: rebuilding every cohort for a
   * mode switch was most of what made the switch stutter.
   */
  private applyLowWalls(animate = false): void {
    const root = this.built?.root;
    const level = this.level;
    if (!root || !level) return;
    if (!this.reconciling) this.appearance?.restore();
    const now = performance.now();
    this.refreshCutaway();
    this.syncWallSlides(now, animate);
    const entries = [...(this.index?.values() ?? [])];
    const state = (entry: { node: THREE.Object3D; meshes: readonly THREE.Mesh[] }) =>
      `${entry.node.visible ? 1 : 0}:${entry.meshes.map(mesh => mesh.geometry.id).join(',')}`;
    const before = entries.map(state);
    applyModeVisibility(root, level, this.visibilityMode(now), name => this.nodeFor(name));
    entries.forEach((entry, i) => {
      if (state(entry) === before[i]) return;
      this.index!.update(entry.node.name); this.syncCohort(entry.node);
    });
    this.applyWallPlanVisibility();
    this.topLayer?.invalidate();
    this.cutawayVersion = (this.cutawayVersion ?? 0) + 1;
    if (!this.reconciling) this.syncItemAppearances(performance.now());
  }

  // ---- the wall plan (the app's WallSide, Lite's wallView) ---------------------------------

  /** The camera the picture is drawn and picked through: the orthographic one in the wall plan, else the 3D camera. */
  private get viewCamera(): THREE.Camera { return this.wallPlan ? this.wallCamera : this.camera; }
  private controlsAllowed(): boolean { return this.active && this.mode !== 'walk' && !this.wallPlan; }
  private wallPlanWall(): Wall | undefined {
    const plan = this.wallPlan;
    return plan ? this.level?.walls.find(wall => wall.id === plan.target.wallId) : undefined;
  }

  /**
   * Show one wall face-on, or leave the wall plan with null. The 3D camera is never touched,
   * so the view returns to where it was; a different wall opens at zoom 1 with no pan.
   */
  setWallPlan(target: WallViewTarget | null): void {
    const current = this.wallPlan?.target;
    if (!target ? !current : !!current && current.wallId === target.wallId && current.side === target.side) return;
    this.settleAppearances(); this.cancelInteraction(); this.cancelTouchNavigation();
    this.wallPlan = target ? { target, zoom: 1, pan: { x: 0, y: 0 }, distance: 0 } : null;
    this.wallPinch = null;
    if (this.controls) this.controls.enabled = this.controlsAllowed();
    // A flat drawing on the design's canvas, lit head-on by the sun turned to face the wall.
    this.aimSun();
    if (this.scene?.background instanceof THREE.Color) this.scene.background.set(this.wallPlan ? WALL_PLAN_BACKGROUND : SCENE_BACKGROUND);
    this.applyLowerFloorsVisibility();
    this.applyLowWalls();
    if (this.isolatedPolygon) for (const node of [...(this.contents?.children ?? [])]) if (node.userData?.kind === 'item') this.applyNodeVisibility(node, this.itemsById?.get(node.userData.itemId));
    this.applyWallPlanCamera();
    this.overlaySignature = ''; this.hoverKind = 'none';
    this.requestFrame(true);
  }

  /** The plan's state for checks: which face, how far the camera stands, the zoom and the pan. */
  wallPlanState(): { target: WallViewTarget; zoom: number; pan: { x: number; y: number }; distance: number } | null {
    const plan = this.wallPlan;
    return plan ? { target: { ...plan.target }, zoom: plan.zoom, pan: { ...plan.pan }, distance: plan.distance } : null;
  }

  /** The face-to-canvas mapping the overlay draws its dimension rows with; null outside the wall plan. */
  wallPlanFrame(): WallPlanFrame | null {
    const plan = this.wallPlan, wall = this.wallPlanWall(), level = this.level;
    if (!plan || !wall || !level) return null;
    const width = this.canvas.clientWidth || 1;
    return { wall, side: plan.target.side, height: wallPlanHeight(level, wall), width, viewHeight: this.canvas.clientHeight || 1,
      zoom: plan.zoom, pan: { ...plan.pan }, sign: wallDirectionSign(wall, plan.target.side), inset: this.wallPlanInsets(width) };
  }

  /**
   * What the overlay draws over the wall plan besides the dimensions: the selected item's rectangle
   * (red while a move drives it into another), those others, and the magnets' guides mid-drag.
   * The 3D outline cannot show there — the face is seen head-on and the wall hides the shell.
   */
  wallPlanMarks(): WallPlanMarks | null {
    const plan = this.wallPlan, wall = this.wallPlanWall(), level = this.level;
    if (!plan || !wall || !level) return null;
    const session = this.pointer, moving = !!session?.intent.active && session.intent.kind !== 'camera';
    const marks: WallPlanMarks = { selected: null, conflicts: [], guides: moving ? session?.wallGuides ?? [] : [] };
    const item = this.highlighted ? level.items.find(candidate => candidate.id === this.highlighted) : undefined;
    if (!item || !this.nodeFor(`item:${item.id}`)?.visible) return marks;
    const footprints = this.overlapFootprints(level);
    const conflicts = this.placing(item.id) ? obstructions(level.items, item, footprints) : [];
    marks.selected = { ...itemWallPlanBox(item, wall, footprints), conflict: conflicts.length > 0 };
    for (const id of conflicts) {
      const other = level.items.find(candidate => candidate.id === id);
      if (other && this.nodeFor(`item:${id}`)?.visible) marks.conflicts.push(itemWallPlanBox(other, wall, footprints));
    }
    return marks;
  }

  /** The editor tells the plan how much canvas its side chrome takes — the rails, the inspector, the chat column. */
  /** Returns whether the frame moved on screen now, so a pointer anchor beside the old picture can be dropped. */
  setWallPlanInsets(insets: WallPlanInsets | null): boolean {
    if (JSON.stringify(insets) === JSON.stringify(this.wallInsets)) return false;
    const previous = this.wallInsets;
    this.wallInsets = insets;
    // A change that arrives mid-gesture waits for the release: the frame must not move under a drag.
    if (this.pointer?.intent.active) { this.wallInsetsPending = true; return false; }
    // Only a panel's cover changed: the scale stays, and the frame slides back into its range if it has to.
    if (previous && insets && previous.left === insets.left && previous.right === insets.right) return this.settleWallPan();
    this.applyWallPlanCamera(); this.overlaySignature = ''; this.requestFrame();
    return !!this.wallPlan;
  }

  /** Slide the frame into the pan range the current insets allow; true when it moves. */
  private settleWallPan(): boolean {
    const plan = this.wallPlan, frame = this.wallPlanFrame();
    if (!plan || !frame) return false;
    const next = clampWallPan(plan.pan, plan.zoom, frame.wall.length, frame.height, frame.width, frame.viewHeight, frame.inset);
    if (Math.abs(next.x - plan.pan.x) < 1e-9 && Math.abs(next.y - plan.pan.y) < 1e-9) return false;
    this.slideWallPlan(next);
    return true;
  }

  private wallSlideFrame = 0;
  /** While a slide runs, the frame passes through pans the new range may not hold; its end is always in range. */
  private wallSliding = false;
  /** Move the frame to `to` over 180 ms (at once under reduced motion), as the plan's own reveal moves. */
  private slideWallPlan(to: { x: number; y: number }): void {
    const plan = this.wallPlan;
    if (!plan) return;
    if (this.wallSlideFrame) cancelAnimationFrame(this.wallSlideFrame);
    this.wallSlideFrame = 0; this.wallSliding = false;
    const from = { ...plan.pan };
    const finish = () => { plan.pan = { ...to }; this.wallSliding = false; this.applyWallPlanCamera(); this.overlaySignature = ''; this.requestFrame(true); };
    if (reducedMotion() || typeof requestAnimationFrame !== 'function') { finish(); return; }
    const start = performance.now(), duration = 180;
    const step = () => {
      this.wallSlideFrame = 0;
      if (this.wallPlan !== plan || this.disposed) { this.wallSliding = false; return; }
      const t = Math.min(1, (performance.now() - start) / duration), eased = 1 - (1 - t) ** 3;
      if (t >= 1) { finish(); return; }
      plan.pan = { x: from.x + (to.x - from.x) * eased, y: from.y + (to.y - from.y) * eased };
      this.wallSliding = true; this.applyWallPlanCamera(); this.overlaySignature = ''; this.requestFrame(true);
      this.wallSlideFrame = requestAnimationFrame(step);
    };
    step();
  }
  private wallInsetsPending = false;
  private wallPlanInsets(width: number): WallPlanInsets {
    const fallback = wallPlanInset(width);
    return this.wallInsets ?? { left: fallback, right: fallback };
  }

  /**
   * The sun lights the house from its usual corner; in the wall plan it turns to face the shown wall
   * from a little above the camera. The same light with the same shadow flag either way: adding a
   * light or switching a shadow off changes every material's shader, and that recompile was the
   * stall on entering the plan.
   */
  private aimSun(): void {
    const sun = this.sun;
    if (!sun) return;
    const plan = this.wallPlan, wall = this.wallPlanWall(), level = this.level;
    if (plan && wall && level) {
      const n = faceNormal(wall, plan.target.side), height = wallPlanHeight(level, wall), reach = wall.thickness / 2 + 1;
      sun.intensity = WALL_PLAN_LIGHT;
      sun.position.set(wall.center.x + n.x * 12, height / 2 + 1, wall.center.z + n.z * 12);
      sun.target.position.set(wall.center.x, height / 2, wall.center.z);
      // The shadow box holds the wall and what stands against it; the shadows fall behind the things that cast them.
      const box = new THREE.Box3().makeEmpty();
      for (const end of [wall.start, wall.end]) for (const side of [-1, 1]) for (const y of [0, height])
        box.expandByPoint(new THREE.Vector3(end.x + n.x * reach * side, y, end.z + n.z * reach * side));
      fitSunShadow(sun, box);
    } else {
      sun.intensity = SUN_INTENSITY;
      // A hidden 3D view aims the sun once it has built the floors below (`syncLowerFloors`).
      const bounds = this.built ? this.sceneBounds(this.active) : undefined;
      if (!bounds || bounds.isEmpty()) return;
      const size = bounds.getSize(new THREE.Vector3()), centre = bounds.getCenter(new THREE.Vector3());
      const span = Math.max(size.x, size.z, 1), toward = this.sunDirection(), reach = span * 1.6 + 8;
      // Along the view's own sun, so shadows fall the way the sky says; else from the house's usual corner.
      if (toward) sun.position.set(centre.x + toward[0] * reach, centre.y + toward[1] * reach, centre.z + toward[2] * reach);
      else sun.position.set(centre.x + span * 0.7, span * 1.3 + 6, centre.z - span * 0.6);
      sun.target.position.copy(centre);
      // With a view out of the windows the house throws its shadow on the ground round it: the shadow box reaches
      // where the shadows of its top corners land.
      const shadowBox = bounds.clone();
      if (toward && toward[1] > .05) {
        const groundY = -levelAltitude(this.hp?.levels ?? [], this.levelIndex ?? 0);
        const drop = Math.min(Math.max(0, bounds.max.y - groundY) / toward[1], 40);
        for (const x of [bounds.min.x, bounds.max.x]) for (const z of [bounds.min.z, bounds.max.z])
          shadowBox.expandByPoint(new THREE.Vector3(x - toward[0] * drop, groundY, z - toward[2] * drop));
      }
      fitSunShadow(sun, shadowBox);
    }
    if (this.renderer?.shadowMap) this.renderer.shadowMap.needsUpdate = true;
  }

  private stopWallSlide(): void {
    if (this.wallSlideFrame) cancelAnimationFrame(this.wallSlideFrame);
    this.wallSlideFrame = 0; this.wallSliding = false;
  }

  /** «Вписать»: the whole wall with its margins, as the plan opened. */
  resetWallPlanView(): void {
    if (!this.wallPlan) return;
    this.stopWallSlide();
    this.interactionHandlers?.onNavigationIntent?.();
    this.wallPlan.zoom = 1; this.wallPlan.pan = { x: 0, y: 0 };
    this.applyWallPlanCamera(); this.overlaySignature = ''; this.requestFrame();
  }

  /**
   * ⌘±, ⇧0 and the zoom menu: `factor` over 1 comes closer. The wall plan zooms about its middle, «reset» to 100 px a
   * metre; the orbit moves along its line of sight, «reset» to the «Zoom to fit» distance, keeping the target.
   */
  zoomView(factor: number | 'reset'): void {
    if (this.wallPlan) {
      const frame = this.wallPlanFrame();
      if (!frame) return;
      this.interactionHandlers?.onNavigationIntent?.();
      this.zoomWallPlanBy(factor === 'reset' ? 100 / pxPerMetre(frame) : factor, wallPlanCentreX(frame), frame.viewHeight / 2);
      return;
    }
    this.interactionHandlers?.onNavigationIntent?.();
    const fit = this.orbitFitDistance();
    if (fit === null) return;
    const offset = this.camera.position.clone().sub(this.controls.target), distance = offset.length();
    // Clamped only ahead: a camera the wheel took past a limit is never pushed back the other way.
    const next = factor === 'reset' ? fit : factor > 1 ? Math.max(distance / factor, Math.min(ORBIT_ZOOM_NEAR, distance)) : Math.min(distance / factor, Math.max(fit * ORBIT_ZOOM_FAR, distance));
    this.camera.position.copy(this.controls.target).addScaledVector(offset.normalize(), next);
    this.controls.update(); this.requestFrame();
  }

  /** The zoom menu's percentage: on the wall plan px a metre, in the orbit the «Zoom to fit» distance over the camera's; null in the walk. */
  zoomPercent(): number | null {
    if (this.wallPlan) { const frame = this.wallPlanFrame(); return frame ? Math.round(pxPerMetre(frame)) : null; }
    const fit = this.orbitFitDistance(), distance = this.camera.position.distanceTo(this.controls.target);
    return fit === null || distance <= 0 ? null : Math.round(fit / distance * 100);
  }

  /** «Вписать»'s distance, from its fixed direction: turning the orbit leaves it, and the percentage, as they are. Measured again only when the floor's box or the camera's lens changes. */
  private orbitFitDistance(): number | null {
    if (this.mode !== 'orbit' || !this.built) return null;
    const box = this.sceneBounds(false);
    if (box.isEmpty()) return null;
    const { min, max } = box, { aspect, fov } = this.camera, fit = this.orbitFit ??= { box: [NaN, NaN, NaN, NaN, NaN, NaN], aspect: NaN, fov: NaN, distance: 0 }, b = fit.box;
    if (b[0] !== min.x || b[1] !== min.y || b[2] !== min.z || b[3] !== max.x || b[4] !== max.y || b[5] !== max.z || fit.aspect !== aspect || fit.fov !== fov) {
      b[0] = min.x; b[1] = min.y; b[2] = min.z; b[3] = max.x; b[4] = max.y; b[5] = max.z; fit.aspect = aspect; fit.fov = fov;
      fit.distance = this.fitDistance(box, FIT_VIEW, 1.04);
    }
    return fit.distance;
  }

  /**
   * Lite's applyCamera for the wall view: an orthographic camera inside the room, `distance` in
   * front of the face at half the wall's height, framing the wall plus 60 cm each side at
   * zoom 1; the pan shifts it along the wall and up. The grid stands 20 m behind the wall.
   */
  private applyWallPlanCamera(): void {
    const plan = this.wallPlan, wall = this.wallPlanWall(), level = this.level;
    if (!plan || !wall || !level) return;
    const width = this.canvas.clientWidth || 1, viewHeight = this.canvas.clientHeight || 1;
    const height = wallPlanHeight(level, wall), n = faceNormal(wall, plan.target.side), sign = wallDirectionSign(wall, plan.target.side), inset = this.wallPlanInsets(width);
    if (!this.wallSliding) plan.pan = clampWallPan(plan.pan, plan.zoom, wall.length, height, width, viewHeight, inset);
    plan.distance = wallPlanCameraDistance(level, wall, plan.target.side);
    // The wall's centre sits in the middle of the free part of the canvas; the camera's own centre is the canvas middle.
    const ppm = pxPerMetre({ wall, width, zoom: plan.zoom, inset }), visibleWidth = width / ppm, visibleHeight = viewHeight / ppm;
    const shift = sign * (plan.pan.x + (width / 2 - wallPlanCentreX({ width, inset })) / ppm), eyeY = height / 2 + plan.pan.y;
    const eye = { x: wall.center.x + n.x * plan.distance + wall.dir.x * shift, z: wall.center.z + n.z * plan.distance + wall.dir.z * shift };
    const camera = this.wallCamera ??= new THREE.OrthographicCamera(-1, 1, 1, -1, .05, 60);
    camera.left = -visibleWidth / 2; camera.right = visibleWidth / 2; camera.top = visibleHeight / 2; camera.bottom = -visibleHeight / 2;
    camera.near = .05; camera.far = 60;
    camera.up.set(0, 1, 0);
    camera.position.set(eye.x, eyeY, eye.z);
    camera.lookAt(wall.center.x + wall.dir.x * shift, eyeY, wall.center.z + wall.dir.z * shift);
    camera.updateProjectionMatrix(); camera.updateMatrixWorld();
    if (this.scene) {
      const grid = this.wallGrid ??= makeWallGrid();
      if (!grid.parent) this.scene.add(grid);
      grid.visible = true;
      grid.position.set(wall.start.x - n.x * 20, 0, wall.start.z - n.z * 20);
      grid.lookAt(grid.position.x + n.x, 0, grid.position.z + n.z);
    }
    if (this.controls) this.controls.enabled = this.controlsAllowed();
  }

  /**
   * Lite's applyWallPlan / restoreFromWallPlan: in the plan only the chosen wall, its openings
   * and the items standing against the face are drawn; floors, ceilings, the other walls, the
   * outdoors and ceiling items are hidden. Runs at the end of every visibility pass — the pass
   * restores walls, openings and items itself, this restores what only it hides.
   */
  private applyWallPlanVisibility(): void {
    const root = this.built?.root, level = this.level;
    if (!root || !level) return;
    const plan = this.wallPlan, wall = this.wallPlanWall();
    if (!plan || !wall) {
      if (this.wallGrid) this.wallGrid.visible = false;
      if (!this.wallPlanMasked) return;
      this.wallPlanMasked = false;
      for (const room of level.rooms) for (const name of [`floor:${room.index}`, `ceiling:${room.index}`]) { const node = this.nodeFor(name); if (node) node.visible = true; }
      const views = this.mode === 'walk';
      for (const child of root.children) if (child.userData?.kind === 'view') child.visible = views;
      this.outdoor?.setVisible(this.outdoorsShown());
      return;
    }
    this.wallPlanMasked = true;
    const onWall = new Set(level.openings.filter(opening => opening.wallId === wall.id).map(opening => opening.id));
    for (const other of level.walls) { const node = this.nodeFor(`wall:${other.id}`); if (node) node.visible = other.id === wall.id && !other.hidden; }
    for (const room of level.rooms) for (const name of [`floor:${room.index}`, `ceiling:${room.index}`]) { const node = this.nodeFor(name); if (node) node.visible = false; }
    for (const opening of level.openings) { const node = this.nodeFor(`opening:${opening.id}`); if (node) node.visible = onWall.has(opening.id); }
    for (const child of root.children) if (child.userData?.kind === 'view') child.visible = false;
    this.outdoor?.setVisible(false);
    for (const item of level.items) {
      const node = this.nodeFor(`item:${item.id}`);
      if (!node) continue;
      const visible = node.visible && onWallPlan(item, level, wall, plan.target.side);
      if (visible === node.visible) continue;
      node.visible = visible; this.syncCohort(node, item);
    }
  }

  /** Where the pointer's ray meets the face plane: metres along the wall and up from the floor. */
  private wallPlanHit(clientX: number, clientY: number): { along: number; up: number } | null {
    const plan = this.wallPlan, wall = this.wallPlanWall();
    if (!plan || !wall) return null;
    const n = faceNormal(wall, plan.target.side), ray = this.rayAt(clientX, clientY), half = wall.thickness / 2;
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(new THREE.Vector3(n.x, 0, n.z), new THREE.Vector3(wall.start.x + n.x * half, 0, wall.start.z + n.z * half));
    const hit = ray.intersectPlane(plane, new THREE.Vector3());
    return hit ? { along: alongWall(wall, { x: hit.x, z: hit.z }), up: hit.y } : null;
  }

  /** A press in the wall plan: on the selected item it begins a move along the face; anywhere else it pans the frame. */
  private wallPlanPointerDown(e: PointerEvent): void {
    if (e.button !== 0) return;
    const base = this.level ?? null, wall = this.wallPlanWall(), space = spaceHeld();
    let item = !space && this.editingEnabled && !this.resizeMode ? this.selectedItemAt(e.clientX, e.clientY) : null;
    const opening = !space && !item && this.editingEnabled && !this.resizeMode && wall ? this.selectedOpeningAt(e.clientX, e.clientY, wall) : null;
    const hit = (item || opening) && wall ? this.wallPlanHit(e.clientX, e.clientY) : null;
    if ((item || opening) && hit) {
      this.interactionHandlers.onEditPrepare?.();
      if (item) item = this.level?.items.find(value => value.id === item!.id) ?? null;
      this.settleAppearances();
    }
    const movable = !!(item && hit && wall && base), openingMove = !!(opening && hit && wall && base);
    this.pointer = { intent: new ScenePointerIntent(e.pointerId, e.clientX, e.clientY, movable || openingMove ? 'move' : 'camera'),
      previous: { x: e.clientX, y: e.clientY }, base: movable || openingMove ? base : null, item: movable ? item : null,
      planeY: 0, grab: { x: 0, z: 0 }, reach: 60, rotationOffset: 0, footprints: base && (movable || openingMove) ? this.gestureFootprints(base) : undefined,
      wallGrab: movable && item && hit && wall ? { along: hit.along - alongWall(wall, item.position), y: hit.up - item.elevation } : undefined,
      opening: openingMove && opening && hit ? { id: opening.id, grab: { along: hit.along - opening.offset, y: hit.up - opening.sill } } : undefined };
    e.stopImmediatePropagation(); e.preventDefault();
    this.capturePointer(e.pointerId);
  }

  /**
   * Lite's moveInWallPlan: along the wall for any item, up and down only for wall-mounted ones;
   * the distance from the wall and the rotation never change. The magnets (`state/wallPlanSnap.ts`)
   * line it up with what else stands on the face; a free move keeps whole centimetres, as on the plan.
   */
  private wallPlanTransform(session: PointerSession, item: Item, base: Level, e: PointerEvent): SceneItemTransform | null {
    const plan = this.wallPlan, wall = this.wallPlanWall(), hit = this.wallPlanHit(e.clientX, e.clientY);
    if (!plan || !wall || !hit) return null;
    const n = faceNormal(wall, plan.target.side), vertical = wallPlanVertical(item);
    // The footprint's reach either side of the item's point, so a measured model stays inside the wall's ends.
    const box = itemWallPlanBox(item, wall, session.footprints), origin = alongWall(wall, item.position);
    const low = origin - box.t0, high = box.t1 - origin, across = acrossWall(wall, plan.target.side, item.position);
    const bounds = wallPlanBounds(base, wall), top = Math.max(bounds.height - item.size.h, 0);
    const along = clamp(hit.along - (session.wallGrab?.along ?? 0), low, Math.max(wall.length - high, low));
    const elevation = vertical ? clamp(hit.up - (session.wallGrab?.y ?? 0), 0, top) : item.elevation;
    const travelling = new Set([item.id, ...stackedItems(base.items, item, this.surfaceProbe).map(other => other.id)]);
    const within = (value: number, lo: number, hi: number) => value >= lo - 1e-9 && value <= hi + 1e-9;
    const snap = snapWallPlanBox({ t0: along - low, t1: along + high, h0: box.h0 + elevation - item.elevation, h1: box.h1 + elevation - item.elevation },
      wallPlanBoxes(base, plan.target, travelling, session.footprints), bounds, {
        scale: this.wallPlanScale(), vertical, disabled: e.ctrlKey || e.metaKey, previous: session.wallCaptures,
        fits: (axis: WallPlanAxis, delta: number) => axis === 't' ? within(along + delta, low, Math.max(wall.length - high, low)) : within(elevation + delta, 0, top) });
    session.wallCaptures = snap.captures; session.wallGuides = snap.guides;
    const held = (axis: WallPlanAxis) => snap.captures.some(capture => capture.axis === axis);
    const t = held('t') ? along + snap.dt : Math.round(along * 100) / 100;
    const position = { x: wall.start.x + wall.dir.x * t + n.x * across, z: wall.start.z + wall.dir.z * t + n.z * across };
    return { position, rotation: item.rotation, elevation: !vertical ? item.elevation : held('h') ? elevation + snap.dh : clamp(Math.round(elevation * 100) / 100, 0, top) };
  }

  /** Metres per CSS pixel on the wall plan, for the magnets' reach. */
  private wallPlanScale(): number {
    const frame = this.wallPlanFrame();
    return frame ? 1 / pxPerMetre(frame) : .005;
  }

  /** The demo walkthrough on or off: while on, each doorway in view gets its enter button (`onDoorMarkers`). */
  setWalkthroughDoors(on: boolean): void {
    if (on === this.walkthroughDoors) return;
    this.walkthroughDoors = on; this.doorMarkersKey = ''; this.doorMarkersAt = 0;
    if (!on) this.interactionHandlers?.onDoorMarkers?.([]);
    this.requestFrame();
  }

  /**
   * Each doorway between two rooms in view, as the app's EnterRoomDoorMarkers shows it in its demo walkthrough: its
   * button stands before the door on the side the camera is on, at about the height of a hand, when nothing but the door
   * itself is in the way (`IsPositionVisible`, 35 cm short of the button); it leads into the room on the door's far side.
   */
  private updateDoorMarkers(now: number): void {
    if (!this.walkthroughDoors || now - this.doorMarkersAt < 80) return;
    this.doorMarkersAt = now;
    const level = this.level, markers: DoorMarker[] = [];
    if (level && this.built && this.active) {
      const tags = new Map(level.openings.map(opening => [opening.modelRef.id, this.models?.entry?.(opening.modelRef.id)?.tags ?? knownModelTags(opening.modelRef.id) ?? []]));
      const camera = this.viewCamera, eye = camera.position, rect = this.canvas.getBoundingClientRect();
      camera.updateMatrixWorld();
      for (const door of passableDoorways(level, tags)) {
        const { wall, centre, opening } = door;
        const side = Math.sign((eye.x - centre.x) * wall.normalIn.x + (eye.z - centre.z) * wall.normalIn.z) || 1;
        const reach = (wall.thickness / 2 + .3) * side;
        const point = new THREE.Vector3(centre.x + wall.normalIn.x * reach, Math.min(1.2, opening.height * .55), centre.z + wall.normalIn.z * reach);
        const ndc = point.clone().project(camera);
        if (ndc.z < -1 || ndc.z > 1 || Math.abs(ndc.x) > .96 || Math.abs(ndc.y) > .94) continue;
        const own = new Set([opening.id, ...openingCopies(level, opening.id)]);
        if (this.doorHidden(point, own)) continue;
        markers.push({ id: opening.id, x: rect.left + (ndc.x + 1) / 2 * rect.width, y: rect.top + (1 - ndc.y) / 2 * rect.height, roomIndex: (side > 0 ? door.back : door.front).index });
      }
    }
    const key = markers.map(marker => `${marker.id}:${Math.round(marker.x)}:${Math.round(marker.y)}`).join('|');
    if (key !== this.doorMarkersKey) { this.doorMarkersKey = key; this.interactionHandlers?.onDoorMarkers?.(markers); }
  }

  /** Something but the door itself between the camera and `point`, nearer by more than 35 cm. */
  private doorHidden(point: THREE.Vector3, own: ReadonlySet<string>): boolean {
    const eye = this.viewCamera.position, toPoint = point.clone().sub(eye), distance = toPoint.length();
    if (distance <= .35) return false;
    const ray = new THREE.Ray(eye.clone(), toPoint.divideScalar(distance));
    const caster = new THREE.Raycaster(ray.origin, ray.direction, 0, distance - .35);
    const shown = (object: THREE.Object3D) => { for (let node: THREE.Object3D | null = object; node; node = node.parent) if (!node.visible) return false; return true; };
    const candidates = (this.index ? this.index.candidates(ray) : this.built?.pickable ?? []).filter(shown);
    return caster.intersectObjects(candidates, false).some(hit => !own.has(hit.object.userData.openingId));
  }

  /** The store's selected plinth or ceiling molding: its strips are outlined as one object. */
  selectTrim(target: { wallId: string; trim: 'plinth' | 'molding' } | null): void {
    const name = target ? `${target.trim}:${target.wallId}` : null;
    if (name === this.selectedTrim) return;
    this.selectedTrim = name; this.overlaySignature = '';
    this.followSurfaces(); this.requestFrame();
  }

  /** The store's selected door or window; the wall plan drags it along the wall. */
  selectOpening(id: string | null): void {
    if (id === this.selectedOpening) return;
    this.selectedOpening = id; this.overlaySignature = '';
    if (id) { this.materialSlotsSignature = ''; this.updateMaterialSlots(); }
    this.followSurfaces(); this.requestFrame();
  }

  /**
   * Compile the outline shaders — the solid rim and the faint glow every selection wears — off the
   * main thread once the house is lit, so the first wall, door or item selected does not stall a
   * frame (two programs, about 30 ms). The materials live as long as the viewer: disposing a
   * material releases its program.
   */
  private warmOutlineShaders(): void {
    if (this.outlineWarmup || this.disposed || typeof this.renderer?.compile !== 'function' || !this.scene || !this.camera) return;
    const scene = new THREE.Scene(), geometry = new THREE.BufferGeometry();
    // Normals matter: a geometry without them compiles another program.
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(9), 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(new Float32Array(9), 3));
    const materials = [outlineMaterial(1, 'select'), outlineMaterial(.22, 'select')];
    for (const material of materials) scene.add(new THREE.Mesh(geometry, material));
    this.outlineWarmup = materials;
    // Leaving the editor disposes these materials and the renderer; the compile must not outlive them.
    compileInBackground(this.renderer, scene, this.camera, this.scene, () => !this.disposed).catch(() => {}).finally(() => geometry.dispose());
  }

  /** Presence colours never take ownership of the local selection or its gesture. */
  setParticipantSelections(values: readonly ParticipantSelection[]): void {
    if (!this.participantSelections && !values.length) return;
    this.participantSelections ??= new ParticipantSelections(this.scene);
    if (!this.participantSelections.set(values)) return;
    const before = this.participantItemIds ?? new Set<string>();
    this.participantItemIds = new Set(values.flatMap(value => value.target.kind === 'item' ? [value.target.id] : []));
    for (const id of new Set([...before, ...this.participantItemIds])) if (before.has(id) !== this.participantItemIds.has(id)) {
      const node = this.nodeFor(`item:${id}`); if (node) this.syncCohort(node);
    }
    this.cohorts?.flush(); this.followSurfaces(); this.requestFrame();
  }

  /** The store's selected wall or floor, which the 3D view outlines. */
  selectSurface(target: { kind: 'wall'; id: string } | { kind: 'room'; index: number } | null): void {
    const current = this.selectedSurface ?? null;
    const same = target?.kind === 'wall' ? current?.kind === 'wall' && current.id === target.id
      : target?.kind === 'room' ? current?.kind === 'room' && current.index === target.index : !current;
    if (same) return;
    this.selectedSurface = target ? { ...target } : null;
    this.followSurfaces(); this.requestFrame();
  }

  /** What the wall, floor, opening and plinth or molding outlines show now, for checks. */
  surfaceSelectionState(): { surface: { visible: boolean; meshes: number; bounds: THREE.Box3 } | null; opening: boolean; trim: { visible: boolean; strips: number } | null } {
    return { surface: this.surfaceSelection?.describe() ?? null, opening: !!this.openingPresentation?.root.visible, trim: this.trimPresentation?.describe() ?? null };
  }

  /**
   * Keep the wall, floor and opening outlines on their geometry — a rebuild, a cut, a slide. The wall
   * plan draws its own marks over the face instead: a shell round a face seen head-on only frames it.
   */
  private followSurfaces(): void {
    const level = this.level, surface = this.selectedSurface ?? null;
    let target: SurfaceSelectionTarget | null = null;
    if (level && surface?.kind === 'wall') {
      const wall = level.walls.find(candidate => candidate.id === surface.id), node = this.nodeFor(`wall:${surface.id}`);
      if (wall && node) target = { kind: 'wall', node, wall };
    } else if (level && surface?.kind === 'room') {
      const room = level.rooms.find(candidate => candidate.index === surface.index), node = this.nodeFor(`floor:${surface.index}`);
      if (room && node) target = { kind: 'room', node, polygon: room.polygon, insets: roomEdgeInsets(room.polygon, level.walls) };
    }
    const enabled = this.active && !this.wallPlan;
    this.participantSelections?.update(level, key => this.nodeFor(key), enabled);
    if (target || this.surfaceSelection) {
      if (!this.surfaceSelection) { this.surfaceSelection = new SurfaceSelection(); this.scene.add(this.surfaceSelection.root); }
      this.surfaceSelection.update(target, enabled);
    }
    const opening = this.selectedOpening ? this.nodeFor(`opening:${this.selectedOpening}`) ?? null : null;
    if (opening || this.openingPresentation) {
      if (!this.openingPresentation) { this.openingPresentation = new SelectionPresentation(); this.scene.add(this.openingPresentation.root); }
      this.openingPresentation.update(opening, { enabled, model: true });
    }
    // A plinth or a molding lives in its wall's node; the wall plan shows it too, outlined there as in 3D.
    const trimWall = this.selectedTrim?.slice(this.selectedTrim.indexOf(':') + 1);
    const trim = this.selectedTrim && trimWall ? this.nodeFor(`wall:${trimWall}`)?.getObjectByName(this.selectedTrim) ?? null : null;
    if (trim || this.trimPresentation) {
      if (!this.trimPresentation) { this.trimPresentation = new TrimSelection(); this.scene.add(this.trimPresentation.root); }
      this.trimPresentation.update(trim, this.active);
    }
  }

  /** The selected door or window on the wall plan's canvas, for the context bar to sit beside. */
  private wallPlanOpeningOverlay(): ViewerOverlay | null {
    const plan = this.wallPlan, wall = this.wallPlanWall();
    const opening = plan && wall ? this.level?.openings.find(candidate => candidate.id === this.selectedOpening && candidate.wallId === wall.id) : undefined;
    if (!plan || !wall || !opening) return null;
    const n = faceNormal(wall, plan.target.side), rect = this.canvas.getBoundingClientRect(), face = wall.thickness / 2;
    const xs: number[] = [], ys: number[] = [];
    for (const side of [-1, 1]) for (const h of [opening.sill, opening.sill + opening.height]) {
      const along = opening.offset + side * opening.width / 2;
      const point = new THREE.Vector3(wall.start.x + wall.dir.x * along + n.x * face, h, wall.start.z + wall.dir.z * along + n.z * face).project(this.viewCamera);
      xs.push(rect.left + (point.x + 1) * rect.width / 2); ys.push(rect.top + (1 - point.y) * rect.height / 2);
    }
    const x = Math.min(...xs), y = Math.min(...ys), width = Math.max(...xs) - x, height = Math.max(...ys) - y;
    return { center: { x: x + width / 2, y: y + height / 2 }, rotate: null, frame: { x, y, width, height } };
  }

  /**
   * The face of an opening's wall its resize handles stand on: the wall plan's own face, in 3D the one the camera is
   * on; `at` a point on it, along the wall from its start and up from the floor.
   */
  private openingFace(opening: Opening): { wall: Wall; n: Vec2; at: (t: number, h: number) => THREE.Vector3 } | null {
    const wall = this.level?.walls.find(candidate => candidate.id === opening.wallId);
    if (!wall || (this.wallPlan && this.wallPlan.target.wallId !== wall.id)) return null;
    const eye = this.viewCamera.position, mid = { x: wall.start.x + wall.dir.x * opening.offset, z: wall.start.z + wall.dir.z * opening.offset };
    const side = this.wallPlan ? this.wallPlan.target.side : (eye.x - mid.x) * wall.normalIn.x + (eye.z - mid.z) * wall.normalIn.z >= 0 ? 'front' : 'back';
    const n = faceNormal(wall, side), half = wall.thickness / 2;
    return { wall, n, at: (t, h) => new THREE.Vector3(wall.start.x + wall.dir.x * t + n.x * half, h, wall.start.z + wall.dir.z * t + n.z * half) };
  }

  /** Metres per screen pixel at a point in view: the wall plan's own scale, else what a metre across the view spans there. */
  private metresPerPixel(point: THREE.Vector3): number {
    if (this.wallPlan) return this.wallPlanScale();
    const rect = this.canvas.getBoundingClientRect(), right = new THREE.Vector3().setFromMatrixColumn(this.viewCamera.matrixWorld, 0);
    const a = point.clone().project(this.viewCamera), b = point.clone().add(right).project(this.viewCamera);
    const pixels = Math.hypot((b.x - a.x) * rect.width / 2, (b.y - a.y) * rect.height / 2);
    return pixels > 1e-6 ? 1 / pixels : .005;
  }

  /**
   * The selected door's or window's resize mode on screen (OpeningFurnitureController.GetBaseResizeMarkers): a handle on
   * each side and top corner, a window's also on its bottom, on the face in view; while one is dragged only it, with
   * the box it started from. Its sizes stand as dimensions outside it: the width over the top, the height beside it,
   * a window's sill down to the floor. Small on screen, it keeps its sides' handles alone, moved apart to be taken.
   */
  private openingResizeOverlay(): ViewerOverlay | null {
    const opening = this.level?.openings.find(candidate => candidate.id === this.selectedOpening), face = opening && this.openingFace(opening);
    if (!opening || !face) return null;
    const rect = this.canvas.getBoundingClientRect();
    const at = (t: number, h: number) => {
      const point = face.at(t, h).project(this.viewCamera);
      return point.z > -1 && point.z < 1 ? { x: rect.left + (point.x + 1) * rect.width / 2, y: rect.top + (1 - point.y) * rect.height / 2 } : null;
    };
    const box = openingBox(opening), corners = [at(box.t0, box.h0), at(box.t1, box.h0), at(box.t1, box.h1), at(box.t0, box.h1)];
    if (corners.some(point => !point)) return null;
    const outline = corners as Array<{ x: number; y: number }>, xs = outline.map(point => point.x), ys = outline.map(point => point.y);
    const frame = { x: Math.min(...xs), y: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) };
    const drag = this.pointer?.intent.active && this.pointer.openingResize?.id === opening.id ? this.pointer.openingResize : undefined;
    const started = drag ? this.pointer?.base?.openings.find(candidate => candidate.id === opening.id) : undefined;
    const small = Math.min(frame.width, frame.height) < 3 * RESIZE_HANDLE_CLEAR;
    const handles: NonNullable<ViewerOverlay['opening']>['handles'] = [];
    for (const edge of openingEdges(openingKind(opening) === 'window')) {
      const sides = edgeSides(edge);
      if (small && (sides.left || sides.right) && (sides.top || sides.bottom)) continue;
      const active = drag?.edge === edge;
      if (drag && !active) continue;
      const point = edgePoint(box, edge), step = .1;
      const p = at(point.t, point.h), along = at(point.t + (sides.left ? -step : sides.right ? step : 0), point.h + (sides.top ? step : sides.bottom ? -step : 0));
      if (!p || !along) continue;
      const length = Math.hypot(along.x - p.x, along.y - p.y) || 1;
      handles.push({ edge, ...p, angle: Math.atan2(along.y - p.y, along.x - p.x) * 180 / Math.PI + 90, active, dir: { x: (along.x - p.x) / length, y: (along.y - p.y) / length } });
    }
    separateHandles(handles, RESIZE_HANDLE_CLEAR);
    // A dimension along an edge, out from the opening the way `t`, `h` point.
    const dims: NonNullable<ViewerOverlay['opening']>['dims'] = [];
    const dim = (kind: OpeningDimension, a: { t: number; h: number }, b: { t: number; h: number }, out: { t: number; h: number }, size: number, was?: number) => {
      // While an edge is dragged, only the sizes it changes.
      const moved = drag && edgeSides(drag.edge);
      if (moved && !(kind === 'width' ? moved.left || moved.right : kind === 'height' ? moved.top || moved.bottom : moved.bottom)) return;
      const p = at(a.t, a.h), q = at(b.t, b.h), mid = at((a.t + b.t) / 2 + out.t * .1, (a.h + b.h) / 2 + out.h * .1), centre = at((a.t + b.t) / 2, (a.h + b.h) / 2);
      if (!p || !q || !mid || !centre) return;
      const length = Math.hypot(q.x - p.x, q.y - p.y);
      if (length < RESIZE_DIM_MIN / 2) return;
      let n = { x: -(q.y - p.y) / length, y: (q.x - p.x) / length };
      if ((mid.x - centre.x) * n.x + (mid.y - centre.y) * n.y < 0) n = { x: -n.x, y: -n.y };
      const text = tr('#ValueCentimeters', Math.round(size * 100)), delta = was === undefined ? undefined : Math.round(size * 100) - Math.round(was * 100);
      dims.push({ kind, text, ...(delta !== undefined ? { delta } : {}), line: { x1: p.x, y1: p.y, x2: q.x, y2: q.y, n } });
    };
    dim('width', { t: box.t0, h: box.h1 }, { t: box.t1, h: box.h1 }, { t: 0, h: 1 }, opening.width, started?.width);
    dim('height', { t: box.t1, h: box.h0 }, { t: box.t1, h: box.h1 }, { t: 1, h: 0 }, opening.height, started?.height);
    if (openingKind(opening) === 'window' && opening.sill > .005) dim('sill', { t: box.t0, h: 0 }, { t: box.t0, h: box.h0 }, { t: -1, h: 0 }, opening.sill, started?.sill);
    let ghost: Array<{ x: number; y: number }> | undefined;
    if (started) {
      const was = openingBox(started), points = [at(was.t0, was.h0), at(was.t1, was.h0), at(was.t1, was.h1), at(was.t0, was.h1)];
      if (points.every(point => point)) ghost = points as Array<{ x: number; y: number }>;
    }
    return { center: { x: frame.x + frame.width / 2, y: frame.y + frame.height / 2 }, rotate: null, frame, opening: { handles, outline, ...(ghost ? { ghost } : {}), dims } };
  }

  /** An opening's edge taken by its handle in resize mode: it then follows the pointer on the face of its wall (`previewOpeningEdge`). */
  beginOpeningResize(handle: { edge: OpeningEdge; angle: number }, pointer: { clientX: number; clientY: number; pointerId: number; pointerType?: string }): boolean {
    if (!this.selectedOpening || !this.active || !this.editingEnabled || !this.resizeMode) return false;
    if (this.beginTouch(pointer, true) || this.touchNavigation) return false;
    this.interactionHandlers.onEditPrepare?.();
    const opening = this.level?.openings.find(candidate => candidate.id === this.selectedOpening), face = opening && this.openingFace(opening);
    if (!opening || !face) return false;
    this.settleAppearances(); this.cancelInteraction();
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(new THREE.Vector3(face.n.x, 0, face.n.z), face.at(0, 0));
    const hit = this.rayAt(pointer.clientX, pointer.clientY).intersectPlane(plane, new THREE.Vector3());
    if (!hit) return false;
    const point = edgePoint(openingBox(opening), handle.edge);
    this.pointer = { intent: new ScenePointerIntent(pointer.pointerId, pointer.clientX, pointer.clientY, 'resize'), previous: { x: pointer.clientX, y: pointer.clientY },
      base: this.level!, item: null, planeY: 0, grab: { x: 0, z: 0 }, reach: Infinity, rotationOffset: 0,
      openingResize: { id: opening.id, edge: handle.edge, wall: face.wall, plane, grab: { t: alongWall(face.wall, { x: hit.x, z: hit.z }) - point.t, h: hit.y - point.h }, angle: handle.angle } };
    this.controls.enabled = false; this.capturePointer(pointer.pointerId); this.applyCursor('resize', false); return true;
  }

  private selectedOpeningAt(x: number, y: number, wall: Wall): Opening | null {
    if (!this.selectedOpening) return null;
    const rect = this.canvas.getBoundingClientRect(), hit = this.pick(x - rect.left, y - rect.top);
    if (hit?.userData.openingId !== this.selectedOpening) return null;
    return this.level?.openings.find(opening => opening.id === this.selectedOpening && opening.wallId === wall.id) ?? null;
  }

  /**
   * A door or window dragged in the wall plan (the app's WallSide): along the wall, a window also up
   * and down, held by the same magnets as items — the middle, the gaps between its neighbours, the
   * edges and centres of what else is on the face — else in whole centimetres; a position the wall
   * refuses (an overlap, an edge) keeps the last good one.
   */
  private wallPlanOpeningMove(session: PointerSession, e: PointerEvent): Level | null {
    const base = session.base, plan = this.wallPlan, wall = this.wallPlanWall(), hit = this.wallPlanHit(e.clientX, e.clientY);
    const opening = base?.openings.find(candidate => candidate.id === session.opening?.id);
    if (!base || !plan || !wall || !hit || !opening || !session.opening) return null;
    const half = opening.width / 2, low = half + .02, high = Math.max(low, wall.length - half - .02);
    const bounds = wallPlanBounds(base, wall), top = Math.max(0, bounds.height - opening.height), isWindow = openingKind(opening) === 'window';
    const offset = clamp(hit.along - session.opening.grab.along, low, high);
    const sill = isWindow ? clamp(hit.up - session.opening.grab.y, 0, top) : opening.sill;
    const neighbours = base.openings.filter(other => other.wallId === wall.id && other.id !== opening.id);
    const clear = (centre: number) => neighbours.every(other => other.offset + other.width / 2 <= centre - half + 1e-9 || other.offset - other.width / 2 >= centre + half - 1e-9);
    const snap = snapWallPlanBox({ t0: offset - half, t1: offset + half, h0: sill, h1: sill + opening.height },
      wallPlanBoxes(base, plan.target, new Set([opening.id]), session.footprints), bounds, {
        scale: this.wallPlanScale(), vertical: isWindow, disabled: e.ctrlKey || e.metaKey, previous: session.wallCaptures,
        fits: (axis: WallPlanAxis, delta: number) => axis === 't' ? offset + delta >= low - 1e-9 && offset + delta <= high + 1e-9 && clear(offset + delta)
          : sill + delta >= -1e-9 && sill + delta <= top + 1e-9 });
    session.wallCaptures = snap.captures; session.wallGuides = snap.guides;
    const held = (axis: WallPlanAxis) => snap.captures.some(capture => capture.axis === axis);
    const values: OpeningProperties = { offset: held('t') ? Math.round((offset + snap.dt) * 1e4) / 1e4 : Math.round(offset * 100) / 100 };
    if (isWindow) values.sill = held('h') ? Math.round((sill + snap.dh) * 1e4) / 1e4 : clamp(Math.round(sill * 100) / 100, 0, top);
    return editProperties(base, { kind: 'opening', id: opening.id, values }).level ?? null;
  }

  /** Drag the frame with the pointer; the wall never leaves it. */
  private panWallPlan(dxPx: number, dyPx: number): void {
    const plan = this.wallPlan, frame = this.wallPlanFrame();
    if (!plan || !frame) return;
    this.stopWallSlide();
    plan.pan = panWallPlanBy(frame, dxPx, dyPx);
    this.applyWallPlanCamera(); this.overlaySignature = ''; this.requestFrame();
  }

  /** Zoom about a canvas point, 1× to 8×: what is under the pointer stays there. */
  private zoomWallPlanBy(factor: number, canvasX: number, canvasY: number): void {
    const plan = this.wallPlan, frame = this.wallPlanFrame();
    if (!plan || !frame) return;
    this.stopWallSlide();
    const next = zoomWallPlanAt(frame, factor, canvasX, canvasY);
    plan.zoom = next.zoom; plan.pan = next.pan;
    this.applyWallPlanCamera(); this.overlaySignature = ''; this.requestFrame();
  }

  private onWheel = (e: WheelEvent): void => {
    if (this.active && this.mode === 'walk' && !this.wallPlan && !this.participantView) { this.wheelWalk(e); return; }
    if (!this.wallPlan || !this.active) return;
    this.interactionHandlers?.onNavigationIntent?.();
    e.preventDefault();
    const rect = this.canvas.getBoundingClientRect();
    const step = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? rect.height : 1;
    // A trackpad pinch arrives as a ctrl-wheel in far smaller steps, so it zooms at its own rate, as the plan does.
    const amount = clamp(-e.deltaY * step * (e.ctrlKey ? .01 : .0015), -1, 1);
    this.zoomWallPlanBy(Math.exp(amount), e.clientX - rect.left, e.clientY - rect.top);
  };

  /**
   * The wheel walks in the walk, as the app's does (`ZoomByWalk`): a notch pushes the walker along the view and it slides
   * about half a metre to a stop; a trackpad's sideways scroll steps sideways the same way; its pinch (a ctrl-wheel)
   * steps toward what lies under the pointer, as two fingers do. The walls stop it as they stop the keys.
   */
  private wheelWalk(e: WheelEvent): void {
    e.preventDefault();
    this.interactionHandlers?.onNavigationIntent?.();
    this.walkFlight = null;
    const unit = e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? this.canvas.getBoundingClientRect().height : 1;
    if (e.ctrlKey) {
      const point = this.worldPointAt(e.clientX, e.clientY);
      const distance = point ? Math.hypot(point.x - this.walk.pos.x, point.y - this.walk.pos.y, point.z - this.walk.pos.z) : null;
      this.walkBy(pinchStep(distance, Math.exp(-e.deltaY * unit * .01)), 0);
      return;
    }
    // A notch of 100 px gives 1.5 m/s, which the wheel's friction spends in about half a metre.
    const forward = -e.deltaY * unit * .015, sideways = e.deltaX * unit * .015;
    const sin = Math.sin(this.walk.yaw), cos = Math.cos(this.walk.yaw);
    const coast = this.walkCoast?.friction === WHEEL_FRICTION ? this.walkCoast : { x: 0, z: 0 };
    let x = coast.x + sin * forward - cos * sideways, z = coast.z + cos * forward + sin * sideways;
    const speed = Math.hypot(x, z);
    if (speed > 3) { x *= 3 / speed; z *= 3 / speed; }
    this.walkCoast = { x, z, friction: WHEEL_FRICTION };
    this.requestFrame();
  }

  /** Two fingers on the wall plan: zoom about the point between them and carry the frame with them. */
  private pinchWallPlan(): void {
    const points = [...(this.touches?.values() ?? [])];
    if (points.length < 2) { this.wallPinch = null; return; }
    const rect = this.canvas.getBoundingClientRect();
    const mid = { x: (points[0].x + points[1].x) / 2 - rect.left, y: (points[0].y + points[1].y) / 2 - rect.top };
    const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
    const previous = this.wallPinch;
    this.wallPinch = { distance, mid };
    if (!previous) return;
    if (previous.distance > 1) this.zoomWallPlanBy(distance / previous.distance, mid.x, mid.y);
    this.panWallPlan(mid.x - previous.mid.x, mid.y - previous.mid.y);
    this.interacting = true;
  }

  /** Native surface-following highlight; selection never enters pickable scene contents. */
  highlight(itemId: string | null): void {
    const previous = this.highlighted;
    this.highlighted = itemId;
    for (const id of new Set([previous,itemId])) { const node = id && this.nodeFor(`item:${id}`); if (node) this.syncCohort(node); }
    this.cohorts?.flush();
    this.materialSlotsSignature = '';
    this.overlaySignature = '';
    this.followHighlight();
    this.updateMaterialSlots(); this.requestFrame();
  }

  /** Outline every item of a Shift-gathered group; an empty list lets the group go. */
  gather(ids: readonly string[]): void {
    const previous = this.gathered ?? [];
    if (previous.length === ids.length && previous.every((id, i) => id === ids[i])) return;
    this.gathered = [...ids];
    for (const id of new Set([...previous, ...ids])) { const node = this.nodeFor(`item:${id}`); if (node) this.syncCohort(node); }
    this.cohorts?.flush();
    this.overlaySignature = '';
    this.followGathered(); this.requestFrame();
  }

  private clearGathered(): void {
    for (const presentation of this.gatheredPresentations?.values() ?? []) presentation.dispose();
    this.gatheredPresentations?.clear(); this.gathered = [];
  }

  /** Keep each gathered item's outline on its item while the group moves or models arrive. */
  private followGathered(): void {
    for (const [id, presentation] of this.gatheredPresentations) if (!this.gathered.includes(id)) {
      presentation.dispose(); this.gatheredPresentations.delete(id);
    }
    for (const id of this.gathered) {
      const target = this.nodeFor(`item:${id}`), item = this.itemsById?.get(id) ?? this.level?.items.find(value => value.id === id);
      let presentation = this.gatheredPresentations.get(id);
      if (!target || !item) { presentation?.clear(); continue; }
      if (!presentation) { presentation = new SelectionPresentation(); this.scene.add(presentation.root); this.gatheredPresentations.set(id, presentation); }
      presentation.update(target, { enabled: this.active && !this.wallPlan, model: item.modelRef.kind !== 'none' && !isRegion(item) });
    }
  }

  /** The parts of the selected item's model, or of the selected door's or window's, for its colours (the app's FurnitureColors). */
  private updateMaterialSlots():void {
    const item=this.level?.items.find(it=>it.id===this.highlighted);
    const opening=item?undefined:this.level?.openings.find(value=>value.id===this.selectedOpening);
    const target=item??opening,node=item?this.nodeFor(`item:${item.id}`):opening&&openingModelNode(this.nodeFor(`opening:${opening.id}`));
    if(!target||!node)return;
    const slots=this.models?.materialSlots?.(node,target)??[];
    const signature=JSON.stringify([target.id,slots]);
    if(signature!==this.materialSlotsSignature){this.materialSlotsSignature=signature;this.interactionHandlers?.onMaterialSlots?.(target.id,slots);}
  }

  setMaterialTransformPreview(preview:EditorState['materialTransformPreview']):void {
    const previousTarget=this.materialPreviewState?.target;
    this.materialPreviewState=preview;
    const nextTarget=preview?.target;
    if (!preview || JSON.stringify(previousTarget) !== JSON.stringify(nextTarget)) {
      this.materialPreview?.end(); this.materialPreviewKey='';
    }
    for(const target of [previousTarget,nextTarget]) if(target?.kind==='item'){const node=this.nodeFor(`item:${target.itemId}`);if(node)this.syncCohort(node);}
    this.cohorts?.flush();
    if(!preview){if(previousTarget?.kind==='item')this.topLayer?.invalidate(`item:${previousTarget.itemId}`);this.requestFrame();return;}
    this.materialPreview??=new MaterialTransformPreview(()=>this.built?.root, root => this.retainResources(root));
    const key=JSON.stringify([preview.target,preview.baseline]);
    if(key!==this.materialPreviewKey||!this.materialPreview.active){this.materialPreview.begin(preview.target,preview.baseline);this.materialPreviewKey=key;}
    this.materialPreview.update(preview.draft);if(preview.target.kind==='item')this.topLayer?.invalidate(`item:${preview.target.itemId}`);this.requestFrame();
  }

  placementPointer(clientX: number, clientY: number): LandingPointer {
    const ray = this.rayAt(clientX, clientY);
    return { kind: 'ray', origin: { x: ray.origin.x, y: ray.origin.y, z: ray.origin.z },
      direction: { x: ray.direction.x, y: ray.direction.y, z: ray.direction.z } };
  }

  shelfPointAt(item: Item, clientX: number, clientY: number, options: { level?: Level; grab?: Vec2; footprints?: Footprints; currentElevation?: number } = {}): Vec2 | null {
    const level = options.level ?? this.level;
    if (!level || !this.surfaceProbe?.layers) return null;
    const exclude = new Set([item.id, ...stackedItems(level.items, item, this.surfaceProbe).map(value => value.id)]);
    const rect = this.canvas.getBoundingClientRect(), hit = this.pick(clientX - rect.left, clientY - rect.top, exclude);
    const baseId = hit?.userData.itemId;
    if (typeof baseId !== 'string') return null;
    return pointedShelfPosition(level, item, this.placementPointer(clientX, clientY), baseId,
      { ...options, probe: this.surfaceProbe, exclude });
  }

  /** Read the visible surface under a collaborator without changing selection or material picks. */
  worldPointAt(clientX: number, clientY: number): { x: number; y: number; z: number } | null {
    const rect = this.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height || clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) return null;
    const caster = new THREE.Raycaster();
    this.viewCamera.updateMatrixWorld();
    caster.setFromCamera(new THREE.Vector2((clientX - rect.left) / rect.width * 2 - 1, -(clientY - rect.top) / rect.height * 2 + 1), this.viewCamera);
    this.cohorts?.flush();
    const candidates = this.built ? (this.index?.candidates(caster.ray) ?? this.built.pickable).filter(object => {
      for (let node: THREE.Object3D | null = object; node; node = node.parent) if (!node.visible) return false;
      return true;
    }) : [];
    const ordinary = caster.intersectObjects(candidates, false)[0], instance = this.cohorts?.pick(caster);
    const hit = instance && (!ordinary || instance.distance < ordinary.distance) ? instance : ordinary;
    if (hit) return { x: hit.point.x, y: hit.point.y, z: hit.point.z };
    const wall = this.wallPlanWall(), plan = this.wallPlan;
    const normal = wall && plan ? faceNormal(wall, plan.target.side) : null;
    const plane = wall && normal
      ? new THREE.Plane().setFromNormalAndCoplanarPoint(new THREE.Vector3(normal.x, 0, normal.z), new THREE.Vector3(wall.start.x + normal.x * wall.thickness / 2, 0, wall.start.z + normal.z * wall.thickness / 2))
      : new THREE.Plane(new THREE.Vector3(0, 1, 0), this.shellInputs?.ceiling && caster.ray.direction.y > 0 ? -(this.level?.height ?? 0) : 0);
    const point = caster.ray.intersectPlane(plane, new THREE.Vector3());
    return point ? { x: point.x, y: point.y, z: point.z } : null;
  }

  /** Where a screen point meets the floor plane, for dropping catalog items. */
  /**
   * The wall face under the pointer, for a catalog piece put on a wall there: the wall, how far along it, the height
   * of the point and the face. In the wall plan it is the face shown.
   */
  wallPointAt(clientX: number, clientY: number): { wallId: string; along: number; height: number; side: 'front' | 'back' } | null {
    const level = this.level;
    if (!level) return null;
    const shown = this.wallPlanWall(), plan = this.wallPlan;
    if (shown && plan) {
      const hit = this.wallPlanHit(clientX, clientY);
      return hit ? { wallId: shown.id, along: hit.along, height: hit.up, side: plan.target.side } : null;
    }
    const hit = this.wallHit(clientX, clientY, { ...level, walls: level.walls.filter(wall => !wall.hidden) });
    if (!hit) return null;
    const front = hit.normal.x * hit.wall.normalIn.x + hit.normal.z * hit.wall.normalIn.z >= 0;
    const along = (hit.point.x - hit.wall.start.x) * hit.direction.x + (hit.point.z - hit.wall.start.z) * hit.direction.z;
    return { wallId: hit.wall.id, along, height: hit.point.y, side: front ? 'front' : 'back' };
  }

  floorPointAt(clientX: number, clientY: number): { x: number; z: number } | null {
    // In the wall plan the view is horizontal, so the floor point is the foot of the face under
    // the pointer, stepped a little into the room: a catalog item lands against this wall.
    const wall = this.wallPlanWall(), plan = this.wallPlan;
    if (wall && plan) {
      const hit = this.wallPlanHit(clientX, clientY);
      if (!hit) return null;
      const n = faceNormal(wall, plan.target.side), along = clamp(hit.along, 0, wall.length), out = wall.thickness / 2 + .3;
      return { x: wall.start.x + wall.dir.x * along + n.x * out, z: wall.start.z + wall.dir.z * along + n.z * out };
    }
    const rect = this.canvas.getBoundingClientRect();
    const ray = new THREE.Raycaster();
    ray.setFromCamera(
      new THREE.Vector2(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1),
      this.camera,
    );
    const hit = new THREE.Vector3();
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    return ray.ray.intersectPlane(plane, hit) ? { x: hit.x, z: hit.z } : null;
  }

  /** Save the current frame as a PNG. */
  capture(name: string): void {
    this.settleAppearances(); this.followHighlight(); this.followSurfaces();
    if (!this.still?.presentToScreen()) this.drawFrame();
    this.canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name || 'scene'}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    }, 'image/png');
  }

  /**
   * The current frame as an image for uploads: drawn onto an opaque canvas (the WebGL
   * buffer is transparent where the sky is, so `background` decides what shows through),
   * the long side capped to what an AI render needs, encoded synchronously after the draw
   * so the buffer is still there.
   */
  snapshot(maxSide = 1600, type = 'image/jpeg', quality = 0.88, crop?: { x: number; y: number; width: number; height: number }, background = '#ffffff'): Promise<Blob | null> {
    this.settleAppearances(); this.followHighlight(); this.followSurfaces();
    if (!this.still?.presentToScreen()) this.drawFrame();
    const source = this.canvas;
    const region = crop ?? { x: 0, y: 0, width: source.width, height: source.height };
    const scale = Math.min(1, maxSide / Math.max(region.width, region.height, 1));
    const target = document.createElement('canvas');
    target.width = Math.max(1, Math.round(region.width * scale));
    target.height = Math.max(1, Math.round(region.height * scale));
    const context = target.getContext('2d');
    if (!context) return Promise.resolve(null);
    context.fillStyle = background;
    context.fillRect(0, 0, target.width, target.height);
    context.drawImage(source, region.x, region.y, region.width, region.height, 0, 0, target.width, target.height);
    return new Promise((resolve) => target.toBlob((blob) => resolve(blob), type, quality));
  }

  private zoomBase: { fov: number; walkFov: number | null } | null = null;

  /** The camera mode's 0.5×–3× zoom: a narrower field of view from the one in use. 1 restores. */
  setZoom(factor: number): void {
    if (factor === 1 && !this.zoomBase) return;
    this.interactionHandlers?.onNavigationIntent?.();
    this.zoomBase ??= { fov: this.camera.fov, walkFov: this.walkFovH };
    const base = this.zoomBase;
    if (this.mode === 'walk' && base.walkFov !== null) { this.walkFovH = base.walkFov / factor; this.applyWalkFov(); }
    else { this.camera.fov = Math.max(5, Math.min(120, base.fov / factor)); this.camera.updateProjectionMatrix(); }
    if (factor === 1) this.zoomBase = null;
    this.requestFrame(true);
  }

  /** What a tap at the client point would pick, with the finish under it. */
  pickAt(clientX: number, clientY: number): { hit: THREE.Object3D | null; material: { slot: string | null; wallSide?: 'front' | 'back' } } {
    const rect = this.canvas.getBoundingClientRect(), hit = this.pick(clientX - rect.left, clientY - rect.top);
    return { hit, material: this.pickedMaterial };
  }

  /** Object under the pointer, in canvas-relative pixels. */
  pick(x: number, y: number, exclude?: ReadonlySet<string>): THREE.Object3D | null {
    if (!this.built) return null;
    const rect = this.canvas.getBoundingClientRect();
    const ray = new THREE.Raycaster();
    ray.setFromCamera(new THREE.Vector2((x / rect.width) * 2 - 1, -(y / rect.height) * 2 + 1), this.viewCamera);
    this.cohorts?.flush();
    const candidates = this.index ? this.index.candidates(ray.ray) : this.built.pickable.filter(object => {
      for (let node: THREE.Object3D | null = object; node; node = node.parent) if (!node.visible) return false;
      return true;
    });
    const ordinary = ray.intersectObjects(exclude ? candidates.filter(object => !exclude.has(object.userData.itemId)) : candidates, false)[0];
    const instance = this.cohorts?.pick(ray), cohort = instance && !exclude?.has(instance.object.userData.itemId) ? instance : null;
    const hit = cohort && (!ordinary || cohort.distance < ordinary.distance) ? cohort : ordinary;
    // A window's panes are air: aimed through them, the ray would pick the room behind or the neighbour's wall.
    const opening = this.openingOnRay(ray.ray, hit ?? null);
    if (opening) { this.pickedMaterial = { slot: null }; return opening; }
    const mesh=hit?.object as THREE.Mesh|undefined;
    const mat=mesh?.material&&(Array.isArray(mesh.material)?mesh.material[hit?.face?.materialIndex??0]:mesh.material);
    this.pickedMaterial={slot:mat?.name||null,wallSide:mat?.userData.hpWallSide};
    return hit?.object ?? null;
  }

  /**
   * The opening a ray passes through, when nothing stands in front of it: in the wall plan every opening
   * of the shown wall, in 3D every window. Its box is the hole it cuts through the wall, clipped to the
   * height the cutaway or walls-down leave of that wall, so a hidden part of a window is not clicked.
   */
  private openingOnRay(ray: THREE.Ray, hit: { distance: number; object: THREE.Object3D } | null): THREE.Object3D | null {
    const level = this.level;
    if (!level || !this.built || !level.openings.length) return null;
    const planWall = this.wallPlan ? this.wallPlanWall() : null;
    if (this.wallPlan && !planWall) return null;
    const now = performance.now(), shown = (node: THREE.Object3D | undefined | null) => {
      for (let current: THREE.Object3D | null = node ?? null; current; current = current.parent) if (!current.visible) return false;
      return !!node;
    };
    let best: { distance: number; opening: Opening } | null = null;
    for (const opening of level.openings) {
      if (planWall ? opening.wallId !== planWall.id : openingKind(opening, this.models?.entry?.(opening.modelRef.id)?.tags ?? []) !== 'window') continue;
      const wall = planWall ?? level.walls.find(candidate => candidate.id === opening.wallId);
      if (!wall || wall.hidden || !shown(this.nodeFor(`wall:${wall.id}`))) continue;
      const distance = openingRayEntry(ray, wall, opening, planWall ? Infinity : this.cutHeightAt(wall.id, now, 'attachment') ?? Infinity);
      if (distance !== null && (!best || distance < best.distance)) best = { distance, opening };
    }
    if (!best) return null;
    // Furniture, a curtain rod of another window, anything nearer than the hole keeps the click.
    if (hit && hit.object.userData.openingId !== best.opening.id && hit.distance < best.distance - .02) return null;
    const node = this.nodeFor(`opening:${best.opening.id}`);
    if (node) return node;
    const stub = new THREE.Object3D();
    stub.userData = { kind: 'opening', openingId: best.opening.id, caption: best.opening.caption };
    return stub;
  }

  dispose(): void {
    this.disposed = true;
    this.stopWallSlide();
    this.unsubscribeWebStates();
    this.materialPreview?.end();this.materialPreviewState=null;
    this.clearGathered();
    this.appearance?.dispose(); this.appearanceRoot?.removeFromParent();
    this.selectionPresentation?.dispose(); this.selectionPresentation?.root.removeFromParent();
    this.surfaceSelection?.dispose(); this.openingPresentation?.dispose(); this.trimPresentation?.dispose(); this.participantSelections?.dispose();
    for (const material of this.outlineWarmup ?? []) material.dispose();
    this.clearConflicts();
    this.pendingAppearances.clear();
    this.unsubscribeAssets(); this.unsubscribeBuiltins(); this.unsubscribeBounds(); this.unsubscribeSpace?.();
    if (this.wellsRefresh !== null) cancelAnimationFrame(this.wellsRefresh);
    this.topLayer?.dispose(); this.cohorts?.dispose();
    this.contactShadows?.dispose();
    this.still?.dispose();
    this.hydration.dispose();
    this.lowerFloors?.dispose(); this.floorAbove?.dispose();
    if (this.lowerFloorsFrame) clearTimeout(this.lowerFloorsFrame);
    if (this.lowerFloorsWarm) clearTimeout(this.lowerFloorsWarm);
    this.scheduler?.dispose();
    this.releaseShell?.(); this.releaseShell = undefined;
    this.models.dispose();
    this.index?.clear(); this.cameraViews?.clear();
    window.removeEventListener('resize', this.onResize);
    this.cancelInteraction();
    this.canvas.removeEventListener('pointerdown', this.onPointerDown, true);
    this.canvas.removeEventListener('wheel', this.onWheel);
    if (this.wallGrid) { this.wallGrid.removeFromParent(); this.wallGrid.geometry.dispose(); const material = this.wallGrid.material as THREE.MeshBasicMaterial; material.map?.dispose(); material.dispose(); }
    window.removeEventListener('pointerup', this.onPointerUp, true);
    window.removeEventListener('pointermove', this.onPointerMove, true);
    window.removeEventListener('pointercancel', this.onPointerCancel);
    window.removeEventListener('blur', this.onBlur);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.canvas.removeEventListener('contextmenu', this.onContextMenu);
    this.controls.removeEventListener('start', this.onInteractStart);
    this.controls.removeEventListener('end', this.onInteractEnd);
    this.controls.removeEventListener('change', this.onControlsChange);
    if (this.built) disposeTree(this.built.root);
    this.envTarget?.dispose();
    this.outdoor?.dispose(); this.windowViewListeners?.clear(); clearTimeout(this.sunTimer);
    this.controls.dispose();
    // React can mount another Viewer on this same canvas. Texture uploads leave
    // pixel-store flags set; Three's new 3D fallback textures require clean state.
    this.renderer.resetState();
    this.renderer.dispose();
  }

  // ---- internals -----------------------------------------------------------
  private onInteractStart(): void {
    this.interactionHandlers?.onNavigationIntent?.();
    if (this.interacting) return;
    this.interacting = true; this.requestFrame();
  }

  private onInteractEnd(): void {
    if (!this.interacting) return;
    this.interacting = false; this.requestFrame();
  }

  private onResize = (): void => {
    const w = this.canvas.clientWidth || 1;
    const h = this.canvas.clientHeight || 1;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    // The walk view's vertical fov depends on the aspect, so it has to be re-derived
    // here. Deriving it once at mode-switch time silently bakes in whatever aspect the
    // canvas happened to have — and a canvas that has not been laid out yet reports a
    // degenerate one, which turns a 104° view into a 0.4° telescope pointed at a wall.
    this.applyWalkFov();
    this.camera.updateProjectionMatrix(); this.applyWallPlanCamera(); this.requestFrame();
  };

  /** Project files store a HORIZONTAL field of view; three.js wants the vertical one. */
  private applyWalkFov(): void {
    if (this.mode !== 'walk' || this.walkFovH === null) return;
    const aspect = this.camera.aspect;
    if (!Number.isFinite(aspect) || aspect < 0.2 || aspect > 6) return;   // not laid out yet
    const hfov = (this.walkFovH * Math.PI) / 180;
    this.camera.fov = (2 * Math.atan(Math.tan(hfov / 2) / aspect) * 180) / Math.PI;
    this.camera.updateProjectionMatrix();
  }

  private beginTouch(pointer: {clientX:number;clientY:number;pointerId:number;pointerType?:string}, fromOverlay = false): boolean {
    if (pointer.pointerType !== 'touch') return false;
    this.touches ??= new Map(); this.touches.set(pointer.pointerId,{x:pointer.clientX,y:pointer.clientY});
    if (this.touches.size < 2 && !this.touchNavigation) return false;
    this.interactionHandlers?.onNavigationIntent?.();
    const first = this.pointer, wasEdit = first && first.intent.kind !== 'camera';
    if (first?.intent.kind === 'camera' && this.mode !== 'walk') this.pointer = null;
    else { const touches = new Map(this.touches); this.cancelInteraction(); this.touches = touches; }
    this.touchNavigation = true;
    this.interactionHandlers.onGesture?.('camera');
    if (this.mode === 'walk' || this.wallPlan) { this.walk.dragging = false; this.wallPinch = null; this.walkFingers = null; this.walkCoast = null; this.walkFlight = null; return true; }
    this.controls.enabled = true;
    // Overlay starts and the first edit touch never reached OrbitControls.
    const withheld = wasEdit && first ? [first.intent.pointerId] : [];
    if (fromOverlay) withheld.push(pointer.pointerId);
    this.forwardingTouch = true;
    try {
      for (const pointerId of withheld) {
        const point = this.touches.get(pointerId);
        if (point) this.canvas.dispatchEvent(new PointerEvent('pointerdown',{pointerId,
          pointerType:'touch',clientX:point.x,clientY:point.y,button:0,bubbles:true}));
      }
    } finally { this.forwardingTouch = false; }
    return true;
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (this.forwardingTouch) return;
    if (this.beginTouch(e)) {
      if (this.mode === 'walk' || this.wallPlan) { e.stopImmediatePropagation(); e.preventDefault(); }
      return;
    }
    this.pointerDown(e);
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (this.touches?.has(e.pointerId)) { this.touches.delete(e.pointerId); this.wallPinch = null; if (this.walkFingers) this.endWalkFingers(); }
    if (this.touchNavigation) {
      if (!this.touches?.size) { this.touchNavigation = false; this.interacting = false; this.interactionHandlers.onGesture?.(null); this.requestFrame(); }
      return;
    }
    if (this.pointer?.intent.pointerId !== e.pointerId) return;
    this.finishInteraction(false, e);
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.pointer && !this.touchNavigation) this.trackHover(e);
    if (this.touches?.has(e.pointerId)) this.touches.set(e.pointerId,{x:e.clientX,y:e.clientY});
    if (this.touchNavigation) {
      if (this.wallPlan) this.pinchWallPlan();
      else if (this.mode === 'walk') this.walkTwoFingers();
      if(this.mode==='walk'||this.wallPlan){e.stopImmediatePropagation();e.preventDefault();}
      return;
    }
    this.pointerMove(e);
  };
  private onPointerCancel = (e: PointerEvent): void => {
    this.touches?.delete(e.pointerId);
    this.walkFingers = null;
    if (this.pointer?.intent.pointerId === e.pointerId) this.cancelInteraction();
    if (this.touchNavigation && !this.touches?.size) { this.touchNavigation=false; this.interacting=false; this.interactionHandlers.onGesture?.(null); this.requestFrame(); }
  };
  private cancelTouchNavigation(): void {
    const wasNavigation = this.touchNavigation;
    for (const [pointerId] of this.touches ?? []) {
      if (typeof PointerEvent !== 'undefined') this.canvas.dispatchEvent(new PointerEvent('pointercancel',{pointerId,pointerType:'touch',bubbles:true}));
    }
    this.touches?.clear(); this.touchNavigation=false; this.interacting=false;
    if (wasNavigation) this.interactionHandlers.onGesture?.(null);
  }
  private onBlur = (): void => { this.walk.keys.clear(); this.setWalkStick(0, 0); this.stopWalkMotion(); this.cancelInteraction(); this.cancelTouchNavigation(); this.settleAppearances(); };
  private onVisibilityChange = (): void => { if (document.hidden) { this.onBlur(); this.scheduler?.stop(); } else this.requestFrame(); };
  /** Back online: the pictures that failed meanwhile no longer wait out their pause. */
  private onOnline = (): void => { this.models.textureImages.retryFailed(); };

  setInteractionHandlers(handlers: ViewerInteractionHandlers): void { this.interactionHandlers = handlers; }
  setEditingEnabled(enabled: boolean): void {
    if (!enabled && this.pointer?.intent.kind !== 'camera') this.cancelInteraction();
    if (!enabled) this.settleAppearances();
    this.editingEnabled = enabled;
    this.overlaySignature = ''; this.requestFrame();
  }
  setResizeMode(enabled: boolean): void {
    if (!enabled && this.pointer?.intent.kind === 'resize') this.cancelInteraction();
    if (enabled) this.settleAppearances();
    this.resizeMode = enabled; this.overlaySignature = ''; this.requestFrame();
  }
  cancelInteraction(): void {
    if (this.roomNavigationFrame) cancelAnimationFrame(this.roomNavigationFrame);
    this.roomNavigationFrame = 0;
    if (this.pointer) this.finishInteraction(true);
  }
  /** Ends a held edit as Escape would; an orbit or a walk look goes on. */
  cancelEdit(): void {
    if (this.pointer && this.pointer.intent.kind !== 'camera') this.finishInteraction(true);
  }

  /** Pointer capture that tolerates a pointer the browser does not consider active. */
  private capturePointer(pointerId: number): void {
    try { this.canvas.setPointerCapture(pointerId); } catch { /* synthetic or already-released pointer */ }
  }

  private pointerDown(e: PointerEvent): void {
    // The right button is the spare hand for the camera: OrbitControls already owns it for
    // orbiting, and in walk — where those controls are off — it turns the head, so a mouse user
    // can look around without first finding a patch of floor that holds no furniture.
    const secondary = e.button === 2;
    if (!this.active || (e.button !== 0 && !secondary) || this.touchNavigation) return;
    this.interactionHandlers?.onNavigationIntent?.();
    if (this.mode === 'walk') { this.walkFlight = null; this.walkCoast = null; }
    if (secondary && this.mode !== 'walk') return;
    if (this.pointer) { this.cancelInteraction(); return; }
    if (this.wallPlan) { this.wallPlanPointerDown(e); return; }
    let item = !secondary && !spaceHeld() && this.editingEnabled && !this.resizeMode ? this.selectedItemAt(e.clientX, e.clientY) : null;
    // A press on one of the gathered items pulls the whole group; with Shift the press is a tap
    // that takes the item out of the group, so it starts nothing.
    const gathered = !item && !secondary && !e.shiftKey && !spaceHeld() && this.editingEnabled && this.gathered?.length ? this.gatheredItemAt(e.clientX, e.clientY) : null;
    if (gathered) { this.beginGroupMove(e, gathered); return; }
    if (item) {
      this.interactionHandlers.onEditPrepare?.();
      item = this.level?.items.find(value => value.id === item!.id) ?? null;
      this.settleAppearances();
    }
    const base = this.level ?? null;
    let planeY = item ? item.elevation : 0;
    const planeHit = item ? this.horizontalHit(e.clientX, e.clientY, planeY) : null;
    const node = item ? this.nodeFor(`item:${item.id}`) : undefined;
    const pointerRay = node ? this.rayAt(e.clientX, e.clientY) : null;
    const meshHit = node && pointerRay ? new THREE.Raycaster(pointerRay.origin, pointerRay.direction).intersectObject(node, true)[0]?.point : null;
    // An upper shelf can put the pressed part of an item above the eye. Its bottom plane
    // then has no forward ray hit, but the visible mesh still starts an object gesture.
    const hit = planeHit ?? meshHit;
    if (!planeHit && meshHit) planeY = meshHit.y;
    const wallHit = item?.placement === 'wall' && base ? this.wallHit(e.clientX, e.clientY, base) : null;
    const movable = item && (item.placement === 'wall' ? wallHit : hit);
    this.pointer = { intent: new ScenePointerIntent(e.pointerId, e.clientX, e.clientY, movable ? 'move' : 'camera'), secondary,
      previous: { x: e.clientX, y: e.clientY }, base: movable && base ? base : null, item: movable ? item : null,
      planeY, grab: hit && item ? { x: hit.x - item.position.x, z: hit.z - item.position.z } : { x: 0, z: 0 },
      shelfGrab: meshHit && item ? { x: meshHit.x - item.position.x, z: meshHit.z - item.position.z } : undefined,
      meshSeed: !planeHit && meshHit && pointerRay && item?.placement === 'floor' ? { point: { x: meshHit.x, z: meshHit.z },
        normal: { x: pointerRay.direction.x, z: pointerRay.direction.z } } : undefined,
      reach: hit ? Math.max(this.camera.position.distanceTo(new THREE.Vector3(hit.x, hit.y, hit.z)) * 3, 4) : 60,
      roomIndex: item ? base?.rooms.find(room => containsPoint(room.polygon, item.position))?.index : undefined,
      rotationOffset: 0,
      gesture: movable && base && item && item.placement !== 'wall' ? { base, sourceId: item.id, copyId: null } : undefined,
      origin: item ? { x: item.position.x, z: item.position.z } : undefined,
      footprints: movable && base ? this.gestureFootprints(base) : undefined,
      wallGrab: wallHit && item ? { along: (wallHit.point.x - item.position.x) * wallHit.direction.x
        + (wallHit.point.z - item.position.z) * wallHit.direction.z, y: wallHit.point.y - item.elevation } : undefined };
    if (movable) {
      this.controls.enabled = false;
      e.stopImmediatePropagation(); e.preventDefault();
      this.capturePointer(e.pointerId);
    } else if (this.mode === 'walk') this.capturePointer(e.pointerId);
  }

  /** The gathered item under the pointer, when it is one that can travel across the floor. */
  private gatheredItemAt(x: number, y: number): Item | null {
    const rect = this.canvas.getBoundingClientRect(), id = this.pick(x - rect.left, y - rect.top)?.userData.itemId;
    const item = typeof id === 'string' && this.gathered.includes(id) ? this.level?.items.find(value => value.id === id) : undefined;
    return item && item.placement !== 'wall' ? item : null;
  }

  private beginGroupMove(e: PointerEvent, pressed: Item): void {
    this.interactionHandlers.onEditPrepare?.();
    this.settleAppearances();
    const base = this.level, item = base?.items.find(value => value.id === pressed.id);
    const hit = item ? this.horizontalHit(e.clientX, e.clientY, item.elevation) : null;
    const { ids, held } = movableItems(base ?? { items: [] }, this.gathered, this.surfaceProbe);
    if (!base || !item || !hit || !ids.length) return;
    this.pointer = { intent: new ScenePointerIntent(e.pointerId, e.clientX, e.clientY, 'move'),
      previous: { x: e.clientX, y: e.clientY }, base, item, planeY: item.elevation,
      grab: { x: hit.x - item.position.x, z: hit.z - item.position.z },
      reach: Math.max(this.camera.position.distanceTo(new THREE.Vector3(hit.x, hit.y, hit.z)) * 3, 4),
      rotationOffset: 0, group: ids, held };
    this.controls.enabled = false;
    e.stopImmediatePropagation(); e.preventDefault();
    this.capturePointer(e.pointerId);
  }

  beginRotation(pointer: { clientX: number; clientY: number; pointerId: number; pointerType?: string }): boolean {
    let item = this.level?.items.find(candidate => candidate.id === this.highlighted);
    if (!this.active || !this.editingEnabled || !item || item.locked || item.placement === 'wall') return false;
    if (this.beginTouch(pointer, true) || this.touchNavigation) return false;
    this.interactionHandlers.onEditPrepare?.();
    item = this.level?.items.find(value => value.id === item!.id);
    if (!item) return false;
    this.settleAppearances();
    this.cancelInteraction();
    const planeY = item.elevation + Math.min(item.size.h / 2, .6);
    const hit = this.horizontalHit(pointer.clientX, pointer.clientY, planeY);
    if (!hit) return false;
    this.pointer = { intent: new ScenePointerIntent(pointer.pointerId, pointer.clientX, pointer.clientY, 'rotate'),
      previous: { x: pointer.clientX, y: pointer.clientY }, base: this.level!, item,
      planeY, grab: { x: 0, z: 0 }, reach: Infinity,
      rotationOffset: item.rotation - Math.atan2(hit.z - item.position.z, hit.x - item.position.x) };
    this.controls.enabled = false;
    this.capturePointer(pointer.pointerId);
    return true;
  }

  /**
   * The height drag button pressed (the app's HeightFromFloorWithDragButton): the selected piece on the floor or under the
   * ceiling rides up and down with the pointer on a vertical plane through it that faces the camera, from the floor to its
   * top at the ceiling, what stands on it going along, as one undo step. Looking straight down there is no such plane.
   */
  beginHeight(pointer: { clientX: number; clientY: number; pointerId: number; pointerType?: string }): boolean {
    let item = this.level?.items.find(candidate => candidate.id === this.highlighted);
    if (!this.active || !this.editingEnabled || !item || item.locked || item.placement === 'wall') return false;
    if (this.beginTouch(pointer, true) || this.touchNavigation) return false;
    this.interactionHandlers.onEditPrepare?.();
    item = this.level?.items.find(value => value.id === item!.id);
    if (!item) return false;
    this.settleAppearances();
    this.cancelInteraction();
    const centre = new THREE.Vector3(item.position.x, item.elevation + item.size.h / 2, item.position.z);
    const facing = new THREE.Vector3().subVectors(this.viewCamera.position, centre).setY(0);
    if (facing.lengthSq() < 1e-6) return false;
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(facing.normalize(), centre);
    const hit = this.rayAt(pointer.clientX, pointer.clientY).intersectPlane(plane, new THREE.Vector3());
    if (!hit) return false;
    this.pointer = { intent: new ScenePointerIntent(pointer.pointerId, pointer.clientX, pointer.clientY, 'height'),
      previous: { x: pointer.clientX, y: pointer.clientY }, base: this.level!, item, planeY: 0, grab: { x: 0, z: 0 }, reach: Infinity, rotationOffset: 0,
      height: { plane, y0: hit.y, start: item.elevation, max: Math.max(item.elevation, (this.level?.height ?? 3) - item.size.h) } };
    this.controls.enabled = false;
    this.capturePointer(pointer.pointerId); this.applyCursor('height', false);
    return true;
  }

  itemResizeBounds(item: Item): ResizeBounds {
    const node = this.nodeFor(`item:${item.id}`);
    if (!node?.userData.real) return fallbackResizeBounds(item);
    const indexed = this.index?.localBounds(node.name);
    if(indexed&&!indexed.isEmpty())return {min:{x:indexed.min.x,y:indexed.min.y,z:indexed.min.z},max:{x:indexed.max.x,y:indexed.max.y,z:indexed.max.z}};
    const key=node.scale.toArray().join(','),cached=this.resizeBoundsCache?.get(node);
    if(cached?.key===key)return cached.bounds;
    const copy=cloneFullModel(node); copy.position.set(0,0,0);copy.rotation.set(0,0,0);
    const box=new THREE.Box3().setFromObject(copy);
    const bounds=box.isEmpty()?fallbackResizeBounds(item):{min:{x:box.min.x,y:box.min.y,z:box.min.z},max:{x:box.max.x,y:box.max.y,z:box.max.z}};
    this.resizeBoundsCache??=new WeakMap();this.resizeBoundsCache.set(node,{key,bounds});return bounds;
  }

  beginResize(face: ResizeFace, pointer: {clientX:number;clientY:number;pointerId:number;pointerType?:string}): boolean {
    let item=this.level?.items.find(it=>it.id===this.highlighted);
    if(!item||item.locked||!this.active||!this.editingEnabled||!this.resizeMode)return false;
    if(this.beginTouch(pointer,true)||this.touchNavigation)return false;
    this.interactionHandlers.onEditPrepare?.();
    item=this.level?.items.find(value=>value.id===item!.id);
    if(!item)return false;
    this.settleAppearances();this.cancelInteraction();
    const bounds=this.itemResizeBounds(item), axis=new THREE.Vector3().copy(resizeAxisVector(item,face.axis));
    const origin=resizeFaceCenter(item,bounds,face),ray=this.rayAt(pointer.clientX,pointer.clientY);
    const start=axisRayParameter(ray.origin,ray.direction,origin,axis);if(start===null)return false;
    this.pointer={intent:new ScenePointerIntent(pointer.pointerId,pointer.clientX,pointer.clientY,'resize'),previous:{x:pointer.clientX,y:pointer.clientY},
      base:this.level!,item,planeY:0,grab:{x:0,z:0},reach:Infinity,rotationOffset:0,
      resize:{face,bounds,origin,axis,start}};
    // The captured canvas shows the cursor from here on: the handle's axis, not the resting arrow.
    this.controls.enabled=false;this.capturePointer(pointer.pointerId);this.applyCursor('resize',false);return true;
  }

  private pointerMove(e: PointerEvent): void {
    const session = this.pointer;
    if (!session || session.intent.pointerId !== e.pointerId) return;
    const wasActive = session.intent.active;
    if (!session.intent.update(e.clientX, e.clientY)) { e.stopImmediatePropagation(); return; }
    this.requestFrame();
    if (!wasActive) {
      this.interactionHandlers.onGesture?.(session.intent.kind);
      if (session.intent.kind !== 'camera' && this.interactionHandlers.onEditBegin?.() === false) {
        this.finishInteraction(true); e.stopImmediatePropagation(); return;
      }
      this.interacting = true;
    }
    if (session.intent.kind === 'camera') {
      if (this.mode === 'walk') this.turnWalk(e.clientX - session.previous.x, e.clientY - session.previous.y);
      else if (this.wallPlan) this.panWallPlan(e.clientX - session.previous.x, e.clientY - session.previous.y);
    } else {
      e.stopImmediatePropagation(); e.preventDefault();
      if (session.openingResize && session.base) {
        const resize = session.openingResize, hit = this.rayAt(e.clientX, e.clientY).intersectPlane(resize.plane, new THREE.Vector3());
        if (hit) {
          const target = { t: alongWall(resize.wall, { x: hit.x, z: hit.z }) - resize.grab.t, h: hit.y - resize.grab.h };
          const result = previewOpeningEdge(session.base, resize.id, resize.edge, target, { scale: this.metresPerPixel(hit), free: e.ctrlKey || e.metaKey });
          if (result.level) this.interactionHandlers.onEditPreview?.(result.level);
        }
        this.applyCursor('resize', false); session.previous = { x: e.clientX, y: e.clientY }; return;
      }
      if(session.resize && session.base && session.item){
        const ray=this.rayAt(e.clientX,e.clientY),sample=axisRayParameter(ray.origin,ray.direction,session.resize.origin,session.resize.axis);
        if(sample!==null)this.interactionHandlers.onEditPreview?.(previewItemResize(session.base,session.item.id,session.resize.bounds,session.resize.face,sample-session.resize.start));
        this.applyCursor('resize',false);session.previous={x:e.clientX,y:e.clientY};return;
      }
      if (session.height && session.base && session.item) {
        const hit = this.rayAt(e.clientX, e.clientY).intersectPlane(session.height.plane, new THREE.Vector3());
        if (hit) {
          const elevation = Math.round(Math.max(0, Math.min(session.height.max, session.height.start + hit.y - session.height.y0)) * 1000) / 1000;
          const item = session.item;
          this.interactionHandlers.onEditPreview?.(previewSceneItemTransform(session.base, item.id, { position: item.position, rotation: item.rotation, elevation }, this.surfaceProbe));
        }
        this.applyCursor('height', false); session.previous = { x: e.clientX, y: e.clientY }; return;
      }
      if (session.group && session.base && session.item) {
        // The group slides as one across the plane the pressed item stands on; Shift locks an axis.
        const hit = this.horizontalHit(e.clientX, e.clientY, session.planeY, session.reach);
        if (hit) {
          const raw = { x: hit.x - session.grab.x - session.item.position.x, z: hit.z - session.grab.z - session.item.position.z };
          const locked = constrainMove(raw, { x: 0, z: 0 }, e.shiftKey, session.axis);
          session.axis = e.shiftKey ? locked.axis : undefined;
          this.interactionHandlers.onEditPreview?.(moveItemGroup(session.base, session.group, locked.point, this.surfaceProbe));
        }
        this.applyCursor(session.intent.kind, false); session.previous = { x: e.clientX, y: e.clientY }; return;
      }
      if (session.opening && session.base) {
        const level = this.wallPlanOpeningMove(session, e);
        if (level) this.interactionHandlers.onEditPreview?.(level);
        this.applyCursor('move', false); session.previous = { x: e.clientX, y: e.clientY }; return;
      }
      const transform = this.dragTransform(session, e);
      if (transform && session.base && session.item) {
        // Alt while dragging drops a copy and leaves the original in place; letting go of Alt
        // before the drop turns it back into a plain move. Both preview from the same base,
        // so the whole gesture stays one undo step.
        const copying = !!(e.altKey && session.gesture);
        session.copy = copying;
        const ray = this.rayAt(e.clientX, e.clientY);
        const level = session.gesture
          ? previewItemMove(session.gesture, transform.position, copying, this.interactionHandlers.copyIdentity && (() => this.interactionHandlers.copyIdentity!(session.gesture!.sourceId)), { pointer: { kind: 'ray',
            origin: { x: ray.origin.x, y: ray.origin.y, z: ray.origin.z }, direction: { x: ray.direction.x, y: ray.direction.y, z: ray.direction.z } },
            probe: this.surfaceProbe, footprints: session.footprints, grab: session.grab }).level
          : previewSceneItemTransform(session.base, session.item.id, transform, this.surfaceProbe);
        this.interactionHandlers.onEditPreview?.(level);
      }
      session.previous = { x: e.clientX, y: e.clientY };
      this.applyCursor(session.intent.kind, e.altKey);
      return;
    }
    session.previous = { x: e.clientX, y: e.clientY };
  }

  private finishInteraction(cancelled: boolean, event?: PointerEvent): void {
    const session = this.pointer;
    if (!session) return;
    if (!cancelled && event && session.intent.kind !== 'camera') {
      this.pointerMove(event);
      if (this.pointer !== session) return;
    }
    this.pointer = null;
    if (cancelled && session.intent.kind === 'camera' && typeof PointerEvent !== 'undefined') {
      this.canvas.dispatchEvent(new PointerEvent('pointercancel', { pointerId: session.intent.pointerId, bubbles: true }));
    }
    if (this.canvas?.hasPointerCapture(session.intent.pointerId)) this.canvas.releasePointerCapture(session.intent.pointerId);
    this.controls.enabled = this.controlsAllowed();
    this.interacting = false;
    this.walk.dragging = false;
    if (session.intent.active && session.intent.kind !== 'camera') { this.interactionHandlers.onEditEnd?.(cancelled); this.settleSlopes(); }
    if (!cancelled && session.intent.active && session.copy && session.gesture?.copyId) this.interactionHandlers.onCopyPlaced?.(session.gesture.copyId);
    if (!cancelled && session.intent.active && session.held) this.interactionHandlers.onLockedStayed?.(session.held);
    this.applyCursor(null, this.altHeld);
    this.interactionHandlers.onGesture?.(null);
    if (this.wallInsetsPending) { this.wallInsetsPending = false; this.applyWallPlanCamera(); }
    if (!cancelled && !session.secondary && !session.intent.active && session.intent.kind !== 'rotate' && session.intent.kind !== 'resize' && event && !spaceHeld()) {
      const rect = this.canvas.getBoundingClientRect();
      const hit=this.pick(event.clientX-rect.left,event.clientY-rect.top);
      // In the walk the second tap of a double tap walks there and picks nothing: the first one has selected.
      if (this.mode === 'walk' && !event.shiftKey && this.walkDoubleTap(event, hit)) { this.overlaySignature = ''; this.requestFrame(); return; }
      this.interactionHandlers.onPick?.(hit, { x: event.clientX, y: event.clientY },this.pickedMaterial, { shiftKey: event.shiftKey });
    }
    this.overlaySignature = ''; this.requestFrame();
  }

  private rayAt(clientX: number, clientY: number): THREE.Ray {
    const rect = this.canvas.getBoundingClientRect(), caster = new THREE.Raycaster(), camera = this.viewCamera;
    camera.updateMatrixWorld();
    caster.setFromCamera(new THREE.Vector2((clientX - rect.left) / rect.width * 2 - 1, -(clientY - rect.top) / rect.height * 2 + 1), camera);
    return caster.ray;
  }

  private horizontalHit(x: number, y: number, planeY: number, reach = Infinity) {
    const ray = this.rayAt(x, y);
    return rayPlanePoint(ray.origin, ray.direction, planeY, reach);
  }

  private selectedItemAt(x: number, y: number): Item | null {
    const item = this.level?.items.find(candidate => candidate.id === this.highlighted);
    if (!item || item.locked) return null;
    const root = this.nodeFor(`item:${item.id}`);
    if (!root || !root.visible) return null;
    const rect = this.canvas.getBoundingClientRect(), hit = this.pick(x - rect.left, y - rect.top);
    if (hit?.userData.itemId === item.id) return item;
    const ray = this.rayAt(x, y), box = new THREE.Box3().setFromObject(root), point = ray.intersectBox(box, new THREE.Vector3());
    if (!point) return null;
    if (hit) {
      const caster = new THREE.Raycaster(ray.origin, ray.direction);
      const obstacle = caster.intersectObject(hit, false)[0];
      if (obstacle && obstacle.distance < ray.origin.distanceTo(point) - .01) return null;
    }
    return item;
  }

  /** The wall face the pointer is on. Of two copies of a shared wall, the one whose front faces the ray, as `nearestWallSpot` picks. */
  private wallHit(x: number, y: number, level: Level): { point: THREE.Vector3; wall: Wall; normal: Vec2; direction: Vec2 } | null {
    type Hit = { point: THREE.Vector3; wall: Wall; normal: Vec2; direction: Vec2 };
    const ray = this.rayAt(x, y), fronts = new Map<string, Hit>();
    let nearest = Infinity, result: Hit | null = null;
    for (const wall of level.walls) for (const side of [-1, 1]) {
      const direction = { x: (wall.end.x - wall.start.x) / wall.length, z: (wall.end.z - wall.start.z) / wall.length };
      const normal = { x: -direction.z * side, z: direction.x * side }, n = new THREE.Vector3(normal.x, 0, normal.z);
      if (ray.direction.dot(n) >= -.05) continue;
      const origin = new THREE.Vector3(wall.start.x + normal.x * wall.thickness / 2, 0, wall.start.z + normal.z * wall.thickness / 2);
      const point = ray.intersectPlane(new THREE.Plane().setFromNormalAndCoplanarPoint(n, origin), new THREE.Vector3());
      if (!point || point.y < -.3 || point.y > wall.height + .3) continue;
      const along = (point.x - wall.start.x) * direction.x + (point.z - wall.start.z) * direction.z;
      if (along < -.3 || along > wall.length + .3) continue;
      const hit = { point, wall, normal, direction }, distance = point.distanceTo(ray.origin);
      if (normal.x * wall.normalIn.x + normal.z * wall.normalIn.z > 0) fronts.set(wall.id, hit);
      if (distance < nearest) { nearest = distance; result = hit; }
    }
    if (result && !fronts.has(result.wall.id)) return wallCopies(level, result.wall).map(copy => fronts.get(copy.id)).find(Boolean) ?? result;
    return result;
  }

  private dragTransform(session: PointerSession, e: PointerEvent): SceneItemTransform | null {
    const item = session.item!, base = session.base!;
    if (session.intent.kind === 'rotate') {
      const hit = this.horizontalHit(e.clientX, e.clientY, session.planeY);
      if (!hit) return null;
      return { position: item.position, rotation: snapSceneRotation(Math.atan2(hit.z - item.position.z, hit.x - item.position.x)
        + session.rotationOffset, e.ctrlKey || e.metaKey) };
    }
    if (this.wallPlan) return this.wallPlanTransform(session, item, base, e);
    if (item.placement === 'wall') {
      const hit = this.wallHit(e.clientX, e.clientY, base);
      if (!hit || item.size.w > hit.wall.length) return null;
      const wall = hit.wall, n = hit.normal, dir = hit.direction;
      const along = clamp((hit.point.x - wall.start.x) * dir.x + (hit.point.z - wall.start.z) * dir.z
        - (session.wallGrab?.along ?? 0), item.size.w / 2, wall.length - item.size.w / 2);
      // On its own face the item keeps the file's turn while it is the face's: worked out again, float noise reads as a turn in a live room.
      const faceYaw = Math.atan2(-n.x, n.z);
      const ownFace = wall.id === item.wallId && (item.position.x - wall.start.x) * n.x + (item.position.z - wall.start.z) * n.z > 0 && sameYaw(item.rotation, faceYaw);
      return { position: { x: wall.start.x + dir.x * along + n.x * (wall.thickness / 2 + item.size.d / 2 + .005),
        z: wall.start.z + dir.z * along + n.z * (wall.thickness / 2 + item.size.d / 2 + .005) },
        elevation: clamp(hit.point.y - (session.wallGrab?.y ?? item.size.h / 2), 0, Math.max(0, wall.height - item.size.h)),
        rotation: ownFace ? item.rotation : faceYaw, wallId: wall.id };
    }
    const shelf = this.shelfPointAt(item, e.clientX, e.clientY, { level: base, grab: session.shelfGrab ?? session.grab,
      footprints: session.footprints, currentElevation: this.level?.items.find(value => value.id === item.id)?.elevation });
    let hit = shelf ?? this.horizontalHit(e.clientX, e.clientY, session.planeY, session.reach);
    if (!hit && session.meshSeed) {
      // Leaving an upper shelf can turn the ray downward, behind its initial grab plane.
      // The floor takes over then; at the horizon keep the original mesh depth instead.
      hit = this.horizontalHit(e.clientX, e.clientY, 0, session.reach);
      if (!hit) {
        const ray = this.rayAt(e.clientX, e.clientY), { point, normal } = session.meshSeed;
        const denominator = ray.direction.x * normal.x + ray.direction.z * normal.z;
        const distance = Math.abs(denominator) > 1e-6
          ? ((point.x - ray.origin.x) * normal.x + (point.z - ray.origin.z) * normal.z) / denominator : -1;
        if (distance > 0) hit = ray.at(Math.min(distance, session.reach), new THREE.Vector3());
      }
    }
    if (!hit) return null;
    const room = base.rooms.find(candidate => containsPoint(candidate.polygon, hit));
    if (room) session.roomIndex = room.index;
    const free = shelf ?? { x: hit.x - session.grab.x, z: hit.z - session.grab.z };
    const locked = constrainMove(free, session.origin ?? item.position, e.shiftKey, session.axis);
    session.axis = e.shiftKey ? locked.axis : undefined;
    const raw = locked.point;
    const snapped = smartSnapItem(base, item.id, raw, { scale: this.snapScale(raw, session.planeY), disabled: e.ctrlKey || e.metaKey, origin: session.origin ?? item.position,
      surface: item.placement === 'ceiling' ? 'ceiling' : 'floor', footprints: session.footprints, previous: session.captures, probe: this.surfaceProbe });
    session.captures = snapped.captures; session.guides = snapped.guides;
    const position = snapped.position;
    const home = base.rooms.find(candidate => candidate.index === session.roomIndex);
    const previous = this.level?.items.find(candidate => candidate.id === item.id)?.position ?? item.position;
    return { position: home ? constrainItemToRoom({ ...item, position }, home.polygon, session.footprints, previous) : position, rotation: item.rotation };
  }

  /**
   * Metres per CSS pixel where the item is being dragged. The magnets then reach the same
   * handful of pixels the plan uses, instead of a fixed distance in metres that is a mile
   * wide up close and unreachable from across the room.
   */
  private snapScale(point: Vec2, planeY: number): number {
    const height = this.canvas.clientHeight || this.canvas.height || 1;
    const distance = this.camera.position.distanceTo(new THREE.Vector3(point.x, planeY, point.z));
    const perPixel = 2 * distance * Math.tan(this.camera.fov * Math.PI / 360) / height;
    return clamp(perPixel, .001, .02);
  }

  private gestureFootprints(level: Level): Footprints {
    const result = new Map();
    for (const item of level.items) {
      let box = this.index?.localBounds(`item:${item.id}`);
      if (!box) {
        const node = this.nodeFor(`item:${item.id}`); if (!node) continue;
        const copy = cloneFullModel(node); copy.position.set(0, 0, 0); copy.rotation.set(0, 0, 0);
        box = new THREE.Box3().setFromObject(copy);
      }
      if (!box.isEmpty()) result.set(item.id, { minX: box.min.x, maxX: box.max.x, minZ: box.min.z, maxZ: box.max.z });
    }
    return result;
  }

  private updateOverlay(): void {
    const item = this.level?.items.find(candidate => candidate.id === this.highlighted);
    const root = item && this.nodeFor(`item:${item.id}`);
    let overlay: ViewerOverlay | null = null;
    if (item && root?.visible && this.active && this.editingEnabled && this.pointer?.intent.kind !== 'camera') {
      const rect = this.canvas.getBoundingClientRect(), markerY = item.elevation + Math.min(item.size.h / 2, .6);
      const screen = (point: THREE.Vector3) => {
        point.project(this.viewCamera);
        return point.z > -1 && point.z < 1 ? { x: rect.left + (point.x + 1) * rect.width / 2,
          y: rect.top + (1 - point.y) * rect.height / 2 } : null;
      };
      const center = screen(new THREE.Vector3(item.position.x, markerY, item.position.z));
      const distance = item.size.d / 2 + .16;
      const rotate = this.wallPlan || item.placement === 'wall' || (item.elevation >= .5 && item.size.d <= .25) ? null
        : screen(new THREE.Vector3(item.position.x - Math.sin(item.rotation) * distance, markerY,
          item.position.z + Math.cos(item.rotation) * distance));
      const box = this.index?.bounds(root.name) ?? new THREE.Box3().setFromObject(root), points: { x: number; y: number }[] = [];
      for (let i = 0; i < 8; i++) {
        const point = screen(new THREE.Vector3(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z));
        if (point) points.push(point);
      }
      if (center && points.length) {
        const x = Math.min(...points.map(p => p.x)), y = Math.min(...points.map(p => p.y));
        overlay = { center, rotate:this.resizeMode?null:rotate, frame: { x, y, width: Math.max(...points.map(p => p.x)) - x, height: Math.max(...points.map(p => p.y)) - y } };
        // The app hides its height button once the camera looks down more steeply than about 49° (forward·up ≤ −0.75).
        const steep = this.viewCamera.getWorldDirection(new THREE.Vector3()).y <= -.75;
        if (!this.resizeMode && !this.wallPlan && item.placement !== 'wall' && !steep) overlay.height = rotate ? { x: rotate.x + 52, y: rotate.y } : { x: center.x, y: center.y + 56 };
        if(this.resizeMode){
          const bounds=this.itemResizeBounds(item);overlay.resize=[];overlay.labels=[];
          // The oriented box itself: eight corners of the local resize bounds, in world space.
          const c=Math.cos(item.rotation),sn=Math.sin(item.rotation);
          const corner=(i:number)=>{const x=i&1?bounds.max.x:bounds.min.x,y=i&2?bounds.max.y:bounds.min.y,z=i&4?bounds.max.z:bounds.min.z;
            return screen(new THREE.Vector3(item.position.x+x*c-z*sn,item.elevation+y,item.position.z+x*sn+z*c));};
          const pts=Array.from({length:8},(_,i)=>corner(i));
          // A face looks at the camera when the eye stands on its outer side.
          const facing=(axis:ResizeFace['axis'],side:-1|1)=>new THREE.Vector3().copy(resizeAxisVector(item,axis)).multiplyScalar(side)
            .dot(this.viewCamera.position.clone().sub(resizeFaceCenter(item,bounds,{axis,side})))>.005;
          for(const axis of ['w','d','h'] as const)for(const side of [-1,1] as const){
            if(axis==='h' && ((item.placement==='floor'&&side===-1)||(item.placement==='ceiling'&&side===1)))continue;
            if(item.placement==='wall'&&axis==='d'&&side===-1)continue;
            // A mansard's back stays on its wall: the app drags only its depth and its height (AtticIsJustAdded).
            if(isAttic(item)&&axis==='d'&&side===(Math.abs(bounds.min.z)<Math.abs(bounds.max.z)?-1:1))continue;
            const face={axis,side},position=resizeFaceCenter(item,bounds,face),normal=new THREE.Vector3().copy(resizeAxisVector(item,axis)).multiplyScalar(side);
            const active=this.pointer?.resize?.face.axis===axis&&this.pointer.resize.face.side===side;
            if(this.pointer?.intent.active&&!active)continue;
            if(!active&&!facing(axis,side))continue;
            const p=screen(position.clone()),along=screen(position.clone().addScaledVector(normal,.1));
            if(p&&along){
              const ray=this.rayAt(p.x,p.y);
              if(!active&&axisRayParameter(ray.origin,ray.direction,position,normal)===null)continue;
              const length=Math.hypot(along.x-p.x,along.y-p.y)||1,dir={x:(along.x-p.x)/length,y:(along.y-p.y)/length};
              const outline=RESIZE_FACE_CORNERS[axis].map(i=>pts[side===1?i|RESIZE_AXIS_BIT[axis]:i]);
              overlay.resize.push({...face,...p,angle:Math.atan2(along.y-p.y,along.x-p.x)*180/Math.PI+90,active:!!active,dir,
                outline:outline.every(point=>point)?outline as Array<{x:number;y:number}>:[]});
            }
          }
          // On a small or distant item the handles would cover each other: each moves out along its own axis.
          separateHandles(overlay.resize,RESIZE_HANDLE_CLEAR);
          // The axis's size beside its handle, out along the face's normal and clear of the handle's disc.
          for(const handle of overlay.resize)if(!overlay.labels.some(label=>label.axis===handle.axis))
            overlay.labels.push({x:handle.x+handle.dir.x*RESIZE_LABEL_GAP,y:handle.y+handle.dir.y*RESIZE_LABEL_GAP,dir:handle.dir,axis:handle.axis,text:tr('#ValueCentimeters',Math.round(item.size[handle.axis]*100))});
          overlay.box=[];
          for(let i=0;i<8;i++)for(const bit of [1,2,4] as const){const j=i|bit;if(j===i)continue;const a=pts[i],b=pts[j];
            if(!a||!b)continue;
            // Hidden: both faces this edge borders turn away from the camera.
            const hidden=([1,2,4] as const).filter(other=>other!==bit).every(other=>!facing(RESIZE_BIT_AXIS[other],i&other?1:-1));
            overlay.box.push({x1:a.x,y1:a.y,x2:b.x,y2:b.y,hidden});}
          // Each size as a dimension along an outline edge of its axis; while a face is dragged, only its own,
          // with how far it has come and the box it started from.
          const drag=this.pointer?.intent.active?this.pointer.resize&&this.pointer.item:undefined;
          const shown=pts.filter((point):point is {x:number;y:number}=>!!point);
          const middle={x:shown.reduce((sum,point)=>sum+point.x,0)/(shown.length||1),y:shown.reduce((sum,point)=>sum+point.y,0)/(shown.length||1)};
          overlay.dims=[];
          for(const axis of ['w','d','h'] as const){
            if(drag&&this.pointer?.resize?.face.axis!==axis)continue;
            const bit=RESIZE_AXIS_BIT[axis],others=([1,2,4] as const).filter(other=>other!==bit),candidates:NonNullable<ViewerOverlay['dims']>[number]['candidates']=[];
            for(const base of [0,others[0],others[1],others[0]|others[1]]){
              const a=pts[base],b=pts[base|bit];if(!a||!b)continue;
              const [one,two]=others.map(other=>facing(RESIZE_BIT_AXIS[other],base&other?1:-1));
              const length=Math.hypot(b.x-a.x,b.y-a.y);
              if(one===two||length<RESIZE_DIM_MIN)continue;
              let n={x:-(b.y-a.y)/length,y:(b.x-a.x)/length};
              if(((a.x+b.x)/2-middle.x)*n.x+((a.y+b.y)/2-middle.y)*n.y<0)n={x:-n.x,y:-n.y};
              candidates.push({x1:a.x,y1:a.y,x2:b.x,y2:b.y,n});
            }
            // Width and depth read best under the item, height beside it.
            candidates.sort(axis==='h'?(p,q)=>(q.x1+q.x2)-(p.x1+p.x2):(p,q)=>(q.y1+q.y2)-(p.y1+p.y2));
            const delta=drag?Math.round(item.size[axis]*100)-Math.round(drag.size[axis]*100):undefined;
            if(candidates.length)overlay.dims.push({axis,text:tr('#ValueCentimeters',Math.round(item.size[axis]*100)),...(delta!==undefined?{delta}:{}),candidates});
          }
          if(drag){
            const start=this.pointer!.resize!.bounds,sc=Math.cos(drag.rotation),ss=Math.sin(drag.rotation);
            const was=(i:number)=>{const x=i&1?start.max.x:start.min.x,y=i&2?start.max.y:start.min.y,z=i&4?start.max.z:start.min.z;
              return screen(new THREE.Vector3(drag.position.x+x*sc-z*ss,drag.elevation+y,drag.position.z+x*ss+z*sc));};
            const corners=Array.from({length:8},(_,i)=>was(i));overlay.ghost=[];
            for(let i=0;i<8;i++)for(const bit of [1,2,4]){const j=i|bit;if(j===i)continue;const a=corners[i],b=corners[j];if(a&&b)overlay.ghost.push({x1:a.x,y1:a.y,x2:b.x,y2:b.y});}
          }
        }
      }
    }
    if (!overlay && !item && this.gathered?.length && !this.wallPlan && this.active && this.editingEnabled && this.pointer?.intent.kind !== 'camera') overlay = this.gatheredOverlay();
    if (!overlay && !item && this.resizeMode && this.active && this.editingEnabled && this.pointer?.intent.kind !== 'camera') overlay = this.openingResizeOverlay();
    if (!overlay && !item && this.active && this.editingEnabled && this.pointer?.intent.kind !== 'camera') overlay = this.wallPlanOpeningOverlay();
    // Leave the full touch target clear even though the visible rotation button is smaller.
    if (overlay?.rotate) overlay.keepOut = [{ x: overlay.rotate.x - 22, y: overlay.rotate.y - 22, width: 44, height: 44 }];
    // The wall plan is a flat drawing: the bar goes above the selected thing instead of over it.
    if (overlay && this.wallPlan) overlay.keepOut = [{ x: overlay.frame.x - 6, y: overlay.frame.y - 6, width: overlay.frame.width + 12, height: overlay.frame.height + 12 }];
    // The resize bar goes round the handles' touch zones and the sizes beside them.
    if (overlay?.opening) overlay.keepOut = [...(overlay.keepOut ?? []),
      ...overlay.opening.handles.map(handle => ({ x: handle.x - 22, y: handle.y - 22, width: 44, height: 44 })),
      ...overlay.opening.dims.map(dim => { const at = openingChipPoint(dim.line, dim.text), width = 29 + 7 * dim.text.length;
        return { x: at.x - width / 2, y: at.y - 12, width, height: 24 }; })];
    if (overlay?.resize?.length) overlay.keepOut = [...(overlay.keepOut ?? []),
      ...overlay.resize.map(handle => ({ x: handle.x - 22, y: handle.y - 22, width: 44, height: 44 })),
      ...(overlay.labels ?? []).flatMap(label => [resizeLabelRect(label), resizeLabelRect(flippedResizeLabel(label))]),
      ...(overlay.dims ?? []).flatMap(dim => dim.candidates.map(line => resizeChipRect(line, dim.text)))];
    const signature = JSON.stringify(overlay);
    if (signature !== this.overlaySignature) { this.overlaySignature = signature; this.interactionHandlers.onOverlay?.(overlay); }
  }

  /** The screen frame around every visible gathered item: where the context bar sits. */
  private gatheredOverlay(): ViewerOverlay | null {
    const rect = this.canvas.getBoundingClientRect(), points: { x: number; y: number }[] = [];
    for (const id of this.gathered) {
      const root = this.nodeFor(`item:${id}`);
      if (!root?.visible) continue;
      const box = this.index?.bounds(root.name) ?? new THREE.Box3().setFromObject(root);
      for (let i = 0; i < 8; i++) {
        const point = new THREE.Vector3(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z).project(this.viewCamera);
        if (point.z > -1 && point.z < 1) points.push({ x: rect.left + (point.x + 1) * rect.width / 2, y: rect.top + (1 - point.y) * rect.height / 2 });
      }
    }
    if (!points.length) return null;
    const x = Math.min(...points.map(p => p.x)), y = Math.min(...points.map(p => p.y));
    const width = Math.max(...points.map(p => p.x)) - x, height = Math.max(...points.map(p => p.y)) - y;
    // The bar goes beside the group: over it, it would hide the next piece to be gathered.
    return { center: { x: x + width / 2, y: y + height / 2 }, rotate: null, frame: { x, y, width, height }, keepOut: [{ x, y, width, height }] };
  }

  /** OrbitControls suppresses this itself while it is enabled; walk mode has it disabled, and a
   * menu popping up mid-drag would abandon the look. */
  private onContextMenu(e: Event): void { if (this.active) e.preventDefault(); }

  private turnWalk(horizontal: number, vertical: number): void {
    this.walk.yaw -= horizontal * 0.0032;
    this.walk.pitch = clamp(this.walk.pitch - vertical * 0.0032, -1.4, 1.4);
    this.applyWalk();
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.active) return;
    if (e.code === 'AltLeft' || e.code === 'AltRight') { this.altHeld = true; this.applyCursor(this.pointer?.intent.active ? this.pointer.intent.kind : null, true); }
    if (e.code === 'Escape') {
      // Escape that ends a gesture or the resize mode is spent: the editor must not also drop the selection.
      if (this.pointer?.intent.active || this.resizeMode) e.preventDefault();
      this.cancelInteraction(); this.settleAppearances(); if(this.resizeMode)this.interactionHandlers.onResizeModeEnd?.(); return;
    }
    const target = e.target as HTMLElement | null;
    if (target?.isContentEditable || target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') return;
    // macOS sends no keyup for a letter released while ⌘ is down: ⌘A or ⌘D would leave the camera sliding.
    if ((e.metaKey || e.ctrlKey) && !e.code.startsWith('Shift')) return;
    if (['KeyW','KeyA','KeyS','KeyD','ArrowUp','ArrowDown','ArrowLeft','ArrowRight','ShiftLeft','ShiftRight'].includes(e.code)) {
      if (!e.code.startsWith('Shift')) this.interactionHandlers?.onNavigationIntent?.();
      this.walk.keys.add(e.code); this.requestFrame();
    }
  };
  private onKeyUp = (e: KeyboardEvent): void => {
    if (e.code === 'AltLeft' || e.code === 'AltRight') { this.altHeld = false; this.applyCursor(this.pointer?.intent.active ? this.pointer.intent.kind : null, false); }
    this.walk.keys.delete(e.code); this.requestFrame();
  };

  /**
   * Hover pick on idle pointer moves, throttled: it feeds only the cursor, and a raycast
   * on every mousemove is noticeable on a 500-mesh scene.
   */
  private trackHover(e: PointerEvent): void {
    if (!this.active) return;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now - this.hoverAt < 40) return;
    this.hoverAt = now;
    let kind: 'none' | 'item' = 'none', selected = false, opening = false;
    if (this.editingEnabled && this.mode !== 'walk') {
      const rect = this.canvas.getBoundingClientRect();
      const hit = this.pick(e.clientX - rect.left, e.clientY - rect.top);
      const id = hit?.userData?.itemId as string | undefined;
      const openingId = this.wallPlan ? hit?.userData?.openingId as string | undefined : undefined;
      if (id) { kind = 'item'; selected = id === this.highlighted; }
      else if (openingId) { kind = 'item'; selected = openingId === this.selectedOpening; }
      opening = !id && !!openingId;
    }
    if (kind !== this.hoverKind || selected !== this.hoverSelected || e.altKey !== this.altHeld || opening !== this.hoverOpening) {
      this.hoverKind = kind; this.hoverSelected = selected; this.altHeld = e.altKey; this.hoverOpening = opening;
      this.applyCursor(null, e.altKey);
    }
  }

  /** The canvas cursor says what a press would do (see cursors.ts). */
  private applyCursor(gesture: 'move' | 'rotate' | 'resize' | 'height' | 'camera' | null, alt: boolean): void {
    const style = (this.canvas as { style?: CSSStyleDeclaration }).style;
    if (!style) return;
    const cursor = cursorStyle(sceneCursor({ mode: this.mode, gesture, hover: this.hoverKind, selectedHover: this.hoverSelected, alt: alt && !this.hoverOpening && !this.pointer?.opening,
      editing: this.editingEnabled && !this.resizeMode, space: spaceHeld(), wallPlan: !!this.wallPlan, resizeAngle: gesture === 'resize' ? this.resizeCursorAngle() : undefined,
      rotateAngle: gesture === 'rotate' ? this.rotateCursorAngle() : undefined }));
    if (this.appliedCursor !== cursor) { this.appliedCursor = cursor; style.cursor = cursor; }
  }

  /** Where the pointer turning an item stands from the item's centre on screen, past the rotate glyph's own corner. */
  private rotateCursorAngle(): number | undefined {
    const session = this.pointer, item = session?.item;
    if (!session || !item) return undefined;
    const rect = this.canvas.getBoundingClientRect(), centre = TURN_CENTRE.set(item.position.x, session.planeY, item.position.z).project(this.viewCamera);
    return rotateGlyphAngle(rect.left + (centre.x + 1) / 2 * rect.width, rect.top + (1 - centre.y) / 2 * rect.height, session.previous.x, session.previous.y);
  }

  /** The dragged face's handle angle on screen (its axis at +90°, as the handles take it), for the resize cursor. */
  private resizeCursorAngle(): number | undefined {
    if (this.pointer?.openingResize) return this.pointer.openingResize.angle;
    const resize = this.pointer?.resize;
    if (!resize) return undefined;
    const rect = this.canvas.getBoundingClientRect(), from = resize.origin.clone().project(this.viewCamera);
    const to = resize.origin.clone().addScaledVector(resize.axis, .1).project(this.viewCamera);
    return Math.atan2(-(to.y - from.y) * rect.height, (to.x - from.x) * rect.width) * 180 / Math.PI + 90;
  }

  private applyWalk(): void {
    this.camera.position.copy(this.walk.pos);
    this.camera.rotation.set(0, 0, 0);
    // A three.js camera looks down its local −Z, so Ry(a) aims it at (−sin a, −cos a).
    // The project's yaw means a heading of (sin yaw, cos yaw) — exactly the negation,
    // hence the half turn. Without it the saved camera faces the opposite wall.
    this.camera.rotateY(this.walk.yaw + Math.PI);
    this.camera.rotateX(this.walk.pitch);
  }

  /** The project's saved camera when the app saved it walking: another view's height is not an eye's. */
  private get walkCamera(): HPScene['camera'] { const camera = this.hp?.camera; return camera?.view === 'WalkView' ? camera : undefined; }

  private get walkEyeHeight(): number { return this.walkCamera?.height ?? 1.6; }

  private resetWalkSupport(height = 0): void {
    const sample = this.stairSupport(), ground = sample.floor(this.walk.pos);
    // Nothing stands under the floor but in a stairwell, where the floor below is the floor.
    height = Math.max(height, ground);
    const sampled = sample(this.walk.pos, height);
    this.walkSupportY = sampled <= height + WALK_MAX_STEP ? sampled : ground;
    this.walk.pos.y = this.walkSupportY + this.walkEyeHeight;
    this.applyWalk();
  }

  /**
   * A walker put down somewhere rather than walked there — the walk entered, a room chosen, another floor chosen — does
   * not land in a stairwell of the floor in view: it stands on the floor just past the edge of the hole it would be in.
   */
  private leaveWell(): void {
    const levels = this.hp?.levels, index = this.levelIndex ?? 0;
    const well = levels && wellAt(wellsDown(levels, index), this.walk.pos);
    if (!well) return;
    const out = stepOutOf(well.polygon, this.walk.pos, WALK_BODY_RADIUS + .05);
    this.walk.pos.x = out.x; this.walk.pos.z = out.z;
  }

  /**
   * The end of the flight a way takes the walk to (`flightEnds`): the one with the higher treads going up, the lower going
   * down, read off the flight's model on the floor in view; without the model, the end nearest the walker.
   */
  private farEnd(way: Stairway): Vec2 {
    const ends = flightEnds(way.well), node = this.walkFlights().nodes.get(way.well.id)?.node;
    if (!ends || !node) return this.walk.pos;
    const treads = treadsOf(node), top = new THREE.Box3().setFromObject(node).max.y + .01;
    const [a, b] = ends.map(end => {
      const probe = { x: end.mid.x - end.out.x * .4, z: end.mid.z - end.out.z * .4 }, heights = treads.heights(probe.x, probe.z, top);
      return { mid: end.mid, height: heights.length ? Math.max(...heights) : undefined };
    });
    if (a.height === undefined || b.height === undefined) return this.walk.pos;
    return (a.height >= b.height) === (way.direction === 'up') ? a.mid : b.mid;
  }

  /**
   * «Go up / Go down the stairs»: the floor the nearest stairway that way leads to comes into view, through `onWalkFloor`
   * as a climb brings it. The walk is then put down at once where `stairLanding` says; the orbit keeps its distance and
   * pans to the stairs. False when there is no such stairway or the editor kept the floor, the walker where it was.
   */
  goStairs(direction: Stairway['direction']): boolean {
    const levels = this.hp?.levels, handlers = this.interactionHandlers;
    if (!levels || !handlers?.onWalkFloor || this.wallPlan) return false;
    const walking = this.mode === 'walk', from = walking ? this.walk.pos : this.controls.target;
    const distance = (way: Stairway) => Math.hypot(way.well.centre.x - from.x, way.well.centre.z - from.z);
    const way = stairwaysFrom(levels, this.levelIndex ?? 0).filter(value => value.direction === direction).sort((a, b) => distance(a) - distance(b))[0];
    const landing = way && walking ? stairLanding(levels, way, this.farEnd(way)) : undefined;
    if (!way || (walking && !landing)) return false;
    handlers.onNavigationIntent?.();
    this.settleAppearances(); this.cancelInteraction();
    if (walking) { this.stopWalkMotion(); this.walkApproach = null; }
    handlers.onWalkFloor(way.to);
    if (this.levelIndex !== way.to) return false;
    if (!landing) {
      this.panOrbit(way.well.centre.x - this.controls.target.x, way.well.centre.z - this.controls.target.z);
      return true;
    }
    this.walk.pos.x = landing.x; this.walk.pos.z = landing.z; this.walk.yaw = landing.yaw; this.walk.pitch = 0;
    this.resetWalkSupport(); this.saveCameraView(); this.requestFrame(true);
    return true;
  }

  /** The heights of a flight's faces turned up under a plan point, as a ray cast down from over its box meets them. */
  private stairSurfaces(point: Vec2, treads: Treads, bounds: THREE.Box3, floor = 0): number[] {
    const heights: number[] = [];
    for (const y of treads.heights(point.x, point.z, bounds.max.y + .01)) {
      // What is under the floor is out of reach.
      if (y >= floor - .01) heights.push(Math.max(floor, y));
    }
    return heights;
  }

  /**
   * The flights the walk can stand on, by item id: the floor's own (its built models), and the ones of the floor below
   * that lead up through the holes in its floor (built with the floors below); where one of either is not built yet, the
   * one carried along from the floor the walker came from. `waiting` lists the stairwells whose flight is not there at all.
   */
  private walkFlights(): { nodes: Map<string, { node: THREE.Object3D; item: Item }>; waiting: Set<string> } {
    const nodes = new Map<string, { node: THREE.Object3D; item: Item }>(), waiting = new Set<string>();
    const levels = this.hp?.levels ?? [], index = this.levelIndex ?? 0, level = levels[index];
    // A flight carried along stands in for the same flight only: one edited since is built again where it is now.
    const carried = (item: Item) => { const entry = this.carried?.get(item.id); return entry?.item === item ? entry.node : undefined; };
    for (const item of level?.items ?? []) {
      const tags = item.tags?.length ? item.tags : this.models?.entry?.(item.modelRef.id)?.tags ?? [];
      if (!tags.some(tag => ['Stairs', 'StairsOpening', 'CornerStairs', 'StairsStraight90'].includes(tag))) continue;
      const node = this.nodeFor(`item:${item.id}`), standing = node?.userData.real ? node : carried(item);
      if (standing) nodes.set(item.id, { node: standing, item });
      else if (levels[index + 1] && leadsUp(item, level)) waiting.add(item.id);
    }
    for (const well of wellsDown(levels, index)) {
      const node = this.lowerFloors?.itemNode(well.id) ?? carried(well.item);
      if (node) nodes.set(well.id, { node, item: well.item }); else waiting.add(well.id);
    }
    return { nodes, waiting };
  }

  /**
   * The walk's footing at a plan point, for feet at `height`: the highest tread within a step of them, or else the lowest
   * surface out of their reach, which stops the walker, or else the floor. Over a stairwell in the floor in view the floor
   * is the one below, and the flight that leads up to this floor is its tread; under this floor anywhere else nothing is
   * in reach. Within a body's reach of the stairwell's edge, where no tread is under the feet, the floor round it still
   * bears the walker: a flight's model need not fill its catalog box, and the gap it leaves at the top would otherwise be
   * a pit. Over a stairwell whose flight is not built yet the feet stay where they are. `floor` is the floor alone.
   *
   * `leap` joins the floor in view and the flight below it in its stairwell whatever the height between them, down to
   * where this floor came into view (the eyes STAIR_BAND over it): a catalog flight dropped at its catalog size seldom
   * ends a step under the floor above — a spiral's top tread stops 0.4–1.2 m short of it, the grand staircase on a 2.7 m
   * floor half a metre over it — and the app's walk, off a stair, goes to the floor in view whatever the height
   * (StairTransitionRuntime), as onto a stair from it. It is where the walker lands, or undefined where it may not go.
   */
  private stairSupport(): ((point: Vec2, height: number) => number) & { floor(point: Vec2): number; leap(from: Vec2, point: Vec2, height: number): number | undefined } {
    const stairs: { treads: Treads; bounds: THREE.Box3; probes: Vec2[] }[] = [];
    const levels = this.hp?.levels ?? [], index = this.levelIndex ?? 0, { nodes, waiting } = this.walkFlights();
    for (const { node } of nodes.values()) {
      node.updateWorldMatrix(true, true);
      const matrix = node.matrixWorld.elements;
      // Round the foot in eight directions of the flight's own frame, opposite ones side by side: its axes and diagonals.
      const [u, v] = [0, 8].map(axis => {
        const length = Math.hypot(matrix[axis], matrix[axis + 2]) || 1;
        return { x: matrix[axis] / length, z: matrix[axis + 2] / length };
      });
      const probes = [u, v, { x: (u.x + v.x) / Math.SQRT2, z: (u.z + v.z) / Math.SQRT2 }, { x: (u.x - v.x) / Math.SQRT2, z: (u.z - v.z) / Math.SQRT2 }]
        .flatMap(({ x, z }) => [{ x: .12 * x, z: .12 * z }, { x: -.12 * x, z: -.12 * z }]);
      const indexed = this.index?.get(node.name) === node ? this.index.bounds(node.name) : undefined;
      stairs.push({ treads: treadsOf(node), bounds: indexed ?? new THREE.Box3().setFromObject(node), probes });
    }
    const wells = wellsDown(levels, index), drop = index > 0 ? levelAltitude(levels, index) - levelAltitude(levels, index - 1) : 0;
    const up = waiting.size ? wellsUp(levels, index).filter(well => waiting.has(well.id)) : [];
    const inside = (point: Vec2) => { const hole = wells.length ? wellAt(wells, point) : undefined; return hole && depthInWell(hole, point) > WELL_BRIM ? hole : undefined; };
    const floor = (point: Vec2) => inside(point) ? -drop : 0;
    /** What the flights hold at a plan point: the surfaces turned up over it, and those round it, flight by flight. */
    const footing = (point: Vec2, ground: number) => stairs.flatMap(({ treads, bounds, probes }) => {
      if (point.x < bounds.min.x || point.x > bounds.max.x || point.z < bounds.min.z || point.z > bounds.max.z) return [];
      return [{ surfaces: this.stairSurfaces(point, treads, bounds, ground),
        surrounding: probes.map(({ x, z }) => this.stairSurfaces({ x: point.x + x, z: point.z + z }, treads, bounds, ground)) }];
    });
    // Three of the eight contacts round the foot tell a tread, even at a winder's slanted edge, from a narrow handrail or
    // spindle, which bears two at most.
    const tread = (y: number, surrounding: number[][]) => surrounding.filter(probe => probe.some(other => Math.abs(other - y) < .06)).length >= 3;
    const sample = (point: Vec2, height: number): number => {
      const hole = wells.length ? wellAt(wells, point) : undefined;
      if ((hole && waiting.has(hole.id)) || (up.length && wellAt(up, point))) return height;
      const brim = !!hole && depthInWell(hole, point) <= WELL_BRIM, ground = hole ? -drop : 0;
      let reachable = -Infinity, blocked = Infinity;
      for (const { surfaces, surrounding } of footing(point, ground)) {
        if (!surfaces.length) {
          // A crack between two treads — open risers, winders cut apart — is stepped over: a tread on both sides of it.
          for (let i = 0; i < surrounding.length; i += 2) {
            const [a, b] = [surrounding[i], surrounding[i + 1]].map(side => side.filter(y => y <= height + WALK_MAX_STEP + 1e-6));
            if (a.length && b.length) reachable = Math.max(reachable, ...a, ...b);
          }
          continue;
        }
        for (const y of surfaces) {
          if (y > height + WALK_MAX_STEP + 1e-6) blocked = Math.min(blocked, y);
          if (y <= height + WALK_MAX_STEP + 1e-6 && tread(y, surrounding)) reachable = Math.max(reachable, y);
        }
      }
      if (reachable > -Infinity) return reachable;
      // No tread under the feet: near the stairwell's edge the floor round it bears the walker, below it too far to step.
      if (brim) return Math.min(blocked, 0);
      return blocked < Infinity ? blocked : ground;
    };
    const depth = this.walkEyeHeight - STAIR_BAND;
    const leap = (from: Vec2, point: Vec2, height: number): number | undefined => {
      if (!wells.length || (!wellAt(wells, point) && !wellAt(wells, from))) return undefined;
      if (Math.abs(height) < 1e-6) {
        // From this floor onto the flight: its highest tread there, a handrail never.
        const hole = wellAt(wells, point);
        if (!hole || waiting.has(hole.id)) return undefined;
        let landing = -Infinity;
        for (const { surfaces, surrounding } of footing(point, -drop)) {
          for (const y of surfaces) if (y >= -depth - 1e-6 && y <= depth + 1e-6 && y > -drop + .01 && tread(y, surrounding)) landing = Math.max(landing, y);
        }
        return landing > -Infinity ? landing : undefined;
      }
      // From the flight onto this floor, where it is under the feet: round the stairwell or at its brim.
      if (height < -depth - 1e-6 || height > depth + 1e-6 || height <= -drop + .01) return undefined;
      return inside(point) ? undefined : 0;
    };
    return Object.assign(sample, { floor, leap });
  }

  /**
   * The app's stair walk (WalkView.Stairs, StairNavigationHandler): climbing a flight in a stairwell, the floor above comes
   * into view once the eyes are STAIR_BAND over its floor, and going down one, the floor below once they are STAIR_BAND
   * under the floor in view — up through a hole in this floor's ceiling over its own flight, down through a hole in its
   * floor over the flight of the floor below, and only to a floor with a room at the hole. The band keeps the floor from
   * flickering at the line, and turning round on the flight goes back. True when the floor changed.
   */
  private climbFloors(): boolean {
    const levels = this.hp?.levels, index = this.levelIndex ?? 0, point = this.walk.pos, eye = point.y;
    if (!levels || !this.interactionHandlers?.onWalkFloor) return false;
    const upper = levels[index + 1], lower = levels[index - 1];
    if (upper) {
      const rise = levelAltitude(levels, index + 1) - levelAltitude(levels, index), well = wellAt(wellsUp(levels, index), point);
      if (well && eye >= rise + STAIR_BAND && wellRoom(upper.rooms, well)) return this.changeWalkFloor(index + 1, -rise);
    }
    if (lower) {
      const drop = levelAltitude(levels, index) - levelAltitude(levels, index - 1), well = wellAt(wellsDown(levels, index), point);
      if (well && eye <= -STAIR_BAND && wellRoom(lower.rooms, well)) return this.changeWalkFloor(index - 1, drop);
    }
    return false;
  }

  /**
   * Asks for floor `to` and carries the walker onto it: its flights go along, moved by `shift` (the difference of the two
   * floors' altitudes), for the walk to stand on until that floor has built them, and `load` moves the walker's heights
   * by as much once the floor is in view (`shiftWalk`). False when the editor kept the floor.
   */
  private changeWalkFloor(to: number, shift: number): boolean {
    const move = new THREE.Matrix4().makeTranslation(0, shift, 0), carried = new Map<string, { node: THREE.Object3D; item: Item }>();
    for (const [id, { node, item }] of this.walkFlights().nodes) {
      node.updateWorldMatrix(true, true);
      const copy = node.clone(true);
      copy.matrixAutoUpdate = false; copy.matrix.copy(node.matrixWorld).premultiply(move); copy.updateMatrixWorld(true);
      carried.set(id, { node: copy, item });
    }
    this.carried = carried;
    this.walkShift = { to, shift };
    this.interactionHandlers.onWalkFloor?.(to);
    const changed = !this.walkShift;
    this.walkShift = null;
    if (!changed) this.carried = null;
    return changed;
  }

  /** The floor the walk climbed to is in view: the walker stays where it is, its heights now measured from this floor. */
  private shiftWalk(shift: number): void {
    this.walkShift = null;
    this.walk.pos.y += shift;
    this.walkSupportY = (this.walkSupportY ?? 0) + shift;
    this.applyWalk();
    this.requestFrame(true);
  }

  /**
   * WASD and the arrows read the same in both camera modes; the on-screen stick adds its push to them. `analog`: only the
   * stick is walking, at its own ramping pace.
   */
  private walkInput(): { fwd: number; strafe: number; running: boolean; analog: boolean } {
    const k = this.walk.keys, stick = this.walkStick ?? { fwd: 0, strafe: 0 };
    const keyFwd = (k.has('KeyW') || k.has('ArrowUp') ? 1 : 0) - (k.has('KeyS') || k.has('ArrowDown') ? 1 : 0);
    const keyStrafe = (k.has('KeyD') || k.has('ArrowRight') ? 1 : 0) - (k.has('KeyA') || k.has('ArrowLeft') ? 1 : 0);
    return { fwd: clamp(keyFwd + stick.fwd, -1, 1), strafe: clamp(keyStrafe + stick.strafe, -1, 1),
      running: k.has('ShiftLeft') || k.has('ShiftRight'), analog: !keyFwd && !keyStrafe && !!(stick.fwd || stick.strafe) };
  }

  /** Whatever still carries the walker by itself — a slide, a flight, a step not yet taken — stops. */
  private stopWalkMotion(): void {
    this.walkPending = null; this.walkCoast = null; this.walkFlight = null; this.walkFingers = null;
  }

  /**
   * The walk's on-screen stick (a finger's twin of WASD): forward and sideways, each −1…1, the length of the push
   * setting the pace up to a walk's. Zero lets go. The walker keeps to the walls and the doors exactly as with the keys.
   */
  setWalkStick(fwd: number, strafe: number): void {
    const length = Math.hypot(fwd, strafe), scale = length > 1 ? 1 / length : 1;
    const next = { fwd: Number.isFinite(fwd) ? fwd * scale : 0, strafe: Number.isFinite(strafe) ? strafe * scale : 0 };
    const was = this.walkStick ?? { fwd: 0, strafe: 0 };
    this.walkStick = next;
    if (next.fwd || next.strafe) { this.walkCoast = null; this.walkFlight = null; }
    if ((next.fwd || next.strafe) && !was.fwd && !was.strafe) this.interactionHandlers?.onNavigationIntent?.();
    if (next.fwd || next.strafe || was.fwd || was.strafe) this.requestFrame();
  }

  /**
   * The same keys in orbit: the camera and what it looks at slide together across the floor,
   * keeping the height and the angle the user set — the keyboard twin of a middle-button pan,
   * not a second walk mode. Its pace follows the orbit distance, so an overview of the whole
   * floor does not crawl at a room's walking speed.
   */
  private stepOrbit(dt: number): boolean {
    if (this.participantView) return false;
    const { fwd, strafe, running } = this.walkInput();
    if (!fwd && !strafe) return false;
    const target = this.controls.target;
    const ahead = new THREE.Vector3(target.x - this.camera.position.x, 0, target.z - this.camera.position.z);
    if (ahead.lengthSq() < 1e-8) return false;
    ahead.normalize();
    const speed = Math.max(2.2, this.camera.position.distanceTo(target) * .6) * (running ? 2 : 1) * dt;
    const dx = (ahead.x * fwd - ahead.z * strafe) * speed, dz = (ahead.z * fwd + ahead.x * strafe) * speed;
    this.camera.position.x += dx; this.camera.position.z += dz;
    target.x += dx; target.z += dz;
    return true;
  }

  private stepWalk(dt: number): void {
    if (this.participantView) return;
    const { fwd, strafe, running, analog } = this.walkInput();
    // Keys or the stick take over from a flight or a slide.
    if (fwd || strafe) { this.walkFlight = null; this.walkCoast = null; }
    if (this.walkFlight) { this.flyWalk(dt); return; }
    this.walkStickTime = analog && (fwd || strafe) ? (this.walkStickTime ?? 0) + dt : 0;
    const speed = (analog ? stickPace(this.walkStickTime) : running ? 4.5 : 2.2) * dt;
    const sin = Math.sin(this.walk.yaw), cos = Math.cos(this.walk.yaw);
    const delta = { x: (sin * fwd - cos * strafe) * speed, z: (cos * fwd + sin * strafe) * speed };
    // Two fingers and the wheel add what they carried since the last frame, and a slide its share of this one.
    const pending = this.walkPending;
    this.walkPending = null;
    if (pending) { delta.x += pending.x; delta.z += pending.z; }
    const coast = this.walkCoast;
    if (coast) {
      delta.x += coast.x * dt; delta.z += coast.z * dt;
      const next = coastDecay(coast, dt, coast.friction);
      this.walkCoast = next ? { ...next, friction: coast.friction } : null;
    }
    const level = this.level, sample = this.stairSupport();
    this.walkSupportY ??= Math.max(0, this.walk.pos.y - this.walkEyeHeight);
    const resting = sample(this.walk.pos, this.walkSupportY);
    if (resting <= this.walkSupportY + 1e-6) this.walkSupportY = resting;
    else if (resting > this.walkSupportY + WALK_MAX_STEP) this.walkSupportY = sample.floor(this.walk.pos);
    const tags = new Map(level?.openings.map(opening => [opening.modelRef.id, this.models?.entry?.(opening.modelRef.id)?.tags ?? []]));
    // Under a mansard's slope the walker stops where its head would touch it (a hand's breadth over the eye).
    const ceilings = level ? slopedCeilings(level) : [];
    const headroom = ceilings.length ? (point: Vec2, feet: number) => {
      const ceiling = ceilings.find(value => containsPoint(value.floor, point));
      return ceiling ? ceilingHeightAt(ceiling, point) - feet - (this.walkEyeHeight + .12) : Infinity;
    } : undefined;
    const next = level ? moveWalker(level, this.walk.pos, delta, tags, { height: this.walkSupportY, sample, leap: sample.leap, headroom })
      : { x: this.walk.pos.x + delta.x, z: this.walk.pos.z + delta.z, height: 0 };
    this.walk.pos.x = next.x; this.walk.pos.z = next.z; this.walkSupportY = next.height;
    const target = this.walkSupportY + this.walkEyeHeight;
    this.walk.pos.y += (target - this.walk.pos.y) * (1 - Math.exp(-Math.max(0, dt) / .07));
    if (this.climbFloors()) return;
    this.applyWalk();
  }

  /** Carries the walker by a step in the view's frame (forward, sideways), taken with the walls on the next frame. */
  private walkBy(fwd: number, strafe: number): { x: number; z: number } {
    const sin = Math.sin(this.walk.yaw), cos = Math.cos(this.walk.yaw);
    const delta = { x: sin * fwd - cos * strafe, z: cos * fwd + sin * strafe };
    const pending = this.walkPending ?? { x: 0, z: 0 };
    this.walkPending = { x: pending.x + delta.x, z: pending.z + delta.z };
    this.requestFrame();
    return delta;
  }

  /**
   * Two fingers on the walk picture, as the app reads them (`WalkGestureStrategy`): a drag grabs the floor and walks —
   * fingers down go forward, fingers left step right — and leaves a slide behind; a pinch steps toward what lies
   * between the fingers; a twist turns the head by its angle. The walls stop all of it as they stop the keys.
   */
  private walkTwoFingers(): void {
    const points = [...(this.touches?.values() ?? [])];
    if (points.length !== 2 || this.participantView) return;
    const [a, b] = points, centre = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const span = Math.hypot(b.x - a.x, b.y - a.y), angle = Math.atan2(b.y - a.y, b.x - a.x);
    const previous = this.walkFingers;
    if (!previous) {
      this.walkFingers = { origin: { centre, span, angle }, centre, span, angle, mode: null, evidence: { pan: 0, pinch: 0, twist: 0, twistAngle: 0 },
        carried: { x: 0, z: 0 }, samples: [] };
      return;
    }
    const dx = centre.x - previous.centre.x, dy = centre.y - previous.centre.y;
    const turn = Math.atan2(Math.sin(angle - previous.angle), Math.cos(angle - previous.angle));
    // What the fingers did is judged by where they are against where they began: the browser moves them one at a time,
    // so a twist's centre wobbles between two pointer events and adding up those wobbles would read it as a drag.
    const origin = previous.origin, twisted = Math.abs(Math.atan2(Math.sin(angle - origin.angle), Math.cos(angle - origin.angle)));
    const evidence = { pan: Math.hypot(centre.x - origin.centre.x, centre.y - origin.centre.y), pinch: Math.abs(span - origin.span),
      twist: twisted * span / 2, twistAngle: twisted };
    const mode = twoFingerMode(evidence, previous.mode);
    const fingers = { ...previous, centre, span, angle, mode, evidence };
    this.walkFingers = fingers;
    if (mode === 'pan' && Math.abs(this.walk.pitch) <= PAN_MAX_PITCH) {
      const step = panStep(dx, dy), moved = this.walkBy(step.fwd, step.strafe);
      fingers.carried = { x: fingers.carried.x + moved.x, z: fingers.carried.z + moved.z };
      const now = performance.now();
      fingers.samples = [...fingers.samples.filter(sample => now - sample.at <= 150), { at: now, ...fingers.carried }];
    } else if (mode === 'pinch' && previous.span > 8) {
      const point = this.worldPointAt(centre.x, centre.y);
      const distance = point ? Math.hypot(point.x - this.walk.pos.x, point.y - this.walk.pos.y, point.z - this.walk.pos.z) : null;
      this.walkBy(pinchStep(distance, span / previous.span), 0);
    } else if (mode === 'twist') {
      this.walk.yaw -= turn;
      this.applyWalk(); this.requestFrame();
    }
  }

  /** The fingers let go: a drag still moving then slides on (`coastVelocity`). */
  private endWalkFingers(): void {
    const fingers = this.walkFingers;
    this.walkFingers = null;
    if (fingers?.mode !== 'pan') return;
    const velocity = coastVelocity(fingers.samples, performance.now());
    if (velocity) { this.walkCoast = { ...velocity, friction: DRAG_FRICTION }; this.requestFrame(); }
  }

  /**
   * A second tap soon after the first, where the first was, walks there (`walkTarget`); the first tap has already
   * selected as a tap does. Returns whether this tap was that second one.
   */
  private walkDoubleTap(event: PointerEvent, hit: THREE.Object3D | null): boolean {
    const now = performance.now(), finger = event.pointerType === 'touch' || event.pointerType === 'pen';
    const last = this.walkTap;
    if (last && now - last.at <= (finger ? 320 : 500) && Math.hypot(event.clientX - last.x, event.clientY - last.y) <= (finger ? 32 : 12)) {
      this.walkTap = null;
      this.walkTowards(hit, event.clientX, event.clientY);
      return true;
    }
    this.walkTap = { at: now, x: event.clientX, y: event.clientY };
    return false;
  }

  /** Where a double tap on this spot of the picture takes the walker, flown there over a second. */
  private walkTowards(hit: THREE.Object3D | null, clientX: number, clientY: number): void {
    const level = this.level, point = this.worldPointAt(clientX, clientY);
    if (!level || !point || this.participantView) return;
    const data = (hit?.userData ?? {}) as Record<string, unknown>;
    let tapped: WalkTapped = { kind: 'floor', point };
    if (data.kind === 'item') { const item = level.items.find(value => value.id === data.itemId); if (item) tapped = { kind: 'item', item, point }; }
    else if (data.kind === 'opening') {
      const opening = level.openings.find(value => value.id === data.openingId), wall = opening && level.walls.find(value => value.id === opening.wallId);
      if (opening && wall) tapped = { kind: 'opening', opening, wall, point };
    } else if (data.kind === 'wall') { const wall = level.walls.find(value => value.id === data.wallId); if (wall) tapped = { kind: 'wall', wall, point }; }
    else if (data.kind === 'ceiling') tapped = { kind: 'ceiling', point };
    const tags = new Map(level.openings.map(opening => [opening.modelRef.id, this.models?.entry?.(opening.modelRef.id)?.tags ?? []]));
    const vfov = this.camera.fov * Math.PI / 180, hfov = 2 * Math.atan(Math.tan(vfov / 2) * (this.camera.aspect || 1));
    const destination = walkTarget(tapped, { level, tags, hfov, vfov, floor: this.walkSupportY ?? 0, approach: this.walkApproach ?? null,
      walker: { x: this.walk.pos.x, z: this.walk.pos.z, yaw: this.walk.yaw, pitch: this.walk.pitch, eye: this.walk.pos.y } });
    if (!destination) return;
    this.interactionHandlers?.onNavigationIntent?.();
    this.walkApproach = destination.approach;
    this.walkCoast = null; this.walkPending = null;
    this.walkFlight = { from: { x: this.walk.pos.x, z: this.walk.pos.z, yaw: this.walk.yaw, pitch: this.walk.pitch }, to: destination.pose, started: performance.now() };
    this.requestFrame(true);
  }

  /** One frame of the flight: a second, easing in and out; with reduced motion the walker is there at once. */
  private flyWalk(dt: number): void {
    const flight = this.walkFlight;
    if (!flight) return;
    const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    const t = reduced ? 1 : (performance.now() - flight.started) / WALK_FLIGHT_MS, eased = easeInOutQuint(t);
    const turn = Math.atan2(Math.sin(flight.to.yaw - flight.from.yaw), Math.cos(flight.to.yaw - flight.from.yaw));
    this.walk.pos.x = flight.from.x + (flight.to.x - flight.from.x) * eased;
    this.walk.pos.z = flight.from.z + (flight.to.z - flight.from.z) * eased;
    this.walk.yaw = flight.from.yaw + turn * eased;
    this.walk.pitch = flight.from.pitch + (flight.to.pitch - flight.from.pitch) * eased;
    if (t >= 1) {
      this.walkFlight = null;
      this.resetWalkSupport(this.walkSupportY ?? 0);
      this.saveCameraView();
    } else {
      const target = (this.walkSupportY ?? 0) + this.walkEyeHeight;
      this.walk.pos.y += (target - this.walk.pos.y) * (1 - Math.exp(-Math.max(0, dt) / .07));
    }
    this.applyWalk();
  }

  /** Keep the outline on its item while it is dragged or replaced by real geometry. */
  private followHighlight(): void {
    const target = this.highlighted && this.nodeFor(`item:${this.highlighted}`);
    const item = this.level?.items.find(item => item.id === this.highlighted);
    if (!target || !item) { this.selectionPresentation?.clear(); this.clearConflicts(); return; }
    if (!this.selectionPresentation) {
      this.selectionPresentation = new SelectionPresentation(); this.scene.add(this.selectionPresentation.root);
    }
    const conflicts = this.level && this.placing(item.id)
      ? obstructions(this.level.items, item, this.overlapFootprints(this.level)) : [];
    this.selectionPresentation.update(target, { enabled: this.active && !this.wallPlan,
      model: item.modelRef.kind !== 'none' && !isRegion(item), tone: conflicts.length ? 'conflict' : 'select' });
    this.followConflicts(conflicts);
  }

  /** True while this item is actually being moved, turned or resized — past the tap
   * threshold, not merely selected. Like Unity, the obstruction is marked for the length of
   * the gesture: a finished room is full of pieces that share space on purpose, and selecting
   * one of them is not a question about the others. */
  private placing(id: string): boolean {
    const session = this.pointer;
    return !!session?.intent.active && session.intent.kind !== 'camera' && session.item?.id === id;
  }

  /** The same shell in red on whatever the item being placed has been driven into. */
  private followConflicts(ids: readonly string[]): void {
    // Guarded: a test-built viewer skips the field initialisers.
    this.conflictPresentations ??= new Map();
    const wanted = new Set(ids);
    for (const [id, presentation] of this.conflictPresentations) {
      if (wanted.has(id)) continue;
      presentation.dispose(); this.conflictPresentations.delete(id);
    }
    for (const id of wanted) {
      const node = this.nodeFor(`item:${id}`), other = this.level?.items.find(candidate => candidate.id === id);
      if (!node || !other) { this.conflictPresentations.get(id)?.dispose(); this.conflictPresentations.delete(id); continue; }
      let presentation = this.conflictPresentations.get(id);
      if (!presentation) {
        presentation = new SelectionPresentation(); this.scene.add(presentation.root);
        this.conflictPresentations.set(id, presentation);
      }
      presentation.update(node, { enabled: this.active && !this.wallPlan, model: other.modelRef.kind !== 'none' && !isRegion(other), tone: 'conflict' });
    }
  }

  private clearConflicts(): void {
    for (const presentation of this.conflictPresentations?.values() ?? []) presentation.dispose();
    this.conflictPresentations?.clear(); this.conflictFootprints = null;
  }

  /** Measured item bounds for the overlap test, without `gestureFootprints`' rebuild of a
   * model that has not been indexed yet: this runs per frame, that one per gesture. Late
   * hydration replaces a placeholder's box, which `resourceVersion` — not `version`, bumped
   * by every pose update — is exactly the counter for. */
  private overlapFootprints(level: Level): Footprints {
    const resources = this.index?.resourceVersion ?? 0;
    const cached = this.conflictFootprints;
    if (cached?.items === level.items && cached.resources === resources) return cached.value;
    const value = new Map<string, LocalFootprint>();
    for (const item of level.items) {
      const box = this.index?.localBounds(`item:${item.id}`);
      if (box && !box.isEmpty()) value.set(item.id, { minX: box.min.x, maxX: box.max.x, minZ: box.min.z, maxZ: box.max.z });
    }
    this.conflictFootprints = { items: level.items, resources, value };
    return value;
  }

  private drawFrame(cameraMoving = false): void {
    const start = performance.now(), info = this.renderer.info;
    // The floors below draw the furniture of the covered rooms the camera can see into from where it stands.
    if (this.lowerFloors) { this.lowerFloors.look(this.camera.position); this.lowerFloors.flush(); }
    this.cohorts?.flush();
    this.frameMetrics ??= { cpuSubmitMs: 0, frameIntervalMs: 0, drawCalls: 0, submittedTriangles: 0,
      shadowCalls: 0, shadowTriangles: 0, colorCalls: 0, aoCalls: 0, colorTriangles: 0, aoTriangles: 0, compositeCalls: 0, compositeTriangles: 0 };
    this.frameMetrics.shadowCalls = 0; this.frameMetrics.shadowTriangles = 0;
    if (info) { info.autoReset = false; info.reset(); }
    const moving = cameraMoving || this.interacting || this.appearance?.active || this.walkMoving();
    const ao = this.ambientOcclusion && !moving && !this.wallPlan && this.built && !this.sceneBounds().isEmpty()
      && this.renderer.extensions.has('EXT_color_buffer_float');
    if (ao) {
      this.contactShadows ??= new ContactShadows(this.renderer, this.scene, this.camera);
      this.contactShadows.setIntensity(this.aoIntensity); this.contactShadows.render();
    } else this.renderer.render(this.scene, this.viewCamera);
    const passes = ao ? this.contactShadows?.lastPasses : null;
    this.frameMetrics.cpuSubmitMs = performance.now() - start;
    this.frameMetrics.drawCalls = info?.render.calls ?? 0;
    this.frameMetrics.submittedTriangles = info?.render.triangles ?? 0;
    this.frameMetrics.aoCalls = passes?.aoCalls ?? 0;
    this.frameMetrics.aoTriangles = passes?.aoTriangles ?? 0;
    this.frameMetrics.compositeCalls = passes?.compositeCalls ?? 0;
    this.frameMetrics.compositeTriangles = passes?.compositeTriangles ?? 0;
    this.frameMetrics.colorTriangles = Math.max(0,(passes?.colorTriangles ?? this.frameMetrics.submittedTriangles)-this.frameMetrics.shadowTriangles);
    this.frameMetrics.colorCalls = Math.max(0, (passes?.colorCalls ?? this.frameMetrics.drawCalls) - this.frameMetrics.shadowCalls);
    // Guarded: a test-built viewer skips the field initialisers.
    for (const listener of this.frameListeners ?? []) listener();
  }

  private walkMoving(): boolean {
    return !this.participantView && this.mode === 'walk' && (Math.abs(this.walk.pos.y - ((this.walkSupportY ?? 0) + this.walkEyeHeight)) > .001
      || ['KeyW','KeyA','KeyS','KeyD','ArrowUp','ArrowDown','ArrowLeft','ArrowRight'].some(key => this.walk.keys.has(key))
      || !!this.walkStick?.fwd || !!this.walkStick?.strafe || !!this.walkPending || !!this.walkCoast || !!this.walkFlight);
  }

  private loop = (t: number): boolean => {
    if (this.disposed || !this.active || document.hidden) { this.lastFrame = 0; return false; }
    const elapsed = this.lastFrame ? t - this.lastFrame : 16, dt = Math.min(elapsed / 1000, .1);
    this.lastFrame = t;
    this.frameMetrics.frameIntervalMs = elapsed;
    this.fps = this.fps * .9 + (1 / Math.max(dt, .0001)) * .1;
    const editing = this.pointer && this.pointer.intent.kind !== 'camera';
    let cameraMoving = false;
    if (!editing && !this.wallPlan && !this.participantView) {
      if (this.mode === 'walk') this.stepWalk(dt);
      else {
        const keyed = this.stepOrbit(dt);
        cameraMoving = !!this.controls.update() || keyed;
        // Record where the keys left the camera once they stop, the way a released drag does.
        if (this.orbitKeyed && !keyed) this.saveCameraView();
        this.orbitKeyed = keyed;
      }
    }
    const posing = new Map<THREE.Object3D,string>();
    for (const id of this.appearingIds ?? []) {
      const node = this.nodeFor(`item:${id}`);
      if (node) posing.set(node,[...node.position.toArray(),...node.scale.toArray()].join(','));
    }
    this.appearance?.update(t);
    for (const [node, before] of posing) if (before !== [...node.position.toArray(),...node.scale.toArray()].join(',')) {
      this.renderer.shadowMap.needsUpdate = true; this.index?.update(node.name);
    }
    if (!this.appearance?.active) this.finishCohortAppearances();
    if (this.highlighted) this.followHighlight();
    if (this.gathered?.length) this.followGathered();
    const sliding = this.updateWallCuts(t);
    this.followSurfaces();
    this.updateOverlay(); this.updateDoorMarkers(t); this.drawFrame(cameraMoving || sliding); this.renderFrames++;
    const again = cameraMoving || this.interacting || !!this.appearance?.active || this.walkMoving() || sliding;
    if (!again) { this.lastFrame = 0; if (!this.wallPlan) this.still?.schedule(); }
    return again;
  };

}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** A wall takes this long to slide between its full height and the cut, like a panel. */
const WALL_SLIDE_MS = 180;
/** «Вписать»'s line of sight in the orbit, from above one corner. */
const FIT_VIEW = new THREE.Vector3(-0.62, -0.55, -0.62).normalize();
const TURN_CENTRE = new THREE.Vector3();
/** How close ⌘+ brings the orbit camera to its target, in metres, and how far ⌘− takes it, in «Вписать» distances. */
const ORBIT_ZOOM_NEAR = .5, ORBIT_ZOOM_FAR = 20;
interface WallSlide { from: number; to: number; at: number; full: number }
function slideHeight(slide: WallSlide, now: number): number {
  const t = Math.min(1, Math.max(0, (now - slide.at) / WALL_SLIDE_MS)), eased = 1 - (1 - t) ** 3;
  return slide.from + (slide.to - slide.from) * eased;
}
function reducedMotion(): boolean {
  try { return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

/**
 * How far past the floor between two floors the eyes go on a flight before the other floor comes into view: the app's
 * HysteresisBandMeters (StairNavigationHandler), about a step, so the floor does not flicker at the line.
 */
const STAIR_BAND = .15;
/** How far into a stairwell the floor round it still bears the walker: a body's reach and a little. */
const WELL_BRIM = WALK_BODY_RADIUS + .1;
/** How often the furniture arriving for the floors below is drawn while it loads. */
const LOWER_FLOORS_FRAME_MS = 250;
/** How long the floors below stay settled before what the camera has not seen of them is made ready. */
const LOWER_FLOORS_WARM_MS = 400;
/** Background work for when the page is idle; where the browser cannot say (Safari), after a pause between frames. */
function whenIdle(work: (deadline?: IdleDeadline) => void): void {
  if (typeof requestIdleCallback === 'function') requestIdleCallback(work, { timeout: 1000 });
  else setTimeout(() => work(), 50);
}

/** The 3D view's backdrop, and the Figma «Grid canvas» the wall plan is drawn on. */
const SCENE_BACKGROUND = 0xdfe4ea, WALL_PLAN_BACKGROUND = 0xfbfbfc;
/** The sun as the house sees it, and head-on in the wall plan: a white wall reads near white, a door still reads against it. */
const SUN_INTENSITY = 3, WALL_PLAN_LIGHT = 3.4;

/**
 * Lite's WallPlanGrid in the colours of the design's grid canvas: a thin line every 20 cm and a
 * stronger one every metre from the wall's start corner, drawn behind the wall so the wall and the
 * furniture cover it. One texture repeat is one metre.
 */
function makeWallGrid(): THREE.Mesh {
  const size = 160, cell = size / 5, canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const context = canvas.getContext('2d');
  if (context) {
    context.clearRect(0, 0, size, size);
    context.fillStyle = 'rgba(1, 9, 54, .09)';
    for (let i = 1; i < 5; i++) { context.fillRect(i * cell, 0, 1, size); context.fillRect(0, i * cell, size, 1); }
    context.fillStyle = '#d6d7de';
    context.fillRect(0, 0, size, 2); context.fillRect(0, 0, 2, size);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(200, 200);
  texture.anisotropy = 4;
  texture.colorSpace = THREE.SRGBColorSpace;
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(200, 200), new THREE.MeshBasicMaterial({ map: texture, transparent: true, depthWrite: false }));
  mesh.name = 'wall-plan-grid'; mesh.userData = { kind: 'grid' };
  mesh.renderOrder = -1;
  return mesh;
}

function disposeTree(root: THREE.Object3D): void {
  restoreModeGeometry(root);
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
    const mat = m.material;
    if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
    else if (mat) (mat as THREE.Material).dispose();
  });
}

/** The level with only one room's floor, walls and openings — the app's RoomIsolationMode. */
function isolateShell(level: Level, roomIndex: number | null): Level {
  if (roomIndex === null) return level;
  const room = level.rooms.find((candidate) => candidate.index === roomIndex);
  if (!room) return level;
  const walls = wallsOfRoom(room, level.walls);
  const kept = new Set(walls.map((wall) => wall.id));
  return { ...level, rooms: [room], walls, openings: level.openings.filter((opening) => kept.has(opening.wallId)) };
}

/**
 * What the wall plan draws of the items: what stands against the face, and a mansard whose back is on it — a
 * ceiling item, but the wall's own shape, as the app draws the constructions under the ceiling in its Wall2D.
 */
function onWallPlan(item: Item, level: Level, wall: Wall, side: 'front' | 'back'): boolean {
  if (!isAttic(item)) return itemFacesWall(item, wall, side);
  const frame = atticFrame(item, level.walls);
  return frame.wall?.id === wall.id && (frame.side === 1) === (side === 'front');
}

/** What the floor's sloped ceilings are, as plain numbers: the shell is built again when they change. */
function ceilingsKey(ceilings: readonly RoomCeiling[]): string {
  return JSON.stringify(ceilings.map(ceiling => [ceiling.room.index, ceiling.height, ceiling.slopes.map(slope =>
    [slope.item.id, slope.frame.origin, slope.frame.width, slope.frame.knee, slope.rise, slope.reach, slope.raises])]));
}

/** A mansard's own signature: what `itemSignature` keeps, and its size and cuts, which change its shape. */
function atticSignature(item: Item, level: Pick<Level, 'walls' | 'openings'>): string {
  const { frame, cuts } = atticShape(item, level);
  return JSON.stringify([itemSignature(item), item.size, frame.zSign, cuts]);
}

/** A UniqueId names a part for the app and shapes nothing drawn: a re-read that only gives one rebuilds nothing. */
function withoutUniqueIds(key: string, value: unknown): unknown {
  return key === 'uniqueId' ? undefined : value;
}

function itemSignature(item: Item): string {
  const { position, elevation, rotation, caption, price, url, wallId, size, scale, uniqueId, ...geometry } = item;
  return JSON.stringify([geometry, scale.map(Math.sign)]);
}

/** The door's or window's own model inside its node: what its colours dress, without the curtains hung beside it. */
function openingModelNode(holder: THREE.Object3D | null | undefined): THREE.Object3D | null {
  let found: THREE.Object3D | null = null;
  holder?.traverse(object => { if (!found && object.userData.openingModel) found = object; });
  return found;
}

export function isWallRegion(item: Item): boolean {
  return item.modelRef.kind === 'local' && item.modelRef.id.startsWith('decor.primitives/1018/1018-region-WallRegion-');
}

/** `drop`: how far under its elevation a ceiling piece over a slope hangs, to the slope (`upgradeItems`). */
function placeItem(node: THREE.Object3D, item: Item, drop = 0): void {
  rememberItemFit(node, item);
  const fit = node.userData.itemFit as { size: Item['size']; scale: Item['scale']; modular: boolean };
  const values = fit.modular ? item.scale.map((v,i)=>Math.abs(v/fit.scale[i])) : [item.size.w/fit.size.w,item.size.h/fit.size.h,item.size.d/fit.size.d];
  node.scale.set(values[0],values[1],values[2]);
  node.position.set(item.position.x, item.elevation - drop + (node.userData.real ? 0 : item.size.h / 2), item.position.z);
  node.rotation.y = -item.rotation;
  node.userData.caption = item.caption;
}

function rememberItemFit(node: THREE.Object3D, item: Item): void {
  node.userData.itemFit ??= { size:{...item.size},scale:[...item.scale],modular:typeof node.userData.parts==='number' && node.userData.parts>0 };
}

/** How far a size readout stands out from its handle's centre: the hot disc's radius and a gap. */
export const RESIZE_LABEL_GAP = 22;
/** Handle centres at least this far apart: a 28 px disc, grown to 31 when hot, and a gap. */
const RESIZE_HANDLE_CLEAR = 36;
/** A dimension line stands this far out from its edge; shorter edges than `RESIZE_DIM_MIN` take none. */
export const RESIZE_DIM_OFFSET = 18;
/**
 * An opening's dimension lines stand further out than an item's: every edge of it has a handle at its middle, where
 * the size would sit, so the line runs beyond the handle's touch zone (22 px) and the size beyond both (`openingChipPoint`).
 */
export const OPENING_DIM_OFFSET = 30;
/** Where an opening's size sits: out from the middle of its edge past the handle there, by as much as the readout reaches that way. */
export function openingChipPoint(line: { x1: number; y1: number; x2: number; y2: number; n: { x: number; y: number } }, text: string): { x: number; y: number } {
  const width = 29 + 7 * text.length, height = 24, reach = 26 + Math.abs(line.n.x) * width / 2 + Math.abs(line.n.y) * height / 2;
  return { x: (line.x1 + line.x2) / 2 + line.n.x * reach, y: (line.y1 + line.y2) / 2 + line.n.y * reach };
}
const RESIZE_DIM_MIN = 56;
/** Where a dimension's size sits: the middle of its line, out from the edge. */
export function resizeDimPoint(line: { x1: number; y1: number; x2: number; y2: number; n: { x: number; y: number } }, offset = RESIZE_DIM_OFFSET): { x: number; y: number } {
  return { x: (line.x1 + line.x2) / 2 + line.n.x * offset, y: (line.y1 + line.y2) / 2 + line.n.y * offset };
}
/** The size's screen box on a dimension line, estimated as the readout's. */
export function resizeChipRect(line: { x1: number; y1: number; x2: number; y2: number; n: { x: number; y: number } }, text: string, offset = RESIZE_DIM_OFFSET) {
  const at = resizeDimPoint(line, offset), width = 29 + 7 * text.length, height = 24;
  return { x: at.x - width / 2, y: at.y - height / 2, width, height };
}
const RESIZE_AXIS_BIT = { w: 1, h: 2, d: 4 } as const;
const RESIZE_BIT_AXIS = { 1: 'w', 2: 'h', 4: 'd' } as const;
/** A face's corners in turn, as box-corner indices with the face's own axis bit still clear. */
const RESIZE_FACE_CORNERS = { w: [0, 2, 6, 4], h: [0, 1, 5, 4], d: [0, 1, 3, 2] } as const;

/** The same readout on the handle's other side, over the face: where it goes when chrome covers its own side. */
export function flippedResizeLabel<T extends { x: number; y: number; dir: { x: number; y: number } }>(label: T): T {
  return { ...label, x: label.x - 2 * RESIZE_LABEL_GAP * label.dir.x, y: label.y - 2 * RESIZE_LABEL_GAP * label.dir.y, dir: { x: -label.dir.x, y: -label.dir.y } };
}
/** How a size readout hangs off its point, in fractions of its own width and height: the near edge
 * on the point and the rest out along `dir`, level with it across the axis. */
export function resizeLabelShift(dir: { x: number; y: number }): { x: number; y: number } {
  return Math.abs(dir.x) >= Math.abs(dir.y) ? { x: dir.x > 0 ? 0 : -1, y: -.5 } : { x: -.5, y: dir.y > 0 ? 0 : -1 };
}
/** The readout's screen box, estimated from its text (letter, gap, 12 px digits, padding) for keep-out zones. */
export function resizeLabelRect(label: { x: number; y: number; dir: { x: number; y: number }; text: string }) {
  const width = 29 + 7 * label.text.length, height = 24, shift = resizeLabelShift(label.dir);
  return { x: label.x + shift.x * width, y: label.y + shift.y * height, width, height };
}

function resizeFaceCenter(item:Item,bounds:ResizeBounds,face:ResizeFace):THREE.Vector3 {
  const x=(bounds.min.x+bounds.max.x)/2,y=(bounds.min.y+bounds.max.y)/2,z=(bounds.min.z+bounds.max.z)/2;
  const p={x,y,z},key=face.axis==='w'?'x':face.axis==='h'?'y':'z';p[key]=face.side===1?bounds.max[key]:bounds.min[key];
  const c=Math.cos(item.rotation),s=Math.sin(item.rotation);
  return new THREE.Vector3(item.position.x+p.x*c-p.z*s,item.elevation+p.y,item.position.z+p.x*s+p.z*c);
}

/** Fit actual world corners into the sun's light space instead of a loose ±scene-span box. */
export function fitSunShadow(sun: THREE.DirectionalLight, bounds: THREE.Box3): void {
  if (bounds.isEmpty()) return;
  sun.updateMatrixWorld(); sun.target.updateMatrixWorld(); sun.shadow.updateMatrices(sun);
  const box = new THREE.Box3().makeEmpty(), point = new THREE.Vector3();
  for (let i = 0; i < 8; i++) {
    point.set(i & 1 ? bounds.max.x : bounds.min.x, i & 2 ? bounds.max.y : bounds.min.y, i & 4 ? bounds.max.z : bounds.min.z);
    box.expandByPoint(point.applyMatrix4(sun.shadow.camera.matrixWorldInverse));
  }
  const camera = sun.shadow.camera, margin = .25;
  camera.left = box.min.x - margin; camera.right = box.max.x + margin;
  camera.bottom = box.min.y - margin; camera.top = box.max.y + margin;
  camera.near = Math.max(.1, -box.max.z - margin); camera.far = Math.max(camera.near + 1, -box.min.z + margin);
  camera.updateProjectionMatrix();
}

/** A valid interior start only when the saved project camera is outside this floor. */
function interiorFloorPoint(rooms: Level['rooms']): Vec2 | null {
  let best: Vec2 | null = null, width = 0;
  for (const room of rooms) {
    const polygon = room.polygon; if (polygon.length < 3) continue;
    const lo = Math.min(...polygon.map(p=>p.z)), hi = Math.max(...polygon.map(p=>p.z));
    for (let row=1;row<32;row++) {
      const z=lo+(hi-lo)*row/32, xs:number[]=[];
      for (let i=0;i<polygon.length;i++) {
        const a=polygon[i],b=polygon[(i+1)%polygon.length];
        if((a.z>z)!==(b.z>z))xs.push(a.x+(b.x-a.x)*(z-a.z)/(b.z-a.z));
      }
      xs.sort((a,b)=>a-b);
      for(let i=0;i+1<xs.length;i+=2) {
        const span=Math.min(xs[i+1]-xs[i],2*Math.min(z-lo,hi-z));
        if(span>width){width=span;best={x:(xs[i]+xs[i+1])/2,z};}
      }
    }
  }
  return best;
}
